// process.env client configuration context
import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Network, BrainCircuit, Shield, ShieldAlert, Server, ArrowLeft, ArrowRight, Layers, Store, Copy, Check, Zap, Plus, ChevronRight, ChevronDown, ChevronUp, Trash2, Database, Globe, RefreshCw, Search, KeyRound, CheckCircle, XCircle, Clock, GitBranch, TrendingUp, DollarSign, Eye, EyeOff, Cpu, Settings, Unlock, Receipt, Filter, X, Loader2, Gift, Sliders, Smartphone, MessageSquare, LayoutDashboard, Smile, Building2, Crown, Bot, Sparkles, Rocket, Handshake, Calculator, FileText, PhoneCall, BookOpen, Printer, Download, Calendar, Wallet } from 'lucide-react';
import { LunaVoiceModel } from './LunaVoiceModel';
import { LunaFace } from './LunaFace';
import TenantManageModal from './TenantManageModal';
import { CommissionLedgerModal } from './CommissionLedgerModal';
import PersonalLunaPanel from './PersonalLunaPanel';
import LunaMasterConfigHub from './LunaMasterConfigHub';
import LunaFaceHub from './LunaFaceHub';
import CorporateMasterPanel from './corporate/CorporateMasterPanel';
import ReleaseGovernanceHub from './admin/ReleaseGovernanceHub';
import TenantBillingHub from './admin/TenantBillingHub';
import BusinessPartnersHub from './admin/BusinessPartnersHub';
import LunaLiveMonitorHUD from './admin/LunaLiveMonitorHUD';
import { AI_MODEL_CATALOG, getModelPricing, calculateTokenCostBreakdown, getAllModelsCatalog, DEFAULT_USD_TO_LKR_RATE, ModelPricingTier } from '../utils/aiModelPricing';
// ─── Lazy-loaded LunaFlowBuilder — separate async chunk avoids Rollup scope hoisting conflicts with @xyflow/react ──
const LazyLunaFlowBuilder = React.lazy(() => import('./luna-builder/LunaFlowBuilder'));
import FlowErrorBoundary from './luna-builder/FlowErrorBoundary';

// ─── Lazy-loaded MapPicker — separate async chunk avoids Rollup scope hoisting conflicts ──
const LazyMapPicker = React.lazy(() => import('./MapPicker'));

interface Tenant {
  id: string;
  name: string;
  status: string;
  ping?: string;
  lastSync?: string;
  apiKey: string;
  adminEmail?: string;
  aiKeyInfo?: {
    hasDedicatedKey: boolean;
    maskedKey: string;
    keySource: 'tenant_dedicated' | 'platform_master';
  };
  assignedModel?: string | null;
  geminiModelPool?: string[];
  url?: string;
  purpose?: string;
  scrapedContent?: string;
  schema?: Record<string, string[]>;
  showKangavenaBranding?: boolean;
  customInstructions?: string;
  isWhiteLabelEnabled?: boolean;
  customAvatarUrl?: string;
  customAvatarName?: string;
  customDeveloperName?: string;
}

interface PosStore {
  id: string;
  name: string;
  location: string;
  status: string;
  apiKey: string;
  phone?: string;
  address?: string;
  latitude?: number | null;
  longitude?: number | null;
  website?: string;
  email?: string;
  storeCode?: string;
  adminUsername?: string;
  approvalStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
  parentStoreId?: number | null;
  shopType?: string;
  isPersonalAssistantMode?: boolean;
  isWhiteLabelEnabled?: boolean;
  customAvatarUrl?: string;
  customAvatarName?: string;
  customDeveloperName?: string;
  billingStatus?: string;
  monthlyRevenue?: number;
  commission?: number;
  lunaTokenUsage?: number;
  commissionBypass?: boolean;
}

// ── Luna Intelligence sub-components (module-level for stable reference) ────────────────────────────
// MUST stay outside AdminDashboard — inline definitions cause React to remount every render, resetting <details> open state.
const LunaGroupCard: React.FC<{ title: string; badge: string; color: string; count: number; tokens: number }> = ({ title, badge, color, count, tokens, children }) => (
  <details className="border border-white/10 rounded-2xl overflow-hidden mb-3.5 open:border-cyan-500/30 group bg-slate-900/40 backdrop-blur-md transition-all">
    <summary className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-white/[0.04] transition-colors cursor-pointer list-none select-none">
      <div className="flex items-center gap-3">
        <span className={`text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider shadow-sm ${color}`}>{badge}</span>
        <span className="text-sm font-black text-slate-100">{title}</span>
      </div>
      <div className="flex items-center gap-4">
        <span className="text-[11px] text-slate-400 font-mono font-medium">{count} sessions</span>
        <span className="text-[11px] text-amber-300 font-mono font-bold bg-amber-500/10 px-2.5 py-0.5 rounded-md border border-amber-500/20">{tokens.toLocaleString()} tokens</span>
        <ChevronDown size={14} className="text-slate-400 group-open:rotate-180 transition-transform duration-300" />
      </div>
    </summary>
    <div className="px-5 pb-5 pt-1 bg-black/40 border-t border-white/5">{children}</div>
  </details>
);

const LunaSessionMiniTable = ({ rows, abuseIps, ipCount }: { rows: any[]; abuseIps: string[]; ipCount: Record<string,number> }) => (
  <div className="overflow-x-auto mt-2 rounded-xl border border-white/10 bg-black/30">
    <table className="w-full text-xs">
      <thead><tr className="border-b border-white/10 bg-slate-900/60">
        {['Time','Mode','IP','Device','Duration','Msgs','Tokens'].map(h => (
          <th key={h} className="px-3.5 py-2 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">{h}</th>
        ))}
      </tr></thead>
      <tbody className="divide-y divide-white/5">
        {rows.map((s: any, i: number) => (
          <tr key={s.id || i} className="hover:bg-white/[0.04] transition-colors">
            <td className="px-3.5 py-2 font-mono text-slate-400 whitespace-nowrap text-[11px]">{new Date(s.connectedAt).toLocaleString('en-US',{month:'short',day:'2-digit',hour:'2-digit',minute:'2-digit'})}</td>
            <td className="px-3.5 py-2 whitespace-nowrap">
              <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider ${s.lunaMode==='fast_stock'?'bg-amber-500/20 text-amber-300 border border-amber-500/30':s.lunaMode==='express'?'bg-rose-500/20 text-rose-300 border border-rose-500/30':'bg-slate-800 text-slate-300 border border-slate-700'}`}>{s.lunaMode||'standard'}</span>
            </td>
            <td className="px-3.5 py-2 font-mono text-[11px] whitespace-nowrap">
              <span className={abuseIps.includes(s.ip) ? 'text-rose-400 font-bold bg-rose-500/10 px-1.5 py-0.5 rounded border border-rose-500/30' : 'text-cyan-400/90 font-medium'}>{s.ip}</span>
              {abuseIps.includes(s.ip) && <span className="ml-1.5 text-[9px] font-bold text-rose-400">×{ipCount[s.ip]}</span>}
            </td>
            <td className="px-3.5 py-2 text-[11px] font-medium"><span className={s.device==='Mobile'?'text-pink-300 font-bold':s.device==='Tablet'?'text-amber-300 font-bold':'text-slate-400'}>{s.device}</span></td>
            <td className="px-3.5 py-2 font-mono text-slate-300 whitespace-nowrap text-[11px]">{s.durationSeconds>0?`${s.durationSeconds}s`:'—'}</td>
            <td className="px-3.5 py-2 font-mono text-center text-slate-300 text-[11px] font-bold">{s.msgCount}</td>
            <td className="px-3.5 py-2 font-mono text-amber-300 font-bold whitespace-nowrap text-[11px]">{s.tokensUsed>0?s.tokensUsed.toLocaleString():'—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const LunaGroupedView = ({ sessions }: { sessions: any[] }) => {
  if (sessions.length === 0) return (
    <div className="border border-white/10 rounded-2xl p-10 text-center text-slate-400 font-mono text-xs bg-slate-950/40">
      No sessions recorded yet. Sessions appear after users connect to Luna.
    </div>
  );
  const ipCount: Record<string, number> = {};
  sessions.forEach((s: any) => { ipCount[s.ip] = (ipCount[s.ip] || 0) + 1; });
  const abuseIps = Object.entries(ipCount).filter(([, c]) => c >= 3).map(([ip]) => ip);

  const posSessions   = sessions.filter((s: any) => s.layer === 'pos');
  const publicSessions = sessions.filter((s: any) => s.layer === 'public');
  const godSessions   = sessions.filter((s: any) => s.layer === 'god');
  const otherSessions = sessions.filter((s: any) => !['pos','public','god'].includes(s.layer));

  const storeGroups: Record<string, any[]> = {};
  posSessions.forEach((s: any) => {
    const key = s.storeName || `Store #${s.storeId || '?'}`;
    if (!storeGroups[key]) storeGroups[key] = [];
    storeGroups[key].push(s);
  });
  const publicIpGroups: Record<string, any[]> = {};
  publicSessions.forEach((s: any) => {
    if (!publicIpGroups[s.ip]) publicIpGroups[s.ip] = [];
    publicIpGroups[s.ip].push(s);
  });

  return (
    <div>
      {abuseIps.length > 0 && (
        <div className="mb-5 px-5 py-3.5 rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-start gap-3.5 shadow-lg">
          <span className="text-rose-400 text-xs font-black uppercase tracking-wider mt-0.5 flex items-center gap-1.5">
            <Shield size={14} className="text-rose-400" /> High-Frequency IPs
          </span>
          <div className="flex flex-wrap gap-2">
            {abuseIps.map(ip => (
              <span key={ip} className="text-rose-300 font-mono text-xs font-bold bg-rose-500/20 px-2.5 py-1 rounded-lg border border-rose-500/40">
                {ip} <span className="text-rose-400">×{ipCount[ip]}</span>
              </span>
            ))}
          </div>
        </div>
      )}
      {Object.entries(storeGroups).length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] text-slate-400 uppercase tracking-widest font-black mb-2.5 px-1 flex items-center gap-2">
            <Store size={12} className="text-emerald-400" /> Internal POS Stores
          </p>
          {Object.entries(storeGroups).map(([sName, rows]) => (
            <LunaGroupCard key={sName} title={sName} badge="POS" color="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" count={rows.length} tokens={rows.reduce((a:number,s:any)=>a+(s.tokensUsed||0),0)}>
              <LunaSessionMiniTable rows={rows} abuseIps={abuseIps} ipCount={ipCount} />
            </LunaGroupCard>
          ))}
        </div>
      )}
      {Object.entries(publicIpGroups).length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] text-slate-400 uppercase tracking-widest font-black mb-2.5 px-1 flex items-center gap-2">
            <Globe size={12} className="text-cyan-400" /> Public Website Sessions
          </p>
          {Object.entries(publicIpGroups)
            .sort(([,a],[,b]) => b.length - a.length)
            .map(([ip, rows]) => (
              <LunaGroupCard key={ip} title={ip}
                badge={rows.length > 1 ? `${rows.length}× same IP` : 'Public'}
                color={rows.length >= 3 ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30' : rows.length > 1 ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-sky-500/20 text-sky-300 border border-sky-500/30'}
                count={rows.length} tokens={rows.reduce((a:number,s:any)=>a+(s.tokensUsed||0),0)}
              >
                <LunaSessionMiniTable rows={rows} abuseIps={abuseIps} ipCount={ipCount} />
              </LunaGroupCard>
            ))}
        </div>
      )}
      {godSessions.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] text-slate-400 uppercase tracking-widest font-black mb-2.5 px-1 flex items-center gap-2">
            <Shield size={12} className="text-purple-400" /> God Mode
          </p>
          <LunaGroupCard title="God Mode Admin" badge="GOD" color="bg-purple-500/20 text-purple-300 border border-purple-500/30" count={godSessions.length} tokens={godSessions.reduce((a:number,s:any)=>a+(s.tokensUsed||0),0)}>
            <LunaSessionMiniTable rows={godSessions} abuseIps={abuseIps} ipCount={ipCount} />
          </LunaGroupCard>
        </div>
      )}
      {otherSessions.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] text-slate-400 uppercase tracking-widest font-black mb-2.5 px-1 flex items-center gap-2">
            <Zap size={12} className="text-amber-400" /> Widget / Tenant
          </p>
          <LunaGroupCard title="Embedded Widgets" badge="WIDGET" color="bg-amber-500/20 text-amber-300 border border-amber-500/30" count={otherSessions.length} tokens={otherSessions.reduce((a:number,s:any)=>a+(s.tokensUsed||0),0)}>
            <LunaSessionMiniTable rows={otherSessions} abuseIps={abuseIps} ipCount={ipCount} />
          </LunaGroupCard>
        </div>
      )}
      <p className="text-[10px] text-slate-500 font-mono text-center pt-3 font-medium">Newest first • Permanent DB storage • Up to 500 sessions loaded</p>
    </div>
  );
};

const StoreActionDropdown: React.FC<{
  store: PosStore;
  onManage: () => void;
  onActivity: () => void;
  onAudit: () => void;
  onGrace: () => void;
  onHardLock: () => void;
  onUnlock: () => void;
  onFreeMode: () => void;
  onDelete: () => void;
  onResetCreds: () => void;
}> = ({ store, onManage, onActivity, onAudit, onGrace, onHardLock, onUnlock, onFreeMode, onDelete, onResetCreds }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block text-left">
      <button
        onClick={e => { e.stopPropagation(); setOpen(!open); }}
        className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-bold font-mono flex items-center gap-1.5 border border-white/10 shadow-sm transition-all"
      >
        Actions <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={e => { e.stopPropagation(); setOpen(false); }} />
          <div className="absolute right-0 mt-1.5 w-56 bg-[#0f172a] border border-slate-700 rounded-xl shadow-[0_10px_30px_rgba(0,0,0,0.8)] z-[70] overflow-hidden py-1 text-xs font-mono" onClick={e => e.stopPropagation()}>
            <button onClick={() => { setOpen(false); onManage(); }} className="w-full text-left px-4 py-2 hover:bg-white/10 text-cyan-300 flex items-center gap-2 font-bold">
              <Eye size={13} /> Manage Store Details
            </button>
            <button onClick={() => { setOpen(false); onResetCreds(); }} className="w-full text-left px-4 py-2 hover:bg-white/10 text-amber-300 flex items-center gap-2 font-bold">
              <KeyRound size={13} /> Reset Credentials
            </button>
            <button onClick={() => { setOpen(false); onActivity(); }} className="w-full text-left px-4 py-2 hover:bg-white/10 text-emerald-300 flex items-center gap-2 font-bold">
              <Activity size={13} /> Store Activity Log
            </button>
            <button onClick={() => { setOpen(false); onAudit(); }} className="w-full text-left px-4 py-2 hover:bg-white/10 text-purple-300 flex items-center gap-2 font-bold">
              <Receipt size={13} /> Audit Logs & Invoices
            </button>
            <div className="my-1 border-t border-slate-800" />
            <button onClick={() => { setOpen(false); onGrace(); }} className="w-full text-left px-4 py-2 hover:bg-amber-500/20 text-amber-300 flex items-center gap-2">
              <Zap size={13} /> Apply Grace Period
            </button>
            <button onClick={() => { setOpen(false); onHardLock(); }} className="w-full text-left px-4 py-2 hover:bg-rose-500/20 text-rose-300 flex items-center gap-2">
              <Zap size={13} /> Apply Hard Lock
            </button>
            {(store.billingStatus === 'LOCKED' || store.billingStatus === 'GRACE_PERIOD') && (
              <button onClick={() => { setOpen(false); onUnlock(); }} className="w-full text-left px-4 py-2 hover:bg-emerald-500/20 text-emerald-300 flex items-center gap-2 font-bold">
                <Unlock size={13} /> Force Unlock Store
              </button>
            )}
            <button onClick={() => { setOpen(false); onFreeMode(); }} className="w-full text-left px-4 py-2 hover:bg-purple-500/20 text-purple-300 flex items-center gap-2 font-bold">
              <Gift size={13} /> {store.commissionBypass ? '100% FREE (Active)' : 'Enable Free Mode'}
            </button>
            <div className="my-1 border-t border-slate-800" />
            <button onClick={() => { setOpen(false); onDelete(); }} className="w-full text-left px-4 py-2 hover:bg-rose-600/30 text-rose-400 flex items-center gap-2 font-bold">
              <Trash2 size={13} /> Delete Store
            </button>
          </div>
        </>
      )}
    </div>
  );
};
// ────────────────────────────────────────────────────────────────────────────────

const StatCard = ({ title, value, icon: Icon, color }: { title: string, value: string, icon: any, color: string }) => (
  <div className="admin-stat-card bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-4 shadow-[0_8px_32px_rgba(0,0,0,0.4)] hover:border-purple-500/50 hover:shadow-[0_8px_32px_rgba(168,85,247,0.15)] transition-all duration-300">
    <div className="flex justify-between items-start mb-3">
      <h3 className="text-slate-400 text-xs font-extrabold uppercase tracking-widest">{title}</h3>
      <div className={`p-2 rounded-xl bg-white/5 border border-white/10 ${color}`}>
        <Icon size={18} />
      </div>
    </div>
    <p className="text-3xl font-black text-white tracking-tight font-mono">{value}</p>
  </div>
);

const CopyButton = ({ text, label = "Copy Code", className = "" }: { text: string, label?: string, className?: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button 
      onClick={handleCopy}
      className={`flex items-center justify-center gap-2.5 px-6 py-4 bg-gradient-to-r from-purple-600 via-indigo-600 to-red-600 hover:from-purple-500 hover:to-red-500 text-white rounded-2xl transition-all duration-300 shadow-[0_0_25px_rgba(168,85,247,0.4)] hover:shadow-[0_0_35px_rgba(168,85,247,0.6)] font-black uppercase tracking-widest text-xs active:scale-[0.99] ${className}`}
    >
      {copied ? <Check size={20} className="text-emerald-300 animate-bounce" /> : <Zap size={20} className="text-amber-300" />}
      {copied ? 'COPIED TO CLIPBOARD!' : label}
    </button>
  );
};

export default function AdminDashboard({ adminToken: adminTokenProp, onExit, isPosOnSubdomainEnabled, setIsPosOnSubdomainEnabled }: { adminToken?: string, onExit: () => void, isPosOnSubdomainEnabled: boolean, setIsPosOnSubdomainEnabled: (val: boolean) => void }) {
  const [adminToken] = React.useState(() => adminTokenProp || sessionStorage.getItem('adminToken') || '');
  // God Mode PIN activation
  const [godModeToken, setGodModeToken] = useState(() => sessionStorage.getItem('godModeToken') || '');
  const [godModeActive, setGodModeActive] = useState(!!sessionStorage.getItem('godModeToken'));
  const [godModePinInput, setGodModePinInput] = useState('');
  const [godModeError, setGodModeError] = useState('');
  const [godModeLoading, setGodModeLoading] = useState(false);
  const [showGodModePin, setShowGodModePin] = useState(false);

  // Activate God Mode with PIN
  const activateGodMode = async () => {
    setGodModeLoading(true);
    setGodModeError('');
    try {
      let res: Response | null = null;
      let lastErr: any = null;

      // Resilient Auto-Retry Protocol (AGENTS.md Rule 12 & 13)
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          res = await fetch('/api/admin/activate-god-mode', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ pin: godModePinInput })
          });
          if (res.status === 502 || res.status === 503 || res.status === 504) {
            if (attempt < 3) {
              await new Promise(r => setTimeout(r, 800 * attempt));
              continue;
            }
          }
          break;
        } catch (fetchErr) {
          lastErr = fetchErr;
          if (attempt < 3) {
            await new Promise(r => setTimeout(r, 800 * attempt));
            continue;
          }
        }
      }

      if (!res) throw lastErr || new Error('Network timeout');

      const data = await res.json();
      if (res.ok && data.god_mode_token) {
        setGodModeToken(data.god_mode_token);
        setGodModeActive(true);
        sessionStorage.setItem('godModeToken', data.god_mode_token);
        setShowGodModePin(false);
        setGodModePinInput('');
      } else {
        setGodModeError(data.error || 'Activation failed');
      }
    } catch { setGodModeError('Connection error (server reloading, retrying...)'); }
    setGodModeLoading(false);
  };

  // Validate god mode token on mount — clear stale/expired tokens
  React.useEffect(() => {
    if (!adminToken) {
      // No admin token — clear everything and force re-login
      setGodModeActive(false);
      setGodModeToken('');
      sessionStorage.removeItem('godModeToken');
      return;
    }
    if (!godModeToken) {
      setGodModeActive(false);
      return;
    }
    // Verify the god mode token is still valid
    fetch('/api/admin/god-mode-status', {
      headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json', 'X-God-Mode-Token': godModeToken }
    }).then(r => r.json()).then(d => {
      if (!d.active) {
        setGodModeActive(false);
        setGodModeToken('');
        sessionStorage.removeItem('godModeToken');
      }
    }).catch(() => {
      // On error, clear stale token and force re-activation
      setGodModeActive(false);
      setGodModeToken('');
      sessionStorage.removeItem('godModeToken');
    });
  }, []);

  const authHeader = useCallback(() => ({
    'Authorization': `Bearer ${adminToken}`,
    'Content-Type': 'application/json',
    ...(godModeToken ? { 'X-God-Mode-Token': godModeToken } : {})
  }), [adminToken, godModeToken]);
  const [rawWidgetCode, setRawWidgetCode] = useState('Loading raw code...');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [posStores, setPosStores] = useState<PosStore[]>([]);

  const [activeSection, setActiveSection] = useState<'overview' | 'internal' | 'external' | 'corporate-suite' | 'apps' | 'luna-config' | 'luna-face' | 'luna-intel' | 'personal-luna' | 'luna-flow' | 'release-hub' | 'tenant-billing' | 'partners'>('overview');
  
  // Kangavena Apps states
  const [apps, setApps] = useState<any[]>([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [isAppModalOpen, setIsAppModalOpen] = useState(false);
  const [newApp, setNewApp] = useState({ name: '', packageName: '' });
  const [usageLogs, setUsageLogs] = useState<any[]>([]);
  const [usageLogsLoading, setUsageLogsLoading] = useState(false);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTenant, setNewTenant] = useState({
    name: '',
    url: '',
    purpose: '',
    customInstructions: '',
    clientEmail: '',
    password: '',
    isFullBundle: true,
    isSetupFeePaid: true,
    isWhiteLabel: true,
  });
  const [expandedTenantId, setExpandedTenantId] = useState<string | null>(null);
  const [savingSettingsId, setSavingSettingsId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'prompt' | 'conversations' | 'logs' | 'schema' | 'scraped' | 'ai-settings' | 'token-billing' | 'white-label-requests'>('prompt');
  const [lunaIntelSubTab, setLunaIntelSubTab] = useState<'live-telemetry' | 'session-audit'>('live-telemetry');

  // Visitor Chat History & Sessions States
  const [tenantConversations, setTenantConversations] = useState<any[]>([]);
  const [loadingConversations, setLoadingConversations] = useState<boolean>(false);
  const [selectedConversation, setSelectedConversation] = useState<any | null>(null);
  const [conversationChannelFilter, setConversationChannelFilter] = useState<'all' | 'web_widget' | 'whatsapp' | 'live_call'>('all');
  const [conversationSearch, setConversationSearch] = useState<string>('');

  // Multi-Model Pricing Matrix & Token Billing States
  const [showPricingMatrixModal, setShowPricingMatrixModal] = useState<boolean>(false);
  const [pricingFilter, setPricingFilter] = useState<'all' | 'google' | 'mimo' | 'live'>('all');
  const [billingReport, setBillingReport] = useState<any | null>(null);
  const [loadingBilling, setLoadingBilling] = useState<boolean>(false);
  const [billingMonth, setBillingMonth] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [billingLkrRate, setBillingLkrRate] = useState<number>(305);
  const [selectedInvoiceModal, setSelectedInvoiceModal] = useState<any | null>(null);
  const [invoiceCopyFeedback, setInvoiceCopyFeedback] = useState<boolean>(false);
  const [modelSimInputTokens, setModelSimInputTokens] = useState<number>(50000);
  const [modelSimOutputTokens, setModelSimOutputTokens] = useState<number>(10000);
  const [modelSimSelectedModel, setModelSimSelectedModel] = useState<string>('gemini-3.1-flash-live-preview');
  const [matrixTargetTenantId, setMatrixTargetTenantId] = useState<string | null>(null);

  // White-Label & Custom Avatar Requests State
  const [whiteLabelRequests, setWhiteLabelRequests] = useState<any[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [approvingReqId, setApprovingReqId] = useState<string | null>(null);

  const [isPosModalOpen, setIsPosModalOpen] = useState(false);
  const [newPosStore, setNewPosStore] = useState({ name: '', location: '', storeCode: '', adminUsername: '', password: '', phone: '', address: '', latitude: '', longitude: '', website: '', email: '' });
  const [expandedPosId, setExpandedPosId] = useState<string | null>(null);
  const [revealedKeys, setRevealedKeys] = useState<Record<string, boolean>>({});
  const [editProfileStore, setEditProfileStore] = useState<PosStore | null>(null);
  const [editProfileForm, setEditProfileForm] = useState({ name: '', phone: '', address: '', latitude: '', longitude: '', website: '', email: '' });
  const [editProfileSaving, setEditProfileSaving] = useState(false);
  const [editProfileError, setEditProfileError] = useState('');
  
  const [editingUrlId, setEditingUrlId] = useState<string | null>(null);
  const [editUrlValue, setEditUrlValue] = useState('');
  const [isSyncing, setIsSyncing] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [credModalTenant, setCredModalTenant] = useState<any>(null);
  const [credModalEmail, setCredModalEmail] = useState('');
  const [credModalPassword, setCredModalPassword] = useState('');
  const [credModalAiKey, setCredModalAiKey] = useState('');
  const [credModalShowAiKey, setCredModalShowAiKey] = useState(false);
  const [credModalTestingKey, setCredModalTestingKey] = useState(false);
  const [credModalKeyTestResult, setCredModalKeyTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [credModalSaving, setCredModalSaving] = useState(false);
  const [credModalKeyPool, setCredModalKeyPool] = useState<any[]>([]);
  const [credModalLoadingPool, setCredModalLoadingPool] = useState(false);
  const [credModalNewKeyInput, setCredModalNewKeyInput] = useState('');
  const [credModalNewKeyLabel, setCredModalNewKeyLabel] = useState('');
  const [credModalAddingKey, setCredModalAddingKey] = useState(false);
  const [credModalTestingKeyId, setCredModalTestingKeyId] = useState<string | null>(null);
  const [credModalKeyResults, setCredModalKeyResults] = useState<Record<string, { success: boolean; message: string }>>({});
  const [credModalDisallowMasterKeyFallback, setCredModalDisallowMasterKeyFallback] = useState<boolean>(false);

  const [isLunaMaximized, setIsLunaMaximized] = useState(false);

  // Reset credentials modal
  const [resetCredsStore, setResetCredsStore] = useState<PosStore | null>(null);
  const [resetCredsData, setResetCredsData] = useState({ storeCode: '', adminUsername: '', password: '' });
  const [resetCredsSaving, setResetCredsSaving] = useState(false);
  const [resetCredsError, setResetCredsError] = useState('');
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);

  // Tenant Management Modal
  const [manageStore, setManageStore] = useState<PosStore | null>(null);
  const [telemetry, setTelemetry] = useState<any>(null);
  const [telemetryLoading, setTelemetryLoading] = useState(true);
  const [expandedStoreGroups, setExpandedStoreGroups] = useState<Record<string, boolean>>({});

  const fetchWhiteLabelRequests = useCallback(async () => {
    setLoadingRequests(true);
    try {
      const res = await fetch('/api/tenet/admin/white-label-requests', { headers: authHeader() });
      const data = await res.json();
      if (data.success && Array.isArray(data.requests)) {
        setWhiteLabelRequests(data.requests);
      }
    } catch (err) {
      console.error('Failed to fetch white label requests:', err);
    } finally {
      setLoadingRequests(false);
    }
  }, [adminToken]);

  const handleApproveWhiteLabel = async (
    requestId: string,
    customAvatarUrl?: string,
    customAvatarName?: string,
    customDeveloperName?: string
  ) => {
    setApprovingReqId(requestId);
    try {
      const res = await fetch(`/api/tenet/admin/white-label-requests/${requestId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ customAvatarUrl, customAvatarName, customDeveloperName }),
      });
      const data = await res.json();
      if (data.success) {
        alert(data.message || 'Approved & Unlocked!');
        fetchWhiteLabelRequests();
        fetch('/api/tenants', { headers: authHeader() })
          .then(r => r.json())
          .then(t => { if (Array.isArray(t)) setTenants(t); });
      } else {
        alert(`Error: ${data.error}`);
      }
    } catch (err) {
      alert('Network error approving request');
    } finally {
      setApprovingReqId(null);
    }
  };

  const handleRejectWhiteLabel = async (requestId: string) => {
    try {
      const res = await fetch(`/api/tenet/admin/white-label-requests/${requestId}/reject`, {
        method: 'POST',
        headers: authHeader(),
      });
      const data = await res.json();
      if (data.success) {
        fetchWhiteLabelRequests();
      }
    } catch (err) {
      console.error('Failed to reject request:', err);
    }
  };

  const fetchTenantBilling = useCallback(async (tenantId: string, month?: string, rate?: number) => {
    if (!tenantId) return;
    setLoadingBilling(true);
    try {
      const m = month !== undefined ? month : billingMonth;
      const r = rate !== undefined ? rate : billingLkrRate;
      const res = await fetch(`/api/admin/tenants/${tenantId}/token-billing?month=${encodeURIComponent(m)}&rate=${r}`, {
        headers: authHeader(),
      });
      if (res.ok) {
        const data = await res.json();
        if (data && (data.tokenUsage || data.invoice?.tokenUsage)) {
          // Normalize so tokenUsage and amounts are always guaranteed at top level
          const normalized = {
            ...data,
            tokenUsage: data.tokenUsage || data.invoice?.tokenUsage || {},
            amounts: data.amounts || data.invoice?.amounts || {},
            assignedModel: typeof data.assignedModel === 'object' ? data.assignedModel : getModelPricing(data.assignedModel || data.assignedModelId),
          };
          setBillingReport(normalized);
        } else {
          setBillingReport(null);
        }
      } else {
        setBillingReport(null);
      }
    } catch (err) {
      console.error('Failed to fetch tenant billing report:', err);
      setBillingReport(null);
    } finally {
      setLoadingBilling(false);
    }
  }, [authHeader, billingMonth, billingLkrRate]);

  useEffect(() => {
    if (activeTab === 'token-billing' && expandedTenantId) {
      fetchTenantBilling(expandedTenantId, billingMonth, billingLkrRate);
    }
  }, [activeTab, expandedTenantId, billingMonth, billingLkrRate, fetchTenantBilling]);

  const fetchTenantConversations = useCallback(async (tenantId: string | number, channel?: string, search?: string) => {
    if (!tenantId) return;
    setLoadingConversations(true);
    try {
      let url = `/api/admin/tenants/${tenantId}/conversations?limit=80`;
      if (channel && channel !== 'all') url += `&channel=${encodeURIComponent(channel)}`;
      if (search && search.trim()) url += `&search=${encodeURIComponent(search.trim())}`;
      const res = await fetch(url, { headers: authHeader });
      const data = await res.json();
      if (data && data.success && Array.isArray(data.conversations)) {
        setTenantConversations(data.conversations);
        if (data.conversations.length > 0) {
          setSelectedConversation((prev: any) => {
            if (!prev) return data.conversations[0];
            const updated = data.conversations.find((c: any) => c.sessionId === prev.sessionId);
            return updated || data.conversations[0];
          });
        } else {
          setSelectedConversation(null);
        }
      } else {
        setTenantConversations([]);
        setSelectedConversation(null);
      }
    } catch (err) {
      console.error('Failed to fetch tenant conversations:', err);
      setTenantConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  }, [authHeader]);

  useEffect(() => {
    if (activeTab === 'conversations' && expandedTenantId) {
      fetchTenantConversations(expandedTenantId, conversationChannelFilter, conversationSearch);
    }
  }, [activeTab, expandedTenantId, conversationChannelFilter, conversationSearch, fetchTenantConversations]);

  const handleDeleteConversation = async (tenantId: string | number, sessionId: string) => {
    if (!confirm(`Are you sure you want to permanently delete conversation session "${sessionId}"?`)) return;
    try {
      const res = await fetch(`/api/admin/tenants/${tenantId}/conversations/${sessionId}`, {
        method: 'DELETE',
        headers: authHeader,
      });
      const data = await res.json();
      if (data && data.success) {
        setTenantConversations(prev => prev.filter(c => c.sessionId !== sessionId));
        setSelectedConversation((prev: any) => (prev?.sessionId === sessionId ? null : prev));
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
      alert('Failed to delete conversation');
    }
  };

  const [billingTestStore, setBillingTestStore] = useState<{ store: PosStore; invoice: any; loading: boolean; error?: string } | null>(null);

  // Telemetry card interactions
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'GRACE_PERIOD' | 'LOCKED'>('ALL');
  const [showCommissionLedger, setShowCommissionLedger] = useState(false);
  const [showLunaUsageLedger, setShowLunaUsageLedger] = useState(false);
  const [lunaUsageData, setLunaUsageData] = useState<{ records: any[]; totalCost: number; totalInputTokens: number; totalOutputTokens: number } | null>(null);
  const [lunaUsageLoading, setLunaUsageLoading] = useState(false);
  const [showStoreAudit, setShowStoreAudit] = useState(false);
  const [auditStore, setAuditStore] = useState<PosStore | null>(null);
  const [auditData, setAuditData] = useState<{ invoices: any[]; lunaUsage: any[]; totalBilledUsd: number } | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  // POS Store Activity Log — per-store usage analytics in God Mode
  const [showPosActivity, setShowPosActivity] = useState(false);
  const [posActivityStore, setPosActivityStore] = useState<PosStore | null>(null);
  const [posActivityData, setPosActivityData] = useState<{ events: any[]; stats: any[] } | null>(null);
  const [posActivityLoading, setPosActivityLoading] = useState(false);
  const [posActivityFrom, setPosActivityFrom] = useState('');
  const [posActivityTo, setPosActivityTo] = useState('');

  // Luna Intelligence session analytics
  const [showLunaIntel, setShowLunaIntel] = useState(false);
  const [lunaIntelData, setLunaIntelData] = useState<{ sessions: any[]; summary: any } | null>(null);
  const [lunaIntelLoading, setLunaIntelLoading] = useState(false);
  const [lunaIntelDateFrom, setLunaIntelDateFrom] = useState('');
  const [lunaIntelDateTo, setLunaIntelDateTo] = useState('');

  // Personal Luna panel
  const [showPersonalLuna, setShowPersonalLuna] = useState(false);
  // Luna Master Config & Permission Hub
  const [showLunaConfigHub, setShowLunaConfigHub] = useState(false);

  // Get storeApiKey for the first approved store (CommissionLedgerModal requires it)
  const firstStoreApiKey = posStores.find(s => s.approvalStatus === 'APPROVED')?.apiKey || '';

  // Pending White-Label & Bespoke Avatar Requests
  const pendingWhiteLabelRequests = whiteLabelRequests.filter(
    (r) => r.status === 'new' || !r.status || r.status === 'PENDING'
  );

  const handleApproveStore = async (id: string) => {
    setApprovingId(id);
    try {
      const res = await fetch(`/api/admin/stores/${id}/approve`, { method: 'PATCH', headers: authHeader() });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Failed to approve'); return; }
      setPosStores(prev => prev.map(s => s.id.toString() === id ? { ...s, approvalStatus: 'APPROVED' } : s));
    } catch { alert('Cannot reach server'); }
    finally { setApprovingId(null); }
  };

  const handleRejectStore = async (id: string) => {
    if (!window.confirm('Reject this sub-store request?')) return;
    setRejectingId(id);
    try {
      const res = await fetch(`/api/admin/stores/${id}/reject`, { method: 'PATCH', headers: authHeader(), body: JSON.stringify({}) });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Failed to reject'); return; }
      setPosStores(prev => prev.map(s => s.id.toString() === id ? { ...s, approvalStatus: 'REJECTED' } : s));
    } catch { alert('Cannot reach server'); }
    finally { setRejectingId(null); }
  };

  const handleResetCredentials = async () => {
    if (!resetCredsStore) return;
    if (!resetCredsData.adminUsername.trim() && !resetCredsData.password && !resetCredsData.storeCode.trim()) {
      setResetCredsError('Provide a new store code, username, and/or password');
      return;
    }
    setResetCredsSaving(true);
    setResetCredsError('');
    try {
      const res = await fetch(`/api/stores/${resetCredsStore.id}/credentials`, {
        method: 'PUT',
        headers: authHeader(),
        body: JSON.stringify(resetCredsData),
      });
      const data = await res.json();
      if (!res.ok) { setResetCredsError(data.error || 'Failed to update'); return; }
      setPosStores(prev => prev.map(s => s.id.toString() === resetCredsStore.id.toString() ? { ...s, ...data.store } : s));
      setResetCredsStore(null);
      setResetCredsData({ storeCode: '', adminUsername: '', password: '' });
    } catch {
      setResetCredsError('Cannot reach server');
    } finally { setResetCredsSaving(false); }
  };

  const toggleKeyReveal = (id: string) => {
    setRevealedKeys(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const maskKey = (key: string, id: string) => {
    return revealedKeys[id] ? key : '••••••••••••••••••••';
  };

  useEffect(() => {
    fetch('/luna-widget.js')
      .then(res => res.text())
      .then(text => setRawWidgetCode(text))
      .catch(err => setRawWidgetCode('Error loading raw code. Please check server.'));

    // Fetch Tenants
    fetch('/api/tenants', { headers: authHeader() })
      .then(res => res.json())
      .then(data => { if (Array.isArray(data)) setTenants(data); })
      .catch(err => console.error('Failed to fetch tenants:', err));

    // Fetch Stores
    fetch('/api/stores', { headers: authHeader() })
      .then(res => res.json())
      .then(data => { if (Array.isArray(data)) setPosStores(data); else console.error('Invalid stores data:', data); })
      .catch(err => console.error('Failed to fetch stores:', err));

    // Fetch Kangavena Apps
    fetch('/api/admin/apps', { headers: authHeader() })
      .then(res => res.json())
      .then(data => { if (Array.isArray(data)) setApps(data); })
      .catch(err => console.error('Failed to fetch apps:', err));

    // Fetch Telemetry
    fetch('/api/admin/telemetry', { headers: authHeader() })
      .then(res => res.json())
      .then(data => setTelemetry(data))
      .catch(err => console.error('Failed to fetch telemetry:', err))
      .finally(() => setTelemetryLoading(false));

    // Fetch White-Label & Bespoke Avatar Requests
    fetchWhiteLabelRequests();
  }, [godModeActive]);

  useEffect(() => {
    if (activeSection === 'apps' && godModeActive) {
      // Refresh apps
      fetch('/api/admin/apps', { headers: authHeader() })
        .then(res => res.json())
        .then(data => { if (Array.isArray(data)) setApps(data); })
        .catch(err => console.error('Failed to fetch apps:', err));

      // Fetch detailed logs
      setUsageLogsLoading(true);
      fetch('/api/admin/apps/usage', { headers: authHeader() })
        .then(res => res.json())
        .then(data => { if (Array.isArray(data)) setUsageLogs(data); })
        .catch(err => console.error('Failed to fetch usage logs:', err))
        .finally(() => setUsageLogsLoading(false));
    }
  }, [activeSection]);

  const handleAddTenant = async () => {
    if (!newTenant.name || !newTenant.clientEmail || !newTenant.password) {
      alert('Website name, client email, and password are required!');
      return;
    }
    
    try {
      const res = await fetch('/api/tenet/create-site', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader()
        },
        body: JSON.stringify({
          name: newTenant.name,
          url: newTenant.url,
          purpose: newTenant.purpose,
          customInstructions: newTenant.customInstructions,
          clientEmail: newTenant.clientEmail,
          password: newTenant.password,
          isFullBundle: newTenant.isFullBundle,
          isSetupFeePaid: newTenant.isSetupFeePaid,
          isWhiteLabelEnabled: newTenant.isWhiteLabel,
          planType: newTenant.isFullBundle ? 'pro' : 'basic_trial'
        })
      });
      const data = await res.json();
      if (data.success && data.tenant) {
        setTenants([data.tenant, ...tenants]);
        setIsModalOpen(false);
        alert(`✅ Tenet Client Account Created Successfully!\n\nEmail: ${newTenant.clientEmail}\nPassword: ${newTenant.password}\nAPI Key: ${data.tenant.apiKey}\nPlan: ${newTenant.isFullBundle ? '👑 Full Pro Bundle (Setup Paid + Unlimited)' : 'Basic Trial'}\n\nClient can now log into /login with these credentials!`);
        setNewTenant({ name: '', url: '', purpose: '', customInstructions: '', clientEmail: '', password: '', isFullBundle: true, isSetupFeePaid: true, isWhiteLabel: true });
      } else {
        alert(`❌ Error creating Tenet account: ${data.error}`);
      }
    } catch (error: any) {
      console.error('Failed to add tenant:', error);
      alert(`❌ Error: ${error.message}`);
    }
  };

  const handleDeleteTenant = async (id: string) => {
    const tenant = tenants.find((t: any) => t.id.toString() === id.toString());
    const tenantName = tenant?.name || `Tenant #${id}`;
    const confirmed = window.confirm(
      `⚠️ Are you sure you want to delete "${tenantName}" and ALL associated data?\n\nThis will permanently remove:\n• All tenant users\n• All form submissions\n• All end users\n• All knowledge records\n• All media assets\n• All Luna agent flows\n\nThis action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      const res = await fetch(`/api/tenants/${id}`, { method: 'DELETE', headers: authHeader() });
      const data = await res.json();
      if (res.ok && data.success) {
        setTenants(tenants.filter((t: any) => t.id.toString() !== id.toString()));
        if (expandedTenantId === id.toString()) setExpandedTenantId(null);
        setFeedback({ type: 'success', message: data.message || `"${tenantName}" deleted successfully.` });
      } else {
        setFeedback({ type: 'error', message: data.error || 'Failed to delete tenant.' });
      }
    } catch (error: any) {
      setFeedback({ type: 'error', message: error?.message || 'Network error while deleting tenant.' });
    }
    setTimeout(() => setFeedback(null), 5000);
  };

  const handleCredentialSubmit = async () => {
    if (!credModalTenant) return;
    setCredModalSaving(true);
    try {
      const bodyPayload: any = {
        disallowMasterKeyFallback: credModalDisallowMasterKeyFallback,
      };
      if (credModalEmail) bodyPayload.newEmail = credModalEmail;
      if (credModalPassword) bodyPayload.newPassword = credModalPassword;
      if (credModalAiKey.trim().length > 0) {
        bodyPayload.geminiApiKey = credModalAiKey.trim();
      }

      const res = await fetch(`/api/tenants/${credModalTenant.id}/credentials`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(bodyPayload),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        // Update local tenant state with new email and AI key info
        setTenants((prev: any[]) => prev.map((t: any) =>
          t.id === credModalTenant.id ? { 
            ...t, 
            adminEmail: data.newEmail || t.adminEmail,
            aiKeyInfo: data.aiKeyInfo || t.aiKeyInfo,
            customFields: { ...(t.customFields || {}), disallowMasterKeyFallback: credModalDisallowMasterKeyFallback }
          } : t
        ));
        setFeedback({ type: 'success', message: data.message });
        setCredModalTenant(null);
        setCredModalEmail('');
        setCredModalPassword('');
        setCredModalAiKey('');
        setCredModalKeyTestResult(null);
      } else {
        setFeedback({ type: 'error', message: data.error || 'Failed to update credentials.' });
      }
    } catch (err: any) {
      setFeedback({ type: 'error', message: err?.message || 'Network error.' });
    } finally {
      setCredModalSaving(false);
      setTimeout(() => setFeedback(null), 5000);
    }
  };

  const handleClearTenantAiKey = async () => {
    if (!credModalTenant) return;
    if (!window.confirm(`Revert ${credModalTenant.name} to Platform Default Master Key?`)) return;
    setCredModalSaving(true);
    try {
      const res = await fetch(`/api/tenants/${credModalTenant.id}/credentials`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ geminiApiKey: '' }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setTenants((prev: any[]) => prev.map((t: any) =>
          t.id === credModalTenant.id ? { ...t, aiKeyInfo: data.aiKeyInfo } : t
        ));
        setCredModalTenant((prev: any) => prev ? { ...prev, aiKeyInfo: data.aiKeyInfo } : null);
        setCredModalAiKey('');
        setCredModalKeyTestResult({ success: true, message: 'Reverted to Platform Master Key.' });
      } else {
        setFeedback({ type: 'error', message: data.error || 'Failed to clear key.' });
      }
    } catch (err: any) {
      setFeedback({ type: 'error', message: err?.message || 'Network error.' });
    } finally {
      setCredModalSaving(false);
    }
  };

  const handleTestTenantAiKey = async () => {
    if (!credModalTenant) return;
    setCredModalTestingKey(true);
    setCredModalKeyTestResult(null);
    try {
      const res = await fetch(`/api/tenants/${credModalTenant.id}/test-ai-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ geminiApiKey: credModalAiKey.trim() || undefined }),
      });
      const data = await res.json();
      if (data.success) {
        setCredModalKeyTestResult({ success: true, message: data.message || 'Key verified successfully!' });
      } else {
        setCredModalKeyTestResult({ success: false, message: data.error || 'Key verification failed' });
      }
    } catch (err: any) {
      setCredModalKeyTestResult({ success: false, message: err?.message || 'Network error during test' });
    } finally {
      setCredModalTestingKey(false);
    }
  };

  const fetchTenantAiKeyPool = async (tenantId: number) => {
    setCredModalLoadingPool(true);
    setCredModalKeyResults({});
    try {
      const res = await fetch(`/api/tenants/${tenantId}/ai-keys`, { headers: authHeader() });
      const data = await res.json();
      if (data.success) {
        setCredModalKeyPool(data.keys || []);
        if (data.keyInfo) {
          setCredModalTenant((prev: any) => prev ? { ...prev, aiKeyInfo: data.keyInfo } : null);
        }
      }
    } catch (e) {
      console.warn('Failed to load tenant AI keys', e);
    } finally {
      setCredModalLoadingPool(false);
    }
  };

  const handleAddKeyToPool = async () => {
    if (!credModalTenant || !credModalNewKeyInput.trim()) return;
    setCredModalAddingKey(true);
    try {
      const res = await fetch(`/api/tenants/${credModalTenant.id}/ai-keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          apiKey: credModalNewKeyInput.trim(),
          label: credModalNewKeyLabel.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setCredModalKeyPool(data.keys || []);
        setCredModalNewKeyInput('');
        setCredModalNewKeyLabel('');
        if (data.keyInfo) {
          setCredModalTenant((prev: any) => prev ? { ...prev, aiKeyInfo: data.keyInfo } : null);
        }
        setFeedback({ type: 'success', message: 'API key added to dedicated pool successfully.' });
      } else {
        setFeedback({ type: 'error', message: data.error || 'Failed to add key' });
      }
    } catch (err: any) {
      setFeedback({ type: 'error', message: err?.message || 'Network error' });
    } finally {
      setCredModalAddingKey(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const handleRemoveKeyFromPool = async (keyId: string) => {
    if (!credModalTenant || !window.confirm('Are you sure you want to remove this API key from the dedicated pool?')) return;
    try {
      const res = await fetch(`/api/tenants/${credModalTenant.id}/ai-keys/${keyId}`, {
        method: 'DELETE',
        headers: authHeader(),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setCredModalKeyPool(data.keys || []);
        if (data.keyInfo) {
          setCredModalTenant((prev: any) => prev ? { ...prev, aiKeyInfo: data.keyInfo } : null);
        }
        setFeedback({ type: 'success', message: 'Key removed from pool.' });
      } else {
        setFeedback({ type: 'error', message: data.error || 'Failed to remove key' });
      }
    } catch (err: any) {
      setFeedback({ type: 'error', message: err?.message || 'Network error' });
    } finally {
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const handleTestPoolKey = async (keyId: string) => {
    if (!credModalTenant) return;
    setCredModalTestingKeyId(keyId);
    try {
      const res = await fetch(`/api/tenants/${credModalTenant.id}/ai-keys/${keyId}/test`, {
        method: 'POST',
        headers: authHeader(),
      });
      const data = await res.json();
      setCredModalKeyResults(prev => ({
        ...prev,
        [keyId]: {
          success: data.success,
          message: data.message || data.error || (data.success ? 'Key verified successfully' : 'Key verification failed')
        }
      }));
    } catch (err: any) {
      setCredModalKeyResults(prev => ({
        ...prev,
        [keyId]: { success: false, message: err?.message || 'Network error during test' }
      }));
    } finally {
      setCredModalTestingKeyId(null);
    }
  };

  const handleResetPoolKeyCooldown = async (keyId: string) => {
    if (!credModalTenant) return;
    try {
      const res = await fetch(`/api/tenants/${credModalTenant.id}/ai-keys/${keyId}/reset`, {
        method: 'POST',
        headers: authHeader(),
      });
      const data = await res.json();
      if (data.success) {
        await fetchTenantAiKeyPool(credModalTenant.id);
        setFeedback({ type: 'success', message: 'Key status reset to Active.' });
      }
    } catch {}
  };

  const handleAddApp = async () => {
    if (!newApp.name || !newApp.packageName) return;
    setAppsLoading(true);
    try {
      const res = await fetch('/api/admin/apps', {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(newApp)
      });
      const data = await res.json();
      if (res.ok) {
        setApps([data, ...apps]);
        setIsAppModalOpen(false);
        setNewApp({ name: '', packageName: '' });
      }
    } catch (error) {
      console.error('Failed to add app:', error);
    } finally {
      setAppsLoading(false);
    }
  };

  const handleDeleteApp = async (id: string) => {
    try {
      await fetch(`/api/admin/apps/${id}`, { method: 'DELETE', headers: authHeader() });
      setApps(apps.filter(app => app.id.toString() !== id.toString()));
    } catch (error) {
      console.error('Failed to delete app:', error);
    }
  };

  const handleUpdateUrl = async (id: string) => {
    try {
      const res = await fetch(`/api/tenants/${id}`, {
        method: 'PUT',
        headers: authHeader(),
        body: JSON.stringify({ url: editUrlValue })
      });
      const updatedTenant = await res.json();
      setTenants(tenants.map(t => t.id.toString() === id.toString() ? updatedTenant : t));
      setEditingUrlId(null);
    } catch (error) {
      console.error('Failed to update URL:', error);
    }
  };

  const handleManualSync = async (id: string) => {
    setIsSyncing(id);
    try {
      const res = await fetch(`/api/tenants/${id}/resync`, { method: 'POST', headers: authHeader() });
      const data = await res.json();
      
      if (res.ok) {
        setTenants(prev => prev.map(t => 
          t.id.toString() === id.toString() 
            ? { ...t, scrapedContent: data.scrapedContent } 
            : t
        ));
        alert("Website scraping complete! Context updated.");
      } else {
        alert(`Sync failed: ${data.error}`);
      }
    } catch (error) {
      console.error('Manual sync error:', error);
      alert("Failed to connect to server for sync.");
    } finally {
      setIsSyncing(null);
    }
  };

  const handleSyncTenant = async (id: string) => {
    setIsSyncing(id);
    try {
      const res = await fetch(`/api/tenants/${id}/sync`, { method: 'POST', headers: authHeader() });
      if (!res.ok) {
        const errData = await res.json();
        alert(`Sync failed: ${errData.error}\n\nDetails: ${errData.details || 'Please ensure the external URL is correct and the /api/kangavena/schema endpoint is implemented.'}`);
        if (errData.tenant) {
          setTenants(tenants.map(t => t.id.toString() === id.toString() ? errData.tenant : t));
        }
        return;
      }
      const updatedTenant = await res.json();
      setTenants(tenants.map(t => t.id.toString() === id.toString() ? updatedTenant : t));
    } catch (error) {
      console.error('Failed to sync tenant:', error);
      alert('Network error occurred while trying to sync.');
    } finally {
      setIsSyncing(null);
    }
  };

  const handleSaveTenantSettings = async (
    id: string,
    data: {
      customInstructions?: string;
      showKangavenaBranding?: boolean;
      isWhiteLabelEnabled?: boolean;
      customAvatarUrl?: string;
      customAvatarName?: string;
      customDeveloperName?: string;
      assignedModel?: string | null;
      geminiModelPool?: string[];
      masterAiMonthlySpendCapLkr?: number;
      masterAiBudgetPolicy?: 'pause' | 'throttle' | 'alert_only';
      customFields?: any;
    },
    silent = false
  ) => {
    setSavingSettingsId(id);
    try {
      const res = await fetch(`/api/tenants/${id}`, {
        method: 'PUT',
        headers: authHeader(),
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updatedTenant = await res.json();
      setTenants(prev => prev.map(t => {
        if (t.id.toString() !== id.toString()) return t;
        const mergedPool = updatedTenant.geminiModelPool ||
          updatedTenant.customFields?.geminiModelPool ||
          data.geminiModelPool ||
          t.geminiModelPool;
        return {
          ...t,
          ...updatedTenant,
          geminiModelPool: mergedPool,
        };
      }));
      if (!silent) {
        alert('Settings saved successfully!');
      }
    } catch (error) {
      console.error('Failed to save tenant settings:', error);
      if (!silent) alert('Failed to save settings.');
    } finally {
      setSavingSettingsId(null);
    }
  };

  const handleAddPosStore = async () => {
    if (!newPosStore.name || !newPosStore.password) return;
    try {
      const body: any = { ...newPosStore };
      if (body.latitude !== '') body.latitude = parseFloat(body.latitude); else delete body.latitude;
      if (body.longitude !== '') body.longitude = parseFloat(body.longitude); else delete body.longitude;
      const res = await fetch('/api/stores', {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify(body)
      });
      const store = await res.json();
      if (!res.ok) { alert(store.error || 'Failed to create store'); return; }
      setPosStores([store, ...posStores]);
      setIsPosModalOpen(false);
      setNewPosStore({ name: '', location: '', storeCode: '', adminUsername: '', password: '', phone: '', address: '', latitude: '', longitude: '', website: '', email: '' });
      setExpandedPosId(store.id.toString());
    } catch (error) {
      console.error('Failed to add store:', error);
    }
  };

  const handleUpdateStoreProfile = async () => {
    if (!editProfileStore) return;
    setEditProfileSaving(true); setEditProfileError('');
    try {
      const body: any = {
        name: editProfileForm.name.trim() || undefined,
        phone: editProfileForm.phone.trim() || null,
        address: editProfileForm.address.trim() || null,
        latitude: editProfileForm.latitude !== '' ? parseFloat(editProfileForm.latitude) : null,
        longitude: editProfileForm.longitude !== '' ? parseFloat(editProfileForm.longitude) : null,
        website: editProfileForm.website.trim() || null,
        email: editProfileForm.email.trim() || null,
      };
      const res = await fetch(`/api/stores/${editProfileStore.id}`, {
        method: 'PUT',
        headers: authHeader(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setEditProfileError(data.error || 'Failed to save'); return; }
      setPosStores(prev => prev.map(s => s.id.toString() === editProfileStore.id.toString() ? { ...s, ...data.store } : s));
      setEditProfileStore(null);
    } catch { setEditProfileError('Cannot reach server'); }
    finally { setEditProfileSaving(false); }
  };

  const handleDeletePosStore = async (id: string) => {
    try {
      await fetch(`/api/stores/${id}`, { method: 'DELETE', headers: authHeader() });
      setPosStores(posStores.filter(s => s.id.toString() !== id.toString()));
      if (expandedPosId === id.toString()) setExpandedPosId(null);
    } catch (error) {
      console.error('Failed to delete store:', error);
    }
  };

  const handleCardFilter = (status: 'ALL' | 'ACTIVE' | 'GRACE_PERIOD' | 'LOCKED') => {
    setStatusFilter(status);
    setTimeout(() => {
      document.getElementById('store-command-center')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const toggleStoreGroup = (id: string) => {
    setExpandedStoreGroups(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleTestLockAndBill = async (store: PosStore, targetStatus: 'GRACE_PERIOD' | 'LOCKED') => {
    setBillingTestStore({ store, invoice: null, loading: true });
    try {
      const res = await fetch('/api/admin/billing/test-lock', {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({ storeId: store.id, targetStatus }),
      });
      const data = await res.json();
      if (!res.ok) { setBillingTestStore({ store, invoice: null, loading: false, error: data.error || 'Test lock failed' }); return; }
      setBillingTestStore({ store, invoice: data.invoice, loading: false });
      setPosStores(prev => prev.map(s => s.id.toString() === store.id.toString() || s.parentStoreId === Number(store.id)
        ? { ...s, billingStatus: targetStatus } : s));
    } catch (e: unknown) {
      setBillingTestStore({ store, invoice: null, loading: false, error: e instanceof Error ? e.message : 'Network error' });
    }
  };

  const handleForceUnlock = async (store: PosStore) => {
    try {
      const res = await fetch('/api/admin/billing/force-unlock', {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({ storeId: store.id }),
      });
      if (!res.ok) { const data = await res.json(); alert(data.error || 'Force unlock failed'); return; }
      setPosStores(prev => prev.map(s =>
        s.id.toString() === store.id.toString() || s.parentStoreId === Number(store.id)
          ? { ...s, billingStatus: 'ACTIVE' } : s
      ));
    } catch (e: unknown) {
      alert('Network error: ' + (e instanceof Error ? e.message : String(e)));
    }
  };

  const approvedStores = posStores.filter(s => s.approvalStatus !== 'PENDING');
  const rootStores = approvedStores.filter(s => {
    if (statusFilter !== 'ALL' && s.billingStatus !== statusFilter) return false;
    return !s.parentStoreId || !approvedStores.some(p => Number(p.id) === Number(s.parentStoreId));
  });
  const branchMap: Record<string, PosStore[]> = {};
  for (const s of approvedStores) {
    if (s.parentStoreId) {
      const key = String(s.parentStoreId);
      if (!branchMap[key]) branchMap[key] = [];
      branchMap[key].push(s);
    }
  }
  const getUltimatePrompt = (apiKey: string) => {
    const serverUrl = window.location.origin.includes('localhost') ? window.location.origin : 'https://kangavena.com';
    return `I am integrating the Kangavena Luna AI SaaS Widget and Backend Sync. Please execute these exact steps:

1. FRONTEND WIDGET INTEGRATION:
You MUST NOT create a local copy of the widget code. Instead, you will use the centralized Master Script from the Kangavena server. This ensures the AI always stays updated with the latest features, emotions, and sound effects.

Open index.html and paste this exact script tag right before the closing </body> tag:
<script src="${serverUrl}/luna-widget.js" data-api-key="${apiKey}" defer></script>

Completely DELETE any local LunaWidget.tsx, RobotVisualizer.tsx, or local AI logic from the React src folder. The site must rely entirely on the remote script for a floating, expandable Luna AI interface.

WIDGET BEHAVIOR:
- Luna will appear as a small floating bubble in the bottom right corner of the website.
- It will feature Luna's interactive digital face.
- When clicked, Luna will fade in and expand to a larger, immersive interface.
- Users can minimize Luna back to a bubble at any time.

2. BACKEND SCHEMA SYNC ENDPOINT:
You MUST expose the database schema to the Kangavena Big Brain so Luna understands this site's data.
Create a new backend API endpoint: \`GET /api/kangavena/schema\`

Requirements for this endpoint:
- It MUST check the \`Authorization\` header. It should only allow access if the header exactly matches: \`Bearer ${apiKey}\`
- It MUST dynamically read the database schema (all tables and their columns) from the actual database.
- It MUST return a JSON object where keys are table names and values are arrays of column names.

CRITICAL SECURITY: In your GET /api/kangavena/schema endpoint, you MUST explicitly exclude sensitive tables. Create a whitelist array ['courses', 'events', 'staff', 'public_announcements'] and ONLY return the schema for these allowed tables. NEVER expose 'users', 'passwords', or 'payments'.

3. DOMAIN SECURITY:
- For security, this Widget and API Key are LOCKED to the domain registered in the Kangavena Admin Panel.
- Ensure the "Website URL" in the Admin Panel matches exactly where this script is hosted (e.g., innovative.cloudliy.com).
- Unauthorized domains will be blocked from loading the AI interface and accessing the schema.

Example response format:
{
  "courses": ["id", "title", "description", "credits"],
  "events": ["id", "name", "date", "location"]
}

Do not hardcode the schema; read it dynamically from the database using the ORM or database driver you are using, but apply the whitelist filter before returning.`;
  };

  // God Mode PIN gate — must activate before accessing dashboard
  if (!godModeActive) {
    return (
      <div className="min-h-screen bg-[#07080E] text-white flex items-center justify-center font-sans relative overflow-hidden">
        {/* Background glow effects */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/10 blur-[140px] rounded-full pointer-events-none"></div>
        <div className="absolute top-1/3 right-1/4 w-[400px] h-[400px] bg-red-600/10 blur-[120px] rounded-full pointer-events-none"></div>

        <div className="admin-pin-form relative z-10 bg-slate-900/80 backdrop-blur-2xl border border-purple-500/30 rounded-3xl p-8 w-full max-w-md shadow-[0_0_80px_rgba(168,85,247,0.25)] mx-4">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center mx-auto mb-4 shadow-[0_0_20px_rgba(168,85,247,0.3)]">
              <Shield size={32} className="text-purple-400 animate-pulse" />
            </div>
            <h1 className="text-3xl font-black tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-indigo-300 to-red-500 font-mono">
              GOD MODE
            </h1>
            <p className="text-xs text-slate-400 mt-2 font-medium">Master Admin Terminal — PIN Activation Required</p>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); activateGodMode(); }} className="space-y-5">
            {godModeError && (
              <div className="text-red-300 text-xs font-bold bg-red-500/15 p-3.5 rounded-xl border border-red-500/30 flex items-center gap-2">
                <XCircle size={16} className="text-red-400 shrink-0" />
                <span>{godModeError}</span>
              </div>
            )}
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2.5">Security Passcode</label>
              <input
                type="password"
                value={godModePinInput}
                onChange={(e) => setGodModePinInput(e.target.value)}
                className="w-full bg-slate-950/80 border border-purple-500/40 rounded-2xl px-5 py-4 text-white text-center text-2xl tracking-[0.4em] focus:border-purple-400 focus:ring-4 focus:ring-purple-500/20 outline-none transition-all font-mono placeholder:text-slate-700 placeholder:tracking-normal placeholder:text-sm"
                placeholder="Enter Passcode"
                autoFocus
                required
              />
            </div>
            <button type="submit" disabled={godModeLoading || !godModePinInput} className="w-full bg-gradient-to-r from-purple-600 via-indigo-600 to-red-600 hover:from-purple-500 hover:to-red-500 text-white font-black py-4 rounded-2xl transition-all duration-300 uppercase tracking-widest text-xs shadow-[0_0_25px_rgba(168,85,247,0.4)] disabled:opacity-50 active:scale-[0.99]">
              {godModeLoading ? 'Verifying Credentials...' : 'Authenticate & Unlock'}
            </button>
            <button type="button" onClick={onExit} className="w-full border border-white/10 rounded-2xl text-slate-400 hover:text-white hover:bg-white/[0.05] py-3.5 text-xs font-black uppercase tracking-widest transition-all">
              Return to POS
            </button>
          </form>
          <p className="text-[10px] text-slate-500 text-center mt-6 font-mono font-medium">Session auto-locks after 30 minutes of inactivity</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-root min-h-screen bg-[#060911] text-slate-100 font-sans flex flex-col lg:flex-row overflow-x-hidden relative">
      {/* Ambient background lighting */}
      <div className="absolute top-0 right-1/4 w-[700px] h-[400px] bg-cyan-600/5 blur-[180px] rounded-full pointer-events-none"></div>
      <div className="absolute top-1/3 left-0 w-[600px] h-[500px] bg-purple-600/5 blur-[180px] rounded-full pointer-events-none"></div>

      {/* Feedback Toast */}
      {feedback && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-2xl border shadow-2xl max-w-md animate-in fade-in ${
          feedback.type === 'success'
            ? 'bg-emerald-950/90 text-emerald-300 border-emerald-500/40 shadow-[0_0_30px_rgba(16,185,129,0.2)]'
            : 'bg-rose-950/90 text-rose-300 border-rose-500/40 shadow-[0_0_30px_rgba(244,63,94,0.2)]'
        }`}>
          <span className="text-xs font-bold">{feedback.message}</span>
        </div>
      )}

      {/* ── 1. LEFT EXECUTIVE SIDEBAR ─────────────────────────────────── */}
      <aside className="w-full lg:w-72 bg-[#090d16] border-b lg:border-b-0 lg:border-r border-slate-800/80 flex flex-col shrink-0 z-20">
        
        {/* Brand Header */}
        <div className="p-5 sm:p-6 border-b border-slate-800/80 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-purple-600 via-indigo-600 to-cyan-500 p-0.5 shadow-[0_0_20px_rgba(168,85,247,0.4)]">
              <div className="w-full h-full bg-[#090d16] rounded-[14px] flex items-center justify-center">
                <Shield className="text-cyan-400" size={20} />
              </div>
            </div>
            <div>
              <h1 className="text-base font-black text-white tracking-wide font-mono leading-none flex items-center gap-2">
                KANGAVENA
              </h1>
              <span className="text-[10px] font-bold text-purple-400 tracking-widest uppercase font-mono block mt-1">MASTER CONTROL</span>
            </div>
          </div>
        </div>

        {/* Sidebar Nav Items */}
        <div className="flex-1 p-4 space-y-6 overflow-y-auto">
          
          {/* Section 1: Navigation */}
          <div>
            <p className="px-3 text-[10px] font-black text-slate-500 uppercase tracking-widest font-mono mb-2">Platform Governance</p>
            <div className="space-y-1">
              <button
                onClick={() => setActiveSection('overview')}
                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-xs font-mono font-bold transition-all ${
                  activeSection === 'overview'
                    ? 'bg-purple-600/20 text-purple-300 border border-purple-500/40 shadow-[0_0_15px_rgba(168,85,247,0.2)]'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <LayoutDashboard size={16} className={activeSection === 'overview' ? 'text-purple-400' : 'text-slate-400'} />
                <span>Overview & Telemetry</span>
              </button>

              <button
                onClick={() => setActiveSection('internal')}
                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-xs font-mono font-bold transition-all ${
                  activeSection === 'internal'
                    ? 'bg-rose-600/20 text-rose-300 border border-rose-500/40 shadow-[0_0_15px_rgba(244,63,94,0.2)]'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Store size={16} className={activeSection === 'internal' ? 'text-rose-400' : 'text-slate-400'} />
                <span>POS Merchant Stores</span>
                <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 font-mono font-bold">
                  {posStores.length}
                </span>
              </button>

              <button
                onClick={() => setActiveSection('external')}
                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-xs font-mono font-bold transition-all ${
                  activeSection === 'external'
                    ? 'bg-cyan-600/20 text-cyan-300 border border-cyan-500/40 shadow-[0_0_15px_rgba(6,182,212,0.2)]'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Globe size={16} className={activeSection === 'external' ? 'text-cyan-400' : 'text-slate-400'} />
                <span>SaaS Web Tenants</span>
                <div className="ml-auto flex items-center gap-1.5">
                  {pendingWhiteLabelRequests.length > 0 && (
                    <span 
                      className="px-2 py-0.5 rounded-full bg-amber-500 text-slate-950 font-black text-[10px] font-mono animate-pulse flex items-center gap-1 shadow-[0_0_12px_rgba(245,158,11,0.6)]"
                      title={`${pendingWhiteLabelRequests.length} pending White-Label & Avatar request(s)`}
                    >
                      <Crown size={10} /> {pendingWhiteLabelRequests.length}
                    </span>
                  )}
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 font-mono font-bold">
                    {tenants.length}
                  </span>
                </div>
              </button>

              <button
                onClick={() => setActiveSection('tenant-billing')}
                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-xs font-mono font-bold transition-all ${
                  activeSection === 'tenant-billing'
                    ? 'bg-amber-600/20 text-amber-300 border border-amber-500/40 shadow-[0_0_15px_rgba(245,158,11,0.2)]'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <DollarSign size={16} className={activeSection === 'tenant-billing' ? 'text-amber-400' : 'text-slate-400'} />
                <span>Subscriptions & Payments</span>
              </button>

              <button
                onClick={() => setActiveSection('corporate-suite')}
                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-xs font-mono font-bold transition-all ${
                  activeSection === 'corporate-suite'
                    ? 'bg-blue-600/20 text-blue-300 border border-blue-500/40 shadow-[0_0_15px_rgba(59,130,246,0.2)]'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Building2 size={16} className={activeSection === 'corporate-suite' ? 'text-blue-400' : 'text-slate-400'} />
                <span>Corporate Suite Firms</span>
              </button>

              <button
                onClick={() => setActiveSection('apps')}
                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-xs font-mono font-bold transition-all ${
                  activeSection === 'apps'
                    ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/40 shadow-[0_0_15px_rgba(99,102,241,0.2)]'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Smartphone size={16} className={activeSection === 'apps' ? 'text-indigo-400' : 'text-slate-400'} />
                <span>App Distribution</span>
              </button>

              <button
                onClick={() => setActiveSection('partners')}
                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-xs font-mono font-bold transition-all ${
                  activeSection === 'partners'
                    ? 'bg-amber-600/20 text-amber-300 border border-amber-500/40 shadow-[0_0_15px_rgba(245,158,11,0.2)]'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Handshake size={16} className={activeSection === 'partners' ? 'text-amber-400' : 'text-slate-400'} />
                <span>Business Partners</span>
              </button>
            </div>
          </div>

          {/* Section 2: Luna Intelligence Quick Launchers */}
          <div>
            <p className="px-3 text-[10px] font-black text-slate-500 uppercase tracking-widest font-mono mb-2">Luna AI Governance</p>
            <div className="space-y-1">
              <button
                onClick={() => setActiveSection('luna-flow')}
                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-xs font-mono font-bold transition-all ${
                  activeSection === 'luna-flow'
                    ? 'bg-cyan-600/20 text-cyan-300 border border-cyan-500/40 shadow-[0_0_15px_rgba(6,182,212,0.2)]'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <GitBranch size={16} className="text-cyan-400" />
                <span>Luna Agent Builder</span>
              </button>

              <button
                onClick={() => setActiveSection('luna-config')}
                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-xs font-mono font-bold transition-all ${
                  activeSection === 'luna-config'
                    ? 'bg-cyan-600/20 text-cyan-300 border border-cyan-500/40 shadow-[0_0_15px_rgba(6,182,212,0.2)]'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <Sliders size={16} className="text-cyan-400" />
                <span>Luna Matrix Config Hub</span>
              </button>

              <button
                onClick={() => setActiveSection('luna-face')}
                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-xs font-mono font-bold transition-all ${
                  activeSection === 'luna-face'
                    ? 'bg-emerald-600/20 text-emerald-300 border border-emerald-500/40 shadow-[0_0_15px_rgba(16,185,129,0.2)]'
                    : 'text-slate-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <Smile size={16} className="text-emerald-400" />
                <span>Manage Luna Face</span>
              </button>

              <button
                onClick={async () => {
                  setActiveSection('luna-intel');
                  setLunaIntelLoading(true);
                  try {
                    const res = await fetch('/api/admin/luna-session-log', { headers: authHeader() });
                    const d = await res.json();
                    setLunaIntelData(d.error ? { sessions: [], summary: {} } : d);
                  } catch { setLunaIntelData({ sessions: [], summary: {} }); }
                  finally { setLunaIntelLoading(false); }
                }}
                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-xs font-mono font-bold transition-all ${
                  activeSection === 'luna-intel'
                    ? 'bg-purple-600/20 text-purple-300 border border-purple-500/40 shadow-[0_0_15px_rgba(168,85,247,0.2)]'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Eye size={16} className="text-purple-400" />
                <span>Live Session Logs</span>
              </button>

              <button
                onClick={() => setActiveSection('personal-luna')}
                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-xs font-mono font-bold transition-all ${
                  activeSection === 'personal-luna'
                    ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/40 shadow-[0_0_15px_rgba(99,102,241,0.2)]'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <MessageSquare size={16} className="text-indigo-400" />
                <span>Personal Luna Mobile</span>
              </button>

              <button
                onClick={() => setActiveSection('release-hub')}
                className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-xs font-mono font-bold transition-all ${
                  activeSection === 'release-hub'
                    ? 'bg-cyan-600/20 text-cyan-300 border border-cyan-500/40 shadow-[0_0_15px_rgba(6,182,212,0.2)]'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Rocket size={16} className="text-cyan-400" />
                <span>Zero-Downtime Release Hub</span>
              </button>
            </div>
          </div>

        </div>

        {/* Sidebar Footer Controls */}
        <div className="p-4 border-t border-slate-800/80 bg-slate-950/80 space-y-3">
          <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-white/5 border border-white/10">
            <span className="text-[10px] font-bold text-slate-300 font-mono">SUBDOMAIN ROUTING</span>
            <button
              onClick={() => setIsPosOnSubdomainEnabled(!isPosOnSubdomainEnabled)}
              className={`w-9 h-5 rounded-full transition-all duration-300 flex items-center px-0.5 ${isPosOnSubdomainEnabled ? 'bg-purple-600' : 'bg-slate-800 border border-slate-700'}`}
            >
              <div className={`w-3.5 h-3.5 bg-white rounded-full transition-transform duration-300 ${isPosOnSubdomainEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
            </button>
          </div>

          <button
            onClick={onExit}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/30 text-xs font-mono font-bold transition-all"
          >
            <ArrowLeft size={14} /> Exit Master Control
          </button>
        </div>
      </aside>

      {/* ── 2. MAIN EXECUTIVE WORKSPACE STAGE ───────────────────────────── */}
      <main className="flex-1 flex flex-col min-w-0 bg-[#060911]">
        
        {/* Top Header Bar */}
        <header className="px-6 sm:px-8 py-5 border-b border-slate-800/80 bg-[#090d16]/90 backdrop-blur-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sticky top-0 z-10 shadow-lg">
          <div>
            <div className="flex items-center gap-2 text-xs font-mono text-slate-400">
              <span>Governance</span>
              <span>/</span>
              <span className="text-purple-400 font-bold uppercase">{activeSection}</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-black text-white font-mono tracking-tight mt-0.5">
              {activeSection === 'overview' ? 'Platform Telemetry & Governance' :
               activeSection === 'internal' ? 'POS Merchant Store Infrastructure' :
               activeSection === 'external' ? 'External SaaS Web Tenants' :
               activeSection === 'luna-flow' ? 'Node-Based Luna Agent Flow Builder' :
               activeSection === 'corporate-suite' ? 'Corporate Advisory & Statutory Filing Suite' :
               activeSection === 'luna-config' ? 'Luna Matrix Governance & Prompt Hub' :
               activeSection === 'luna-face' ? 'Luna Face & Emotion Parameter Studio' :
               activeSection === 'luna-intel' ? 'Luna Session Telemetry & Audit Logs' :
               activeSection === 'tenant-billing' ? 'Tenant Subscriptions & PayPal Payment Ledger' :
               activeSection === 'release-hub' ? 'Zero-Downtime Release Governance & Canary Hub' : 'Mobile & Desktop App Distribution'}
            </h2>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
            {pendingWhiteLabelRequests.length > 0 && (
              <button
                onClick={() => {
                  setActiveSection('external');
                }}
                className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-gradient-to-r from-amber-500/25 via-orange-500/25 to-amber-500/25 border border-amber-500/60 text-amber-300 font-mono text-xs font-black shadow-[0_0_25px_rgba(245,158,11,0.35)] animate-pulse hover:scale-105 transition cursor-pointer"
                title="Click to view and approve pending white-label inquiries"
              >
                <Crown size={15} className="text-amber-400 animate-bounce" />
                <span>{pendingWhiteLabelRequests.length} WHITE-LABEL REQ</span>
              </button>
            )}

            <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 font-mono text-xs font-bold shadow-[0_0_15px_rgba(16,185,129,0.15)]">
              <Shield size={14} className="text-emerald-400" />
              <span>GOD MODE SECURE</span>
            </div>

            {activeSection === 'internal' && (
              <button
                onClick={() => setIsPosModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white rounded-xl transition-all text-xs font-black font-mono uppercase tracking-wider shadow-[0_0_20px_rgba(239,68,68,0.35)] shrink-0 active:scale-[0.98]"
              >
                <Plus size={15} /> ADD POS STORE
              </button>
            )}
            {activeSection === 'external' && (
              <>
                <button
                  onClick={() => setShowPricingMatrixModal(true)}
                  className="flex items-center gap-2 px-3.5 py-2 bg-gradient-to-r from-cyan-600/30 to-blue-600/30 hover:from-cyan-500/40 hover:to-blue-500/40 border border-cyan-500/50 text-cyan-300 rounded-xl transition-all text-xs font-black font-mono uppercase tracking-wider shadow-[0_0_20px_rgba(6,182,212,0.25)] shrink-0 active:scale-[0.98] cursor-pointer"
                  title="View full AI Model Pricing Matrix for all Gemini & MiMo models in USD and LKR"
                >
                  <DollarSign size={15} className="text-cyan-400" />
                  <span>MODEL PRICING MATRIX</span>
                </button>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-xl transition-all text-xs font-black font-mono uppercase tracking-wider shadow-[0_0_20px_rgba(168,85,247,0.35)] shrink-0 active:scale-[0.98] cursor-pointer"
                >
                  <Plus size={15} /> ADD WEBSITE
                </button>
              </>
            )}
            {activeSection === 'apps' && (
              <button
                onClick={() => setIsAppModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-xl transition-all text-xs font-black font-mono uppercase tracking-wider shadow-[0_0_20px_rgba(59,130,246,0.35)] shrink-0 active:scale-[0.98]"
              >
                <Plus size={15} /> ADD APP
              </button>
            )}
          </div>
        </header>

        {/* Main Stage Content Area */}
        <div className="flex-1 p-6 sm:p-8 space-y-8 overflow-y-auto">
          
          {/* OVERVIEW SECTION */}
          {activeSection === 'overview' && (
            <div className="space-y-6">
              {/* Telemetry Operations Grid */}
              <div className="bg-[#090d16]/90 border border-slate-800/80 rounded-2xl p-6 shadow-xl">
                <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-800">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                      <Activity className="text-cyan-400" size={18} />
                    </div>
                    <div>
                      <h3 className="text-sm font-black text-white font-mono uppercase tracking-wider">PLATFORM TELEMETRY METRICS</h3>
                      <p className="text-xs text-slate-400 font-mono">Real-time revenue, AI engine billing, and store health</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-mono text-emerald-400">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping"></span>
                    <span>SYSTEM LIVE (99.99%)</span>
                  </div>
                </div>

                {telemetryLoading ? (
                  <div className="flex items-center justify-center py-10 gap-3 text-slate-400 font-mono text-xs">
                    <Loader2 className="animate-spin text-cyan-400" size={20} /> Loading telemetry metrics...
                  </div>
                ) : telemetry && !telemetry.error ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
                    
                    {/* Revenue */}
                    <button onClick={() => setShowCommissionLedger(true)} className="bg-black/40 border border-white/10 hover:border-emerald-500/50 rounded-2xl p-4 text-left transition-all group hover:bg-emerald-500/5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono group-hover:text-emerald-400">Platform Revenue</span>
                        <DollarSign size={16} className="text-emerald-400" />
                      </div>
                      <p className="text-xl font-black text-white font-mono leading-tight">
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(telemetry.totalRevenue || 0)}
                      </p>
                      <span className="text-[10px] text-emerald-400 font-mono mt-1.5 block">View Commission Ledger →</span>
                    </button>

                    {/* Luna Tokens */}
                    <button
                      onClick={async () => {
                        setShowLunaUsageLedger(true); setLunaUsageLoading(true);
                        try {
                          const res = await fetch('/api/admin/luna-usage/global', { headers: authHeader() });
                          const d = await res.json();
                          setLunaUsageData(d && !d.error ? { records: d.records || [], totalCost: d.totalCost || 0, totalInputTokens: d.totalInputTokens || 0, totalOutputTokens: d.totalOutputTokens || 0 } : { records: [], totalCost: 0, totalInputTokens: 0, totalOutputTokens: 0 });
                        } catch { setLunaUsageData({ records: [], totalCost: 0, totalInputTokens: 0, totalOutputTokens: 0 }); }
                        finally { setLunaUsageLoading(false); }
                      }}
                      className="bg-black/40 border border-white/10 hover:border-purple-500/50 rounded-2xl p-4 text-left transition-all group hover:bg-purple-500/5"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono group-hover:text-purple-400">Luna Tokens</span>
                        <BrainCircuit size={16} className="text-purple-400" />
                      </div>
                      <p className="text-xl font-black text-white font-mono leading-tight">
                        {new Intl.NumberFormat('en-US', { notation: 'compact' }).format((telemetry.totalLunaUsage?.inputTokens || 0) + (telemetry.totalLunaUsage?.outputTokens || 0))}
                      </p>
                      <span className="text-[10px] text-purple-400 font-mono mt-1.5 block">
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(telemetry.totalLunaUsage?.totalCostUsd || 0)} Billed
                      </span>
                    </button>

                    {/* Luna Intel */}
                    <button
                      onClick={async () => {
                        setActiveSection('luna-intel'); setLunaIntelLoading(true);
                        try {
                          const res = await fetch('/api/admin/luna-session-log', { headers: authHeader() });
                          const d = await res.json();
                          setLunaIntelData(d.error ? { sessions: [], summary: {} } : d);
                        } catch { setLunaIntelData({ sessions: [], summary: {} }); }
                        finally { setLunaIntelLoading(false); }
                      }}
                      className="bg-black/40 border border-white/10 hover:border-cyan-500/50 rounded-2xl p-4 text-left transition-all group hover:bg-cyan-500/5"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono group-hover:text-cyan-400">Luna Intel</span>
                        <Eye size={16} className="text-cyan-400" />
                      </div>
                      <p className="text-xl font-black text-cyan-400 font-mono leading-tight">
                        {telemetry.totalLunaUsage ? ((telemetry.totalLunaUsage.inputTokens || 0) + (telemetry.totalLunaUsage.outputTokens || 0)).toLocaleString() : '—'}
                      </p>
                      <span className="text-[10px] text-cyan-400 font-mono mt-1.5 block">Inspect Live Sessions →</span>
                    </button>

                    {/* Personal Luna */}
                    <button onClick={() => setActiveSection('personal-luna')} className="bg-black/40 border border-white/10 hover:border-indigo-500/50 rounded-2xl p-4 text-left transition-all group hover:bg-indigo-500/5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono group-hover:text-indigo-400">Personal Luna</span>
                        <Smartphone size={16} className="text-indigo-400" />
                      </div>
                      <p className="text-xl font-black text-indigo-400 font-mono leading-tight">Mobile Hub</p>
                      <span className="text-[10px] text-indigo-400 font-mono mt-1.5 block">User Accounts & Chats →</span>
                    </button>

                    {/* Luna Matrix Config Hub */}
                    <button onClick={() => setActiveSection('luna-config')} className="bg-gradient-to-br from-cyan-950/60 to-purple-950/60 border border-cyan-500/50 hover:border-cyan-400 rounded-2xl p-4 text-left transition-all group shadow-[0_0_20px_rgba(6,182,212,0.2)] hover:scale-[1.02]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-black text-cyan-300 uppercase tracking-wider font-mono group-hover:text-white">Luna Matrix</span>
                        <Sliders size={16} className="text-cyan-400" />
                      </div>
                      <p className="text-xl font-black text-white font-mono leading-tight">Config Hub</p>
                      <span className="text-[10px] text-cyan-300 font-mono font-bold mt-1.5 block">Rules, Prompts & RBAC →</span>
                    </button>

                    {/* Active Stores */}
                    <button onClick={() => { setActiveSection('internal'); handleCardFilter('ACTIVE'); }} className="bg-black/40 border border-white/10 hover:border-emerald-500/50 rounded-2xl p-4 text-left transition-all group hover:bg-emerald-500/5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono group-hover:text-emerald-400">Active Stores</span>
                        <CheckCircle size={16} className="text-emerald-400" />
                      </div>
                      <p className="text-xl font-black text-emerald-400 font-mono leading-tight">{telemetry.tenantCounts?.ACTIVE || 0}</p>
                      <span className="text-[10px] text-emerald-400 font-mono mt-1.5 block">Filter Active Stores</span>
                    </button>

                    {/* Restricted Stores */}
                    <button onClick={() => { setActiveSection('internal'); handleCardFilter('LOCKED'); }} className="bg-black/40 border border-white/10 hover:border-rose-500/50 rounded-2xl p-4 text-left transition-all group hover:bg-rose-500/5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono group-hover:text-rose-400">Locked / Grace</span>
                        <XCircle size={16} className="text-rose-400" />
                      </div>
                      <p className="text-xl font-black text-rose-400 font-mono leading-tight">
                        {telemetry.tenantCounts?.LOCKED || 0} <span className="text-xs text-amber-400">({telemetry.tenantCounts?.GRACE_PERIOD || 0} G)</span>
                      </p>
                      <span className="text-[10px] text-rose-400 font-mono mt-1.5 block">Filter Restricted Stores</span>
                    </button>

                  </div>
                ) : null}
              </div>
            </div>
          )}

          {/* LUNA AGENT FLOW BUILDER SECTION */}
          {activeSection === 'luna-flow' && (
            <div className="h-[calc(100vh-170px)] min-h-[640px] w-full">
              <FlowErrorBoundary>
                <React.Suspense fallback={
                  <div className="flex items-center justify-center h-full bg-slate-950/60 rounded-2xl">
                    <div className="text-center space-y-3">
                      <Loader2 className="w-8 h-8 animate-spin text-cyan-400 mx-auto" />
                      <p className="text-sm text-slate-400 font-medium">Loading Luna Flow Builder...</p>
                    </div>
                  </div>
                }>
                  <LazyLunaFlowBuilder authHeader={authHeader} />
                </React.Suspense>
              </FlowErrorBoundary>
            </div>
          )}

          {/* LUNA MATRIX CONFIG HUB SECTION */}
          {activeSection === 'luna-config' && (
            <div className="space-y-6">
              <LunaMasterConfigHub
                isInline
                isOpen={true}
                onClose={() => setActiveSection('overview')}
                authHeader={authHeader}
              />
            </div>
          )}

          {/* LUNA FACE HUB SECTION */}
          {activeSection === 'luna-face' && (
            <div className="space-y-6">
              <LunaFaceHub
                isInline
                authHeader={authHeader}
              />
            </div>
          )}

          {/* LUNA SESSION INTEL & REAL-TIME TELEMETRY HUB */}
          {activeSection === 'luna-intel' && (
            <div className="space-y-6">
              <div className="bg-[#090D18] border border-cyan-500/30 rounded-2xl w-full flex flex-col overflow-hidden shadow-xl">
                {/* Header */}
                <div className="px-6 py-5 border-b border-cyan-500/20 bg-slate-950/80 shrink-0">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3.5">
                      <div className="w-10 h-10 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                        <BrainCircuit className="text-cyan-400 animate-pulse" size={20} />
                      </div>
                      <div>
                        <h2 className="text-xl font-black text-white tracking-tight font-mono">LUNA INTELLIGENCE</h2>
                        <p className="text-xs text-cyan-400/80 font-mono">Real-time multi-gateway observability & audit hub</p>
                      </div>
                    </div>

                    {/* Sub-tab Switcher: Live Telemetry vs Session Audit */}
                    <div className="flex items-center gap-1.5 p-1 bg-black/60 border border-cyan-500/30 rounded-2xl">
                      <button
                        onClick={() => setLunaIntelSubTab('live-telemetry')}
                        className={`px-4 py-2 rounded-xl text-xs font-mono font-bold transition-all flex items-center gap-2 ${
                          lunaIntelSubTab === 'live-telemetry'
                            ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/50 shadow-[0_0_15px_rgba(6,182,212,0.3)]'
                            : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        <Activity size={14} className="text-cyan-400 animate-pulse" />
                        <span>🛰️ Live Telemetry & Fallback HUD</span>
                      </button>

                      <button
                        onClick={() => setLunaIntelSubTab('session-audit')}
                        className={`px-4 py-2 rounded-xl text-xs font-mono font-bold transition-all flex items-center gap-2 ${
                          lunaIntelSubTab === 'session-audit'
                            ? 'bg-purple-500/20 text-purple-300 border border-purple-500/50 shadow-[0_0_15px_rgba(168,85,247,0.3)]'
                            : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        <Clock size={14} className="text-purple-400" />
                        <span>📜 Historical Session Audit</span>
                      </button>
                    </div>
                  </div>

                  {/* Date range filter row ONLY shown when in session-audit tab */}
                  {lunaIntelSubTab === 'session-audit' && (
                    <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-white/5">
                      <span className="text-[10px] text-slate-400 uppercase tracking-widest font-black">Filter by date:</span>
                      <input type="date" value={lunaIntelDateFrom} onChange={e => setLunaIntelDateFrom(e.target.value)}
                        className="bg-black/60 border border-white/10 rounded-xl px-3 py-1.5 text-xs text-slate-200 font-mono outline-none focus:border-cyan-500/50 [color-scheme:dark]" />
                      <span className="text-slate-500 text-xs">→</span>
                      <input type="date" value={lunaIntelDateTo} onChange={e => setLunaIntelDateTo(e.target.value)}
                        className="bg-black/60 border border-white/10 rounded-xl px-3 py-1.5 text-xs text-slate-200 font-mono outline-none focus:border-cyan-500/50 [color-scheme:dark]" />
                      <button onClick={async () => {
                        setLunaIntelLoading(true);
                        try {
                          const params = new URLSearchParams();
                          if (lunaIntelDateFrom) params.set('from', lunaIntelDateFrom);
                          if (lunaIntelDateTo)   params.set('to',   lunaIntelDateTo);
                          const url = `/api/admin/luna-session-log${params.toString() ? '?' + params : ''}`;
                          const res = await fetch(url, { headers: authHeader() });
                          const d = await res.json();
                          setLunaIntelData(d.error ? { sessions: [], summary: {} } : d);
                        } catch { setLunaIntelData({ sessions: [], summary: {} }); }
                        finally { setLunaIntelLoading(false); }
                      }} className="px-4 py-1.5 rounded-xl bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 text-xs font-black uppercase tracking-wider hover:bg-cyan-500/30 transition-all flex items-center gap-2 shadow-sm">
                        <RefreshCw size={12} /> {lunaIntelDateFrom || lunaIntelDateTo ? 'Search' : 'Latest'}
                      </button>
                      {(lunaIntelDateFrom || lunaIntelDateTo) && (
                        <button onClick={async () => {
                          setLunaIntelDateFrom(''); setLunaIntelDateTo('');
                          setLunaIntelLoading(true);
                          try {
                            const res = await fetch('/api/admin/luna-session-log', { headers: authHeader() });
                            const d = await res.json();
                            setLunaIntelData(d.error ? { sessions: [], summary: {} } : d);
                          } catch { setLunaIntelData({ sessions: [], summary: {} }); }
                          finally { setLunaIntelLoading(false); }
                        }} className="text-xs text-slate-400 hover:text-slate-200 transition-colors underline font-medium">
                          Clear filter
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex-1 p-6 font-sans">
                  {lunaIntelSubTab === 'live-telemetry' ? (
                    <LunaLiveMonitorHUD authHeader={authHeader} adminToken={adminToken} />
                  ) : lunaIntelLoading ? (
                    <div className="flex flex-col items-center justify-center h-48">
                      <div className="w-10 h-10 border-2 border-cyan-500/30 border-t-cyan-400 rounded-full animate-spin mb-3" />
                      <span className="text-cyan-400 text-xs font-mono font-medium">Loading session telemetry...</span>
                    </div>
                  ) : lunaIntelData ? (
                    <>
                      {/* KPI Summary Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                        {[
                          { label: 'POS', value: lunaIntelData.summary.posSessions ?? 0, color: 'text-emerald-400' },
                          { label: 'Fast Stock', value: lunaIntelData.summary.fastStockSessions ?? 0, color: 'text-amber-400' },
                          { label: 'Express', value: lunaIntelData.summary.expressSessions ?? 0, color: 'text-rose-400' },
                          { label: 'Unique IPs', value: lunaIntelData.summary.uniqueIPs ?? 0, color: 'text-violet-400' },
                          { label: 'Mobile', value: lunaIntelData.summary.mobileUsers ?? 0, color: 'text-pink-400' },
                          { label: 'Desktop', value: lunaIntelData.summary.desktopUsers ?? 0, color: 'text-slate-300' },
                          { label: 'Avg Duration', value: `${lunaIntelData.summary.avgDurationSec ?? 0}s`, color: 'text-teal-400' },
                          { label: 'Today Tokens', value: (lunaIntelData.summary.todayTokens ?? 0).toLocaleString(), color: 'text-amber-300' },
                          { label: 'Total Sessions', value: lunaIntelData.summary.totalSessions ?? 0, color: 'text-white' },
                        ].map(({ label, value, color }) => (
                          <div key={label} className="bg-black/50 border border-white/10 rounded-2xl p-3.5">
                            <p className="text-[9px] text-slate-400 uppercase tracking-widest font-black mb-1">{label}</p>
                            <p className={`text-xl font-black font-mono ${color}`}>{value}</p>
                          </div>
                        ))}
                      </div>

                      {/* ── Grouped Session View ── */}
                      <LunaGroupedView sessions={lunaIntelData.sessions} />
                    </>
                  ) : (
                    <div className="text-center py-12 text-slate-400 font-mono text-xs">Failed to load session data.</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ZERO-DOWNTIME RELEASE GOVERNANCE & CANARY HUB (AGENTS.md Rule 23) */}
          {activeSection === 'release-hub' && (
            <div className="space-y-6">
              <ReleaseGovernanceHub authHeader={authHeader} />
            </div>
          )}

          {/* TENANT SUBSCRIPTIONS & PAYMENT LEDGER */}
          {activeSection === 'tenant-billing' && (
            <TenantBillingHub
              authHeader={authHeader}
              tenants={tenants}
              onRefreshTenants={() => {
                fetch('/api/tenants', { headers: authHeader() })
                  .then(res => res.json())
                  .then(data => { if (Array.isArray(data)) setTenants(data); })
                  .catch(err => console.error('Failed to fetch tenants:', err));
              }}
            />
          )}

          {/* BUSINESS PARTNERS GOVERNANCE HUB */}
          {activeSection === 'partners' && (
            <BusinessPartnersHub authHeader={authHeader} />
          )}
        
        {activeSection === 'external' && (
          <div className="space-y-6">
            {/* Top Action Bar for Creating Tenet Client Credentials */}
            <div className="bg-slate-900/80 border border-purple-500/30 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-xl">
              <div>
                <h2 className="text-lg font-black text-white flex items-center gap-2 font-mono">
                  <Globe className="w-5 h-5 text-purple-400" />
                  <span>TENET MULTI-TENANT CLIENT ECOSYSTEM</span>
                </h2>
                <p className="text-xs text-slate-400 mt-1">
                  Master Admin control: Create client website accounts, generate credentials, and manage web scraping.
                </p>
              </div>

              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-black text-xs px-5 py-3 rounded-xl shadow-lg shadow-purple-500/20 flex items-center gap-2 transition cursor-pointer shrink-0 uppercase tracking-wider"
              >
                <Plus size={16} />
                <span>Create Tenet Client Account</span>
              </button>
            </div>

            {/* ── Pending White-Label & Bespoke Avatar Requests Global Banner ── */}
            {pendingWhiteLabelRequests.length > 0 && (
              <div className="p-5 bg-gradient-to-r from-amber-500/15 via-slate-950 to-cyan-500/15 border-2 border-amber-500/40 rounded-2xl shadow-[0_0_35px_rgba(245,158,11,0.25)] space-y-4">
                <div className="flex items-center justify-between border-b border-amber-500/20 pb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-400">
                      <Crown size={20} className="animate-bounce" />
                    </div>
                    <div>
                      <h3 className="text-sm font-black text-amber-300 uppercase tracking-wider font-mono flex items-center gap-2">
                        <span>Pending White-Label & Bespoke 3D Avatar Inquiries</span>
                        <span className="px-2 py-0.5 rounded-full bg-amber-500 text-slate-950 font-bold text-[10px]">
                          {pendingWhiteLabelRequests.length} NEW
                        </span>
                      </h3>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Inquiries submitted by tenants from the Flow Builder. 1-Click approve to unlock custom branding immediately without searching cards.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={fetchWhiteLabelRequests}
                    disabled={loadingRequests}
                    className="px-3 py-1.5 bg-black/50 hover:bg-black/80 border border-amber-500/30 rounded-xl text-amber-300 text-xs font-mono transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  >
                    <RefreshCw size={12} className={loadingRequests ? 'animate-spin' : ''} />
                    <span>Refresh</span>
                  </button>
                </div>

                <div className="space-y-3">
                  {pendingWhiteLabelRequests.map((req) => {
                    const fd = req.formData || {};
                    const tenantObj = tenants.find(
                      (t) => String(t.id) === String(req.tenantId) || String(t.apiKey) === String(req.tenantId)
                    );
                    return (
                      <div
                        key={req.id}
                        className="flex flex-col lg:flex-row lg:items-center justify-between bg-black/80 rounded-2xl p-4 border border-amber-500/30 gap-4 hover:border-amber-500/60 transition-all shadow-lg"
                      >
                        <div className="flex-1 min-w-0 space-y-1.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-black text-amber-400 uppercase tracking-wider font-mono">
                              {fd.requestType === 'agent_rebrand'
                                ? '🏷️ Agent Rebrand'
                                : fd.requestType === 'full_enterprise'
                                ? '👑 Full Enterprise'
                                : '🎨 Bespoke 3D Avatar'}
                            </span>
                            <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-mono font-bold border border-amber-500/40 animate-pulse">
                              AWAITING APPROVAL
                            </span>
                            {req.createdAt && (
                              <span className="text-[11px] text-slate-500 font-mono">
                                {new Date(req.createdAt).toLocaleString()}
                              </span>
                            )}
                          </div>
                          
                          <h4 className="text-sm font-extrabold text-white">
                            Tenant: <span className="text-cyan-300 font-mono">{fd.tenantName || tenantObj?.name || `Tenant #${req.tenantId}`}</span>
                            {tenantObj?.url && (
                              <a href={tenantObj.url} target="_blank" rel="noreferrer" className="text-xs text-purple-400 font-normal ml-2 hover:underline">
                                ({tenantObj.url})
                              </a>
                            )}
                          </h4>

                          {fd.desiredAgentName && (
                            <p className="text-xs text-slate-200">
                              Requested Brand Name: <strong className="text-amber-300 font-mono text-sm">{fd.desiredAgentName}</strong>
                            </p>
                          )}

                          {fd.avatarDescription && (
                            <p className="text-xs text-slate-400 bg-slate-950/90 p-2.5 rounded-xl border border-white/5 font-mono">
                              "{fd.avatarDescription}"
                            </p>
                          )}

                          <div className="flex items-center gap-4 flex-wrap text-xs pt-0.5">
                            {fd.referenceUrl && (
                              <p className="text-cyan-400 truncate max-w-md">
                                Asset/3D URL: <a href={fd.referenceUrl} target="_blank" rel="noreferrer" className="underline font-mono">{fd.referenceUrl}</a>
                              </p>
                            )}
                            {fd.contactPhone && (
                              <p className="text-emerald-400 font-mono">
                                WhatsApp Contact: <strong>{fd.contactPhone}</strong>
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2.5 shrink-0">
                          <button
                            onClick={() => handleApproveWhiteLabel(req.id, fd.referenceUrl, fd.desiredAgentName)}
                            disabled={approvingReqId === req.id}
                            className="px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl transition shadow-[0_0_20px_rgba(16,185,129,0.35)] flex items-center gap-2 cursor-pointer disabled:opacity-50 active:scale-95"
                          >
                            <CheckCircle size={15} />
                            <span>{approvingReqId === req.id ? 'Unlocking...' : '✨ Approve & Unlock'}</span>
                          </button>
                          <button
                            onClick={() => handleRejectWhiteLabel(req.id)}
                            className="px-3.5 py-2.5 bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/30 rounded-xl text-xs font-bold transition cursor-pointer"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-5">
          {tenants.map((tenant) => {
            const tenantPendingRequests = pendingWhiteLabelRequests.filter(
              (r) => String(r.tenantId) === String(tenant.id) || String(r.tenantId) === String(tenant.apiKey)
            );
            return (
            <div key={tenant.id} className="flex flex-col">
              <div 
                onClick={() => {
                  const willOpen = expandedTenantId !== tenant.id.toString();
                  setExpandedTenantId(willOpen ? tenant.id.toString() : null);
                  if (willOpen && tenantPendingRequests.length > 0) {
                    setActiveTab('white-label-requests');
                  }
                }}
                className={`bg-slate-950/80 border admin-tenant-card rounded-2xl p-5 cursor-pointer transition-all duration-300 ${expandedTenantId === tenant.id.toString() ? 'border-purple-500/60 shadow-[0_0_30px_rgba(168,85,247,0.25)]' : 'border-white/10 hover:border-purple-500/40'}`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-extrabold text-lg flex items-center gap-3 text-white">
                      {tenant.name}
                      {expandedTenantId === tenant.id.toString() ? <ChevronUp size={18} className="text-purple-400"/> : <ChevronDown size={18} className="text-slate-400"/>}
                    </h3>
                    <div className="text-xs text-slate-400 font-mono mt-1.5 flex items-center gap-2">
                      API Key: <span className="text-purple-300 font-bold hover:underline cursor-pointer bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20" onClick={(e) => { e.stopPropagation(); toggleKeyReveal(tenant.id.toString()); }}>{maskKey(tenant.apiKey, tenant.id.toString())}</span>
                    </div>
                    {tenant.adminEmail && (
                      <div className="text-xs text-slate-400 font-mono mt-1 flex items-center gap-2">
                        Admin Email: <span className="text-cyan-300 font-bold">{tenant.adminEmail}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {tenantPendingRequests.length > 0 && (
                      <span className="px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider border bg-gradient-to-r from-amber-500/25 via-orange-500/25 to-amber-500/25 text-amber-300 border-amber-500/60 animate-pulse shadow-[0_0_15px_rgba(245,158,11,0.3)] flex items-center gap-1.5 font-mono">
                        <Crown size={12} className="text-amber-400" />
                        <span>{tenantPendingRequests.length} White-Label Req</span>
                      </span>
                    )}
                    {tenant.assignedModel ? (
                      <span className="px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider border bg-cyan-500/20 text-cyan-300 border-cyan-500/50 shadow-[0_0_12px_rgba(6,182,212,0.25)] flex items-center gap-1.5 font-mono" title={`Custom model assigned: ${tenant.assignedModel}`}>
                        <Bot size={12} className="text-cyan-400" />
                        <span>MODEL: {getModelPricing(tenant.assignedModel).displayName}</span>
                      </span>
                    ) : (
                      <span className="px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border bg-slate-800/80 text-slate-400 border-slate-700/60 flex items-center gap-1.5 font-mono" title="Inherits platform sovereign model: gemini-3.1-flash-live-preview">
                        <Bot size={12} className="text-slate-500" />
                        <span>MODEL: GEMINI 3.1 LIVE (GLOBAL)</span>
                      </span>
                    )}
                    {tenant.aiKeyInfo?.hasDedicatedKey ? (
                      <span className="px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider border bg-purple-500/20 text-purple-300 border-purple-500/50 shadow-[0_0_12px_rgba(168,85,247,0.25)] flex items-center gap-1.5 font-mono">
                        <Cpu size={12} className="text-purple-400" />
                        <span>AI: DEDICATED ({tenant.aiKeyInfo.maskedKey})</span>
                      </span>
                    ) : (
                      <span className="px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border bg-slate-800/80 text-slate-400 border-slate-700/60 flex items-center gap-1.5 font-mono">
                        <Cpu size={12} className="text-slate-500" />
                        <span>AI: MASTER</span>
                      </span>
                    )}
                    <span className={`px-3.5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider border shadow-sm ${tenant.status === 'Active' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' : tenant.status === 'Pending Integration' ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' : 'bg-rose-500/20 text-rose-300 border-rose-500/40'}`}>
                      {tenant.status}
                    </span>
                  </div>
                </div>
                {(tenant.ping || tenant.lastSync || tenant.url || tenant.purpose) && (
                  <div className="text-xs text-slate-400 font-mono flex flex-wrap gap-5 mt-4 items-center pt-3 border-t border-white/5">
                    {tenant.ping && <p className="flex items-center gap-1.5"><Activity size={12} className="text-emerald-400" /> Ping: <span className="text-white font-bold">{tenant.ping}</span></p>}
                    {tenant.lastSync && <p className="flex items-center gap-1.5"><Clock size={12} className="text-cyan-400" /> Sync: <span className="text-white font-bold">{tenant.lastSync}</span></p>}
                    {tenant.purpose && <p className="flex items-center gap-1.5"><Zap size={12} className="text-amber-400" /> Purpose: <span className="text-white font-bold">{tenant.purpose}</span></p>}
                    {tenant.url && (
                      <div className="flex items-center gap-2">
                        <p>URL:</p>
                        {editingUrlId === tenant.id.toString() ? (
                          <div className="flex items-center gap-2">
                            <input 
                              type="text" 
                              value={editUrlValue} 
                              onChange={(e) => setEditUrlValue(e.target.value)}
                              className="bg-black border border-purple-500/50 rounded-lg px-2.5 py-1 text-white outline-none text-xs"
                            />
                            <button onClick={() => handleUpdateUrl(tenant.id.toString())} className="text-emerald-400 hover:text-emerald-300 font-bold">Save</button>
                            <button onClick={() => setEditingUrlId(null)} className="text-slate-400 hover:text-slate-300">Cancel</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <a href={tenant.url} target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline font-bold flex items-center gap-1"><Globe size={12} /> {tenant.url}</a>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingUrlId(tenant.id.toString());
                                setEditUrlValue(tenant.url || '');
                              }} 
                              className="text-xs text-purple-400 hover:text-purple-300 font-bold ml-1"
                            >
                              Edit
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Expanded Panel */}
              {expandedTenantId === tenant.id.toString() && (
                <div className="mt-4 bg-slate-950/90 border border-purple-500/30 admin-expanded rounded-2xl p-6 shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/10 blur-[120px] rounded-full pointer-events-none"></div>
                  
                  <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-3 relative z-10">
                    <div className="flex gap-4 overflow-x-auto">
                      <button 
                        onClick={() => setActiveTab('prompt')} 
                        className={`text-xs font-black uppercase tracking-wider pb-2 border-b-2 transition-all ${activeTab === 'prompt' ? 'border-purple-500 text-purple-300' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
                      >
                        Integration Hub
                      </button>
                      <button 
                        onClick={() => {
                          setActiveTab('conversations');
                          fetchTenantConversations(tenant.id.toString(), conversationChannelFilter, conversationSearch);
                        }} 
                        className={`text-xs font-black uppercase tracking-wider pb-2 border-b-2 transition-all flex items-center gap-1.5 ${activeTab === 'conversations' ? 'border-cyan-500 text-cyan-300 font-bold' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
                      >
                        <MessageSquare size={13} className="text-cyan-400" />
                        <span>Chat History & Sessions</span>
                      </button>
                      <button 
                        onClick={() => setActiveTab('scraped')} 
                        className={`text-xs font-black uppercase tracking-wider pb-2 border-b-2 transition-all ${activeTab === 'scraped' ? 'border-purple-500 text-purple-300' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
                      >
                        Scraped Data
                      </button>
                      <button 
                        onClick={() => setActiveTab('logs')} 
                        className={`text-xs font-black uppercase tracking-wider pb-2 border-b-2 transition-all ${activeTab === 'logs' ? 'border-purple-500 text-purple-300' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
                      >
                        Data Logs
                      </button>
                      <button 
                        onClick={() => setActiveTab('schema')} 
                        className={`text-xs font-black uppercase tracking-wider pb-2 border-b-2 transition-all ${activeTab === 'schema' ? 'border-purple-500 text-purple-300' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
                      >
                        Database Schema
                      </button>
                      <button 
                        onClick={() => setActiveTab('ai-settings')} 
                        className={`text-xs font-black uppercase tracking-wider pb-2 border-b-2 transition-all ${activeTab === 'ai-settings' ? 'border-purple-500 text-purple-300' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
                      >
                        ⚙ AI Settings
                      </button>
                      <button 
                        onClick={() => {
                          setActiveTab('token-billing');
                          fetchTenantBilling(tenant.id.toString(), billingMonth, billingLkrRate);
                        }} 
                        className={`text-xs font-black uppercase tracking-wider pb-2 border-b-2 transition-all flex items-center gap-1.5 ${activeTab === 'token-billing' ? 'border-emerald-500 text-emerald-300 font-bold' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
                      >
                        <Receipt size={13} className="text-emerald-400" />
                        <span>Token Billing & Invoices</span>
                      </button>
                      <button 
                        onClick={() => {
                          setActiveTab('white-label-requests');
                          fetchWhiteLabelRequests();
                        }} 
                        className={`text-xs font-black uppercase tracking-wider pb-2 border-b-2 transition-all flex items-center gap-1.5 ${activeTab === 'white-label-requests' ? 'border-amber-500 text-amber-300 font-bold' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
                      >
                        <Crown size={13} className="text-amber-400" />
                        <span>White-Label Inquiries</span>
                        {whiteLabelRequests.filter(r => r.status === 'new').length > 0 && (
                          <span className="px-1.5 py-0.2 rounded-full bg-amber-500 text-slate-950 font-bold text-[9px]">
                            {whiteLabelRequests.filter(r => r.status === 'new').length}
                          </span>
                        )}
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <button 
                        onClick={() => handleSyncTenant(tenant.id.toString())}
                        disabled={isSyncing === tenant.id.toString()}
                        className="flex items-center gap-2 px-3.5 py-2 bg-cyan-500/15 hover:bg-cyan-500/30 text-cyan-300 rounded-xl transition-all border border-cyan-500/30 text-xs font-black uppercase tracking-wider disabled:opacity-50"
                      >
                        <Activity size={14} className={isSyncing === tenant.id.toString() ? 'animate-spin' : ''} /> 
                        {isSyncing === tenant.id.toString() ? 'Syncing...' : 'Check Connection'}
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setCredModalTenant(tenant);
                          setCredModalEmail(tenant.adminEmail || '');
                          setCredModalPassword('');
                          setCredModalAiKey('');
                          setCredModalShowAiKey(false);
                          setCredModalKeyTestResult(null);
                          setCredModalDisallowMasterKeyFallback(Boolean(tenant.customFields?.disallowMasterKeyFallback || tenant.aiKeyInfo?.disallowMasterKeyFallback));
                          fetchTenantAiKeyPool(tenant.id);
                        }}
                        className="flex items-center gap-2 px-3.5 py-2 bg-cyan-500/15 hover:bg-cyan-500/30 text-cyan-300 rounded-xl transition-all border border-cyan-500/30 text-xs font-black uppercase tracking-wider cursor-pointer"
                      >
                        <KeyRound size={14} /> Credentials & AI Key
                      </button>
                      <button 
                        onClick={() => handleDeleteTenant(tenant.id.toString())}
                        className="flex items-center gap-2 px-3.5 py-2 bg-rose-500/15 hover:bg-rose-500/30 text-rose-300 rounded-xl transition-all border border-rose-500/30 text-xs font-black uppercase tracking-wider"
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </div>

                  {activeTab === 'prompt' && (
                    <div className="flex flex-col relative z-10">
                      <h3 className="text-base font-black text-white flex items-center gap-2 mb-2">
                        <Zap className="text-amber-400" size={20} /> The Ultimate Luna AI Master Prompt
                      </h3>
                      <p className="text-xs text-slate-400 mb-4 font-medium">Copy and paste this prompt into the Luna AI configuration for <strong className="text-white">{tenant.name}</strong>. It includes their unique API key.</p>
                      
                      <div className="w-full admin-code-preview h-56 overflow-y-auto bg-black/90 p-4 rounded-2xl border border-white/10 mb-4 custom-scrollbar">
                        <pre className="text-emerald-400/90 font-mono text-xs whitespace-pre-wrap break-words leading-relaxed">
                          {getUltimatePrompt(tenant.apiKey)}
                        </pre>
                      </div>
                      
                      <CopyButton text={getUltimatePrompt(tenant.apiKey)} label="COPY ULTIMATE PROMPT" className="w-full text-xs py-4" />
                      
                      <div className="mt-6 p-5 bg-cyan-500/10 border border-cyan-500/25 rounded-2xl">
                        <h4 className="text-cyan-400 font-bold text-sm mb-2 flex items-center gap-2"><Server size={16} /> Backend Database Sync</h4>
                        <p className="text-xs text-slate-300 mb-2">To sync the database schema, the external website must implement a GET endpoint at:</p>
                        <code className="block bg-black/80 p-3 rounded-xl text-cyan-300 mb-3 font-mono text-xs overflow-x-auto border border-cyan-500/20">
                          {tenant.url ? `${tenant.url.replace(/\/$/, '')}/api/kangavena/schema` : 'https://their-website.com/api/kangavena/schema'}
                        </code>
                        <p className="text-xs text-slate-300">It must validate the <code className="text-purple-400 font-bold">Authorization: Bearer {maskKey(tenant.apiKey, tenant.id.toString())}</code> header and return a JSON object mapping table names to arrays of column names.</p>
                      </div>
                    </div>
                  )}

                  {activeTab === 'conversations' && (
                    <div className="flex flex-col relative z-10 gap-4">
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 bg-slate-900/80 p-4 rounded-2xl border border-cyan-500/20">
                        <div>
                          <h3 className="text-base font-black text-white flex items-center gap-2">
                            <MessageSquare className="text-cyan-400" size={18} />
                            <span>Visitor Chat History & Sessions</span>
                            <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                              {tenantConversations.length} Sessions Logged
                            </span>
                          </h3>
                          <p className="text-xs text-slate-400 mt-0.5">
                            Real-time turn-by-turn chat history, voice queries, spoken responses, and token metrics from Website Co-Pilot, WhatsApp, and Voice calls.
                          </p>
                        </div>

                        <div className="flex items-center gap-2 w-full md:w-auto">
                          {/* Channel Filter */}
                          <div className="flex bg-slate-950 p-1 rounded-xl border border-white/10 text-xs">
                            <button
                              onClick={() => setConversationChannelFilter('all')}
                              className={`px-2.5 py-1 rounded-lg font-bold transition-all ${conversationChannelFilter === 'all' ? 'bg-cyan-500 text-slate-950 shadow-md' : 'text-slate-400 hover:text-white'}`}
                            >
                              All
                            </button>
                            <button
                              onClick={() => setConversationChannelFilter('web_widget')}
                              className={`px-2.5 py-1 rounded-lg font-bold transition-all flex items-center gap-1 ${conversationChannelFilter === 'web_widget' ? 'bg-cyan-500 text-slate-950 shadow-md' : 'text-slate-400 hover:text-white'}`}
                            >
                              <Globe size={11} /> Website
                            </button>
                            <button
                              onClick={() => setConversationChannelFilter('whatsapp')}
                              className={`px-2.5 py-1 rounded-lg font-bold transition-all flex items-center gap-1 ${conversationChannelFilter === 'whatsapp' ? 'bg-emerald-500 text-slate-950 shadow-md' : 'text-slate-400 hover:text-white'}`}
                            >
                              <Smartphone size={11} /> WhatsApp
                            </button>
                            <button
                              onClick={() => setConversationChannelFilter('live_call')}
                              className={`px-2.5 py-1 rounded-lg font-bold transition-all flex items-center gap-1 ${conversationChannelFilter === 'live_call' ? 'bg-purple-500 text-slate-950 shadow-md' : 'text-slate-400 hover:text-white'}`}
                            >
                              <PhoneCall size={11} /> Voice
                            </button>
                          </div>

                          {/* Search */}
                          <div className="relative">
                            <input
                              type="text"
                              value={conversationSearch}
                              onChange={(e) => setConversationSearch(e.target.value)}
                              placeholder="Search phone, session..."
                              className="w-36 md:w-44 px-2.5 py-1.5 pl-7 rounded-xl bg-slate-950 border border-white/10 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
                            />
                            <Search size={12} className="absolute left-2.5 top-2.5 text-slate-500 pointer-events-none" />
                          </div>

                          <button
                            onClick={() => fetchTenantConversations(tenant.id.toString(), conversationChannelFilter, conversationSearch)}
                            disabled={loadingConversations}
                            className="p-1.5 bg-slate-950 hover:bg-slate-800 text-slate-300 rounded-xl border border-white/10 transition-all cursor-pointer disabled:opacity-50"
                            title="Refresh Conversations"
                          >
                            <RefreshCw size={14} className={loadingConversations ? 'animate-spin text-cyan-400' : ''} />
                          </button>
                        </div>
                      </div>

                      {/* Explorer Grid */}
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-[460px]">
                        {/* Left List: Sessions */}
                        <div className="lg:col-span-4 bg-black/70 border border-white/10 rounded-2xl p-3 flex flex-col h-[520px] overflow-hidden">
                          <div className="text-[11px] font-mono font-bold text-slate-400 uppercase tracking-wider mb-2 px-1 flex justify-between items-center">
                            <span>Recorded Sessions</span>
                            <span>{tenantConversations.length}</span>
                          </div>

                          {loadingConversations ? (
                            <div className="flex-1 flex flex-col items-center justify-center gap-2 text-slate-400">
                              <Loader2 size={24} className="animate-spin text-cyan-400" />
                              <span className="text-xs font-mono">Loading sessions...</span>
                            </div>
                          ) : tenantConversations.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-center p-4 text-slate-500">
                              <MessageSquare size={32} className="mb-2 opacity-30 text-cyan-400" />
                              <p className="text-xs font-medium">No recorded conversations found for this filter.</p>
                              <span className="text-[10px] mt-1 text-slate-600">Visitor queries on website or WhatsApp will appear here automatically.</span>
                            </div>
                          ) : (
                            <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                              {tenantConversations.map((conv) => {
                                const isSelected = selectedConversation?.sessionId === conv.sessionId;
                                const isWeb = conv.channel === 'web_widget' || conv.channel === 'widget' || conv.channel === 'public_web';
                                const isWa = conv.channel === 'whatsapp';
                                const msgCount = Array.isArray(conv.messages) ? conv.messages.length : 0;
                                const lastActiveStr = conv.lastActiveAt ? new Date(conv.lastActiveAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Recent';

                                return (
                                  <div
                                    key={conv.sessionId || conv.id}
                                    onClick={() => setSelectedConversation(conv)}
                                    className={`p-3 rounded-xl border transition-all cursor-pointer text-left ${
                                      isSelected
                                        ? 'bg-cyan-950/40 border-cyan-400/80 shadow-[0_0_15px_rgba(6,182,212,0.25)]'
                                        : 'bg-slate-900/50 hover:bg-slate-900 border-white/5 hover:border-white/20'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between gap-1 mb-1">
                                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase font-mono tracking-wider flex items-center gap-1 ${
                                        isWeb ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' : isWa ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                                      }`}>
                                        {isWeb ? <Globe size={9} /> : isWa ? <Smartphone size={9} /> : <PhoneCall size={9} />}
                                        {isWeb ? 'Website Co-Pilot' : isWa ? 'WhatsApp' : 'Live Call'}
                                      </span>
                                      <span className="text-[10px] text-slate-500 font-mono">{lastActiveStr}</span>
                                    </div>

                                    <div className="text-xs font-bold text-white truncate mb-1">
                                      {conv.visitorName || (conv.phone ? `+${conv.phone}` : 'Web Visitor')}
                                    </div>

                                    <div className="flex items-center justify-between text-[10px] text-slate-400 font-mono">
                                      <span className="truncate max-w-[130px] opacity-70">
                                        ID: {conv.sessionId?.slice(0, 14)}...
                                      </span>
                                      <div className="flex items-center gap-2">
                                        <span className="text-cyan-400 font-bold">{msgCount} turns</span>
                                        <span>•</span>
                                        <span>{Number(conv.totalTokens || 0).toLocaleString()} tok</span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Right Detail: Full Transcript */}
                        <div className="lg:col-span-8 bg-black/80 border border-white/10 rounded-2xl p-4 flex flex-col h-[520px] overflow-hidden">
                          {selectedConversation ? (
                            <>
                              {/* Selected Header */}
                              <div className="flex items-center justify-between pb-3 mb-3 border-b border-white/10">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-xl bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                                    {selectedConversation.channel === 'whatsapp' ? <Smartphone size={16} /> : <Globe size={16} />}
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <h4 className="text-xs font-bold text-white">
                                        {selectedConversation.visitorName || (selectedConversation.phone ? `+${selectedConversation.phone}` : 'Website Visitor')}
                                      </h4>
                                      <span className="px-2 py-0.5 rounded text-[9px] font-mono uppercase bg-white/5 border border-white/10 text-slate-300">
                                        {selectedConversation.channel}
                                      </span>
                                    </div>
                                    <p className="text-[10px] text-slate-500 font-mono truncate max-w-[280px]">
                                      Session: {selectedConversation.sessionId}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center gap-3">
                                  <div className="text-right">
                                    <span className="text-[10px] text-slate-400 block font-mono">Tokens Used</span>
                                    <span className="text-xs font-bold text-emerald-400 font-mono">
                                      {Number(selectedConversation.totalTokens || 0).toLocaleString()}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => handleDeleteConversation(tenant.id.toString(), selectedConversation.sessionId)}
                                    className="p-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 rounded-xl border border-rose-500/20 transition-all cursor-pointer"
                                    title="Delete this conversation log"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              </div>

                              {/* Message Bubbles Container */}
                              <div className="flex-1 overflow-y-auto space-y-3 pr-2 custom-scrollbar">
                                {Array.isArray(selectedConversation.messages) && selectedConversation.messages.length > 0 ? (
                                  selectedConversation.messages.map((m: any, idx: number) => {
                                    const isUser = m.role === 'user' || m.sender === 'visitor' || m.sender === 'customer';
                                    const text = m.text || m.content || m.promptPreview || m.responsePreview || '';
                                    const time = m.time || (m.timestamp ? new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');
                                    const tokens = m.tokens?.totalTokens || m.tokensUsed;

                                    return (
                                      <div
                                        key={idx}
                                        className={`flex flex-col ${isUser ? 'items-start' : 'items-end'}`}
                                      >
                                        <div className="flex items-center gap-1.5 mb-1 px-1 text-[10px] text-slate-500 font-mono">
                                          <span>{isUser ? '👤 Visitor' : '✨ Luna AI'}</span>
                                          {time && <span>• {time}</span>}
                                          {tokens && <span className="text-emerald-400/80">({tokens} tokens)</span>}
                                        </div>

                                        <div
                                          className={`max-w-[85%] p-3.5 rounded-2xl text-xs leading-relaxed ${
                                            isUser
                                              ? 'bg-slate-900 border border-white/10 text-slate-200 rounded-tl-sm'
                                              : 'bg-cyan-950/40 border border-cyan-500/30 text-cyan-100 rounded-tr-sm shadow-[0_0_20px_rgba(6,182,212,0.15)]'
                                          }`}
                                        >
                                          {text}
                                        </div>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div className="flex-1 h-full flex flex-col items-center justify-center text-center p-6 text-slate-500">
                                    <p className="text-xs">No individual message turns recorded in this session log.</p>
                                    <span className="text-[10px] text-slate-600 mt-1">Session telemetry was captured with {selectedConversation.totalTokens || 0} tokens.</span>
                                  </div>
                                )}
                              </div>
                            </>
                          ) : (
                            <div className="flex-1 flex flex-col items-center justify-center text-center p-6 text-slate-500">
                              <MessageSquare size={40} className="mb-2 opacity-25 text-cyan-400" />
                              <h4 className="text-xs font-bold text-slate-300">Select a Conversation Session</h4>
                              <p className="text-[11px] text-slate-500 mt-1 max-w-sm">
                                Click on any visitor session on the left to inspect the complete turn-by-turn chat messages, prompt inputs, and spoken answers.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'schema' && (
                    <div className="flex flex-col relative z-10">
                      <h3 className="text-base font-black text-white flex items-center gap-2 mb-2">
                        <Server className="text-cyan-400" size={20} /> Connected Database Tables
                      </h3>
                      <p className="text-xs text-slate-400 mb-4 font-medium">These are the tables synced from <strong className="text-white">{tenant.name}</strong>'s database.</p>
                      
                      {tenant.schema ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {Object.entries(tenant.schema).map(([tableName, columns]: [string, string[]]) => (
                            <div key={tableName} className="bg-black/70 border border-white/10 rounded-2xl p-4">
                              <h4 className="text-purple-400 font-bold text-xs mb-3 uppercase tracking-wider flex items-center gap-2 font-mono">
                                <Database size={16} /> {tableName}
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {columns.map(col => (
                                  <span key={col} className="px-2.5 py-1 bg-white/5 rounded-lg text-xs font-mono text-slate-300 border border-white/10">
                                    {col}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 bg-black/60 rounded-2xl border border-white/10">
                          <Database className="text-slate-600 mx-auto mb-4" size={44} />
                          <p className="text-slate-400 text-sm font-bold">No schema synced yet.</p>
                          <p className="text-xs text-slate-500 mt-1.5">Click "Check Connection" to simulate a sync from the external site.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'scraped' && (
                    <div className="flex flex-col relative z-10">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-base font-black text-white flex items-center gap-2">
                          <Globe className="text-cyan-400" size={20} /> Public Website Context
                        </h3>
                        <button 
                          onClick={() => handleManualSync(tenant.id.toString())}
                          disabled={isSyncing === tenant.id.toString()}
                          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-xl transition-all font-black text-xs uppercase tracking-wider shadow-md disabled:opacity-50"
                        >
                          <RefreshCw size={14} className={isSyncing === tenant.id.toString() ? 'animate-spin' : ''} />
                          {isSyncing === tenant.id.toString() ? 'Scraping...' : '🔄 Sync Website Data Now'}
                        </button>
                      </div>
                      
                      <p className="text-xs text-slate-400 mb-4 font-medium">This is the public information Luna has retrieved from <strong className="text-white">{tenant.url}</strong> to use as context.</p>
                      
                      <div className="w-full h-64 overflow-y-auto bg-black/90 p-4 rounded-2xl border border-white/10 custom-scrollbar font-mono text-xs">
                        {tenant.scrapedContent ? (
                          <p className="text-slate-300 text-xs leading-relaxed whitespace-pre-wrap">
                            {tenant.scrapedContent}
                          </p>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full text-slate-500">
                            <Search size={32} className="mb-2 opacity-20" />
                            <p>No data scraped yet. Click Sync.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === 'logs' && (
                    <div className="flex flex-col relative z-10">
                      <h3 className="text-base font-black text-white flex items-center gap-2 mb-2">
                        <Activity className="text-emerald-400" size={20} /> Dynamic Data Pipeline
                      </h3>
                      <p className="text-xs text-slate-400 mb-4 font-medium">Incoming JSON payloads from <strong className="text-white">{tenant.name}</strong> will appear here. No strict DB schemas required.</p>
                      
                      <div className="w-full bg-black/90 p-5 rounded-2xl border border-white/10 font-mono text-xs space-y-3">
                        <div className="flex gap-4">
                          <span className="text-slate-500">[{new Date().toISOString()}]</span>
                          <span className="text-cyan-400 font-bold">INFO</span>
                          <span className="text-white">Connection established</span>
                        </div>
                        <div className="flex gap-4">
                          <span className="text-slate-500">[{new Date().toISOString()}]</span>
                          <span className="text-purple-400 font-bold">PAYLOAD</span>
                          <span className="text-emerald-300">{`{"event": "new_course", "course_name": "Caregiving", "tenant_id": "${tenant.apiKey}"}`}</span>
                        </div>
                        <div className="flex gap-4">
                          <span className="text-slate-500">[{new Date().toISOString()}]</span>
                          <span className="text-purple-400 font-bold">PAYLOAD</span>
                          <span className="text-emerald-300">{`{"event": "user_interaction", "action": "voice_query", "duration_ms": 1450}`}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'ai-settings' && (
                    <div className="flex flex-col relative z-10 gap-6">
                      <h3 className="text-base font-black text-white flex items-center gap-2">
                        ⚙ AI Persona & Model Settings
                      </h3>
                      <p className="text-xs text-slate-400 font-medium">Control AI model architecture, token pricing tier, and persona behavior for <strong className="text-white">{tenant.name}</strong>.</p>

                      {/* Dedicated AI Model & Pricing Assignment Card */}
                      <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-cyan-950/20 border-2 border-cyan-500/40 rounded-2xl p-6 shadow-xl relative overflow-hidden">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-white/10">
                          <div>
                            <div className="flex items-center gap-2">
                              <Bot className="w-5 h-5 text-cyan-400" />
                              <h4 className="text-sm font-black text-white uppercase tracking-wider font-mono">
                                Dedicated AI Model & Token Pricing Assignment
                              </h4>
                              {tenant.assignedModel ? (
                                <span className="px-2.5 py-0.5 rounded-full bg-cyan-500/20 border border-cyan-500/50 text-cyan-300 font-mono text-[10px] font-bold">
                                  CUSTOM ASSIGNED
                                </span>
                              ) : (
                                <span className="px-2.5 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-400 font-mono text-[10px] font-bold">
                                  INHERITING GLOBAL (GEMINI 3.1 LIVE)
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-400 mt-1">
                              Assign an isolated AI model to this tenant. Token consumption and monthly invoices will be calculated using this model's specific pricing tier.
                            </p>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              type="button"
                              onClick={() => setShowPricingMatrixModal(true)}
                              className="px-3 py-1.5 rounded-xl bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 text-xs font-mono font-bold transition flex items-center gap-1.5 cursor-pointer"
                            >
                              <DollarSign size={13} />
                              <span>View Pricing Matrix</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setActiveTab('token-billing');
                                fetchTenantBilling(tenant.id.toString(), billingMonth, billingLkrRate);
                              }}
                              className="px-3 py-1.5 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-300 text-xs font-mono font-bold transition flex items-center gap-1.5 cursor-pointer"
                            >
                              <Receipt size={13} />
                              <span>View Token Billing</span>
                            </button>
                          </div>
                        </div>

                        {/* Model Dropdown & Summary */}
                        <div className="mt-4 space-y-4">
                          <div>
                            <label className="block text-[11px] font-black text-cyan-300 mb-1.5 uppercase tracking-wider font-mono">
                              Select Assigned AI Model
                            </label>
                            <select
                              id={`assigned-model-select-${tenant.id}`}
                              defaultValue={tenant.assignedModel || ''}
                              className="w-full bg-slate-950 border border-cyan-500/40 rounded-xl px-4 py-2.5 text-xs text-white font-mono outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition"
                            >
                              <option value="">
                                🌐 Global Sovereign Default: Gemini 3.1 Live (Input $0.75 / Cached $0.075 / Output $3.00 per 1M)
                              </option>
                              <optgroup label="── Google Gemini Models ──">
                                {getAllModelsCatalog()
                                  .filter(m => m.provider === 'Google Gemini')
                                  .map(m => (
                                    <option key={m.id} value={m.id}>
                                      {m.displayName} — Input: ${m.inputPricePerM} | Cached: ${m.cachedInputPricePerM} | Output: ${m.outputPricePerM} / 1M
                                    </option>
                                  ))}
                              </optgroup>
                              <optgroup label="── Xiaomi MiMo Models ──">
                                {getAllModelsCatalog()
                                  .filter(m => m.provider === 'Xiaomi MiMo')
                                  .map(m => (
                                    <option key={m.id} value={m.id}>
                                      {m.displayName} — Input: ${m.inputPricePerM} | Cached: ${m.cachedInputPricePerM} | Output: ${m.outputPricePerM} / 1M
                                    </option>
                                  ))}
                              </optgroup>
                            </select>
                          </div>

                          {/* Quick Rates Reference Display */}
                          {(() => {
                            const curModel = getModelPricing(tenant.assignedModel);
                            return (
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-black/50 p-3.5 rounded-xl border border-white/5 font-mono text-[11px]">
                                <div className="space-y-0.5">
                                  <span className="text-slate-400 text-[10px] uppercase">Active Model</span>
                                  <p className="font-bold text-cyan-300 truncate" title={curModel.displayName}>{curModel.displayName}</p>
                                </div>
                                <div className="space-y-0.5">
                                  <span className="text-slate-400 text-[10px] uppercase">Input / Cached (1M)</span>
                                  <p className="font-bold text-emerald-400">
                                    ${curModel.inputPricePerM} <span className="text-[10px] text-emerald-600">(${curModel.cachedInputPricePerM} cached)</span>
                                  </p>
                                </div>
                                <div className="space-y-0.5">
                                  <span className="text-slate-400 text-[10px] uppercase">Output Tokens (1M)</span>
                                  <p className="font-bold text-amber-300">${curModel.outputPricePerM}</p>
                                </div>
                                <div className="space-y-0.5">
                                  <span className="text-slate-400 text-[10px] uppercase">Live Voice Audio (1M)</span>
                                  <p className="font-bold text-purple-300">
                                    {curModel.audioInputPricePerM ? `$${curModel.audioInputPricePerM} in / $${curModel.audioOutputPricePerM} out` : 'Standard TTS/STT'}
                                  </p>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Save Model Button */}
                          <div className="flex justify-end pt-1">
                            <button
                              type="button"
                              onClick={() => {
                                const sel = document.getElementById(`assigned-model-select-${tenant.id}`) as HTMLSelectElement;
                                const val = sel?.value ? sel.value.trim() : null;
                                handleSaveTenantSettings(tenant.id.toString(), { assignedModel: val });
                              }}
                              disabled={savingSettingsId === tenant.id.toString()}
                              className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl transition shadow-lg shadow-cyan-500/20 flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                            >
                              {savingSettingsId === tenant.id.toString() ? (
                                <><RefreshCw size={13} className="animate-spin" /> Saving Model...</>
                              ) : (
                                <><Bot size={14} /> Save Model Assignment</>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Branding Toggle */}
                      <div className="bg-black/70 border border-white/10 rounded-2xl p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-white font-bold text-sm">Kangavena Branding</h4>
                            <p className="text-xs text-slate-400 mt-1 font-medium">
                              {tenant.showKangavenaBranding !== false
                                ? '🟢 FREE TIER — Luna identifies as "Luna by Kangavena.com" on this website.'
                                : '🔵 PAID TIER — White-labeled. Luna has total amnesia about Kangavena.'}
                            </p>
                          </div>
                          <button
                            onClick={() => handleSaveTenantSettings(tenant.id.toString(), { showKangavenaBranding: tenant.showKangavenaBranding === false ? true : false })}
                            className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${tenant.showKangavenaBranding !== false ? 'bg-cyan-500' : 'bg-purple-600'}`}
                          >
                            <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-300 ${tenant.showKangavenaBranding !== false ? '' : 'translate-x-7'}`} />
                          </button>
                        </div>
                      </div>

                      {/* White-Label Custom Agent Renaming Toggle */}
                      <div className="bg-black/70 border border-amber-500/30 rounded-2xl p-5 bg-gradient-to-r from-amber-500/10 to-transparent">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-amber-300 font-bold text-sm flex items-center gap-1.5">
                              <Crown size={15} className="text-amber-400" /> Premium White-Label & Custom Agent Renaming
                            </h4>
                            <p className="text-xs text-slate-400 mt-1 font-medium">
                              When enabled, tenant can rename Luna to their brand name in Luna Core Hub.
                            </p>
                          </div>
                          <button
                            onClick={() => handleSaveTenantSettings(tenant.id.toString(), { isWhiteLabelEnabled: !tenant.isWhiteLabelEnabled })}
                            className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${tenant.isWhiteLabelEnabled ? 'bg-amber-500' : 'bg-slate-700'}`}
                          >
                            <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-300 ${tenant.isWhiteLabelEnabled ? 'translate-x-7' : ''}`} />
                          </button>
                        </div>

                        {/* Bespoke 3D Avatar Inputs */}
                        <div className="mt-4 pt-4 border-t border-amber-500/20 grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1 uppercase tracking-wider">Custom 3D Avatar Name</label>
                            <input
                              id={`custom-avatar-name-${tenant.id}`}
                              defaultValue={tenant.customAvatarName || ''}
                              placeholder="e.g. Robot Mascot"
                              className="w-full bg-slate-900 border border-cyan-500/30 rounded-xl px-3 py-2 text-xs text-white outline-none font-mono"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1 uppercase tracking-wider">Custom 3D Model (.glb URL)</label>
                            <input
                              id={`custom-avatar-url-${tenant.id}`}
                              defaultValue={tenant.customAvatarUrl || ''}
                              placeholder="https://.../model.glb"
                              className="w-full bg-slate-900 border border-cyan-500/30 rounded-xl px-3 py-2 text-xs text-cyan-300 outline-none font-mono"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-[10px] font-bold text-amber-300 mb-1 uppercase tracking-wider flex items-center justify-between">
                              <span>👑 Enterprise Developer & Platform Attribution Rebrand</span>
                              <span className="text-[9px] text-slate-400 font-normal">High-Tier Paid Rebrand</span>
                            </label>
                            <input
                              id={`custom-dev-name-${tenant.id}`}
                              defaultValue={tenant.customDeveloperName || ''}
                              placeholder="e.g. DTech Technologies & Robotics (leave empty for default Kangavena attribution)"
                              className="w-full bg-slate-900 border border-amber-500/30 rounded-xl px-3 py-2 text-xs text-amber-200 outline-none font-mono"
                            />
                            <p className="text-[10px] text-slate-400 mt-1">
                              If empty, AI creator attribution is enforced as: <strong className="text-slate-300">Created by Kangavena Cloud Platform (කඟවේනා - kangavena.com)</strong>.
                            </p>
                          </div>
                          <div className="md:col-span-2">
                            <button
                              onClick={() => {
                                const nameEl = document.getElementById(`custom-avatar-name-${tenant.id}`) as HTMLInputElement;
                                const urlEl = document.getElementById(`custom-avatar-url-${tenant.id}`) as HTMLInputElement;
                                const devEl = document.getElementById(`custom-dev-name-${tenant.id}`) as HTMLInputElement;
                                handleSaveTenantSettings(tenant.id.toString(), {
                                  customAvatarName: nameEl?.value || '',
                                  customAvatarUrl: urlEl?.value || '',
                                  customDeveloperName: devEl?.value || '',
                                  isWhiteLabelEnabled: true,
                                });
                              }}
                              className="px-4 py-2 bg-gradient-to-r from-amber-500/20 to-cyan-500/20 hover:from-amber-500/30 hover:to-cyan-500/30 text-amber-300 border border-amber-500/40 rounded-xl text-xs font-bold uppercase tracking-wider transition"
                            >
                              Save Branding Settings & Unlock White-Label
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Custom Instructions */}
                      <div className="bg-black/70 border border-white/10 rounded-2xl p-5">
                        <h4 className="text-white font-bold text-sm mb-2">Custom System Instructions</h4>
                        <p className="text-xs text-slate-400 mb-3 font-medium">These rules are appended to Luna's AI prompt for this tenant. Example: "Speak formally", "Do not discuss pricing", "Always greet in Sinhala".</p>
                        <textarea
                          id={`custom-instructions-${tenant.id}`}
                          defaultValue={tenant.customInstructions || ''}
                          placeholder="e.g. Speak formally. Do not talk about prices. Always recommend visiting the campus in person."
                          className="w-full h-32 bg-black/90 border border-white/10 rounded-xl p-3.5 text-xs text-white placeholder-slate-600 outline-none focus:border-purple-500/50 resize-none font-mono"
                        />
                        <button
                          onClick={() => {
                            const el = document.getElementById(`custom-instructions-${tenant.id}`) as HTMLTextAreaElement;
                            if (el) handleSaveTenantSettings(tenant.id.toString(), { customInstructions: el.value });
                          }}
                          disabled={savingSettingsId === tenant.id.toString()}
                          className="mt-3.5 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-xl font-black text-xs uppercase tracking-wider transition-all shadow-md disabled:opacity-50"
                        >
                          {savingSettingsId === tenant.id.toString() ? 'Saving...' : '💾 Save Instructions'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* White-Label Inquiries Tab Content */}
                  {activeTab === 'white-label-requests' && (
                    <div className="flex flex-col relative z-10 gap-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-base font-black text-white flex items-center gap-2">
                            <Crown size={18} className="text-amber-400" /> White-Label & Bespoke 3D Avatar Inquiries
                          </h3>
                          <p className="text-xs text-slate-400 mt-1 font-medium">
                            Inquiries submitted by tenants from the Flow Builder modal. 1-Click approve to unlock custom branding.
                          </p>
                        </div>
                        <button
                          onClick={fetchWhiteLabelRequests}
                          disabled={loadingRequests}
                          className="p-2 bg-slate-900 border border-white/10 rounded-xl text-slate-300 hover:text-white hover:border-white/30 text-xs font-mono flex items-center gap-1.5 cursor-pointer"
                        >
                          <RefreshCw size={13} className={loadingRequests ? 'animate-spin text-amber-400' : ''} /> Refresh
                        </button>
                      </div>

                      {loadingRequests ? (
                        <div className="py-12 flex items-center justify-center">
                          <RefreshCw className="animate-spin text-amber-400" size={24} />
                        </div>
                      ) : whiteLabelRequests.length === 0 ? (
                        <div className="bg-slate-900/50 border border-dashed border-white/10 rounded-2xl p-8 text-center">
                          <Crown className="mx-auto text-slate-600 mb-2" size={32} />
                          <p className="text-xs text-slate-400 font-mono">No white-label or bespoke avatar requests submitted yet.</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {whiteLabelRequests.map((req) => {
                            const fd = req.formData || {};
                            const isApproved = req.status === 'approved';
                            const isRejected = req.status === 'rejected';
                            return (
                              <div
                                key={req.id}
                                className={`p-4 rounded-2xl border transition-all ${
                                  isApproved
                                    ? 'bg-emerald-950/20 border-emerald-500/40 shadow-emerald-950/20'
                                    : isRejected
                                    ? 'bg-rose-950/20 border-rose-500/30 opacity-60'
                                    : 'bg-gradient-to-r from-amber-500/10 via-slate-900 to-slate-900 border-amber-500/40 shadow-xl'
                                }`}
                              >
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-black text-amber-300 uppercase tracking-wider">
                                        {fd.requestType === 'agent_rebrand'
                                          ? '🏷️ Agent Rebrand'
                                          : fd.requestType === 'full_enterprise'
                                          ? '👑 Full Enterprise'
                                          : '🎨 Bespoke 3D Avatar'}
                                      </span>
                                      <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full ${
                                        isApproved
                                          ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                          : isRejected
                                          ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                                          : 'bg-amber-500/20 text-amber-300 border border-amber-500/40 animate-pulse'
                                      }`}>
                                        {req.status?.toUpperCase() || 'NEW'}
                                      </span>
                                    </div>
                                    <h4 className="text-sm font-bold text-white mt-1">
                                      Tenant: <span className="text-cyan-300 font-mono">{fd.tenantName || `Tenant #${req.tenantId}`}</span>
                                    </h4>
                                    {fd.desiredAgentName && (
                                      <p className="text-xs text-slate-300 mt-0.5">
                                        Desired Brand Name: <strong className="text-amber-300 font-mono">{fd.desiredAgentName}</strong>
                                      </p>
                                    )}
                                    {fd.avatarDescription && (
                                      <p className="text-xs text-slate-400 mt-1 bg-black/40 p-2 rounded-lg font-mono">
                                        "{fd.avatarDescription}"
                                      </p>
                                    )}
                                    {fd.referenceUrl && (
                                      <p className="text-xs text-cyan-400 mt-1 truncate">
                                        Asset / Logo URL: <a href={fd.referenceUrl} target="_blank" rel="noreferrer" className="underline">{fd.referenceUrl}</a>
                                      </p>
                                    )}
                                    {fd.contactPhone && (
                                      <p className="text-xs text-emerald-400 mt-1 font-mono">
                                        WhatsApp Contact: <strong>{fd.contactPhone}</strong>
                                      </p>
                                    )}
                                  </div>

                                  <div className="flex items-center gap-2 shrink-0">
                                    {!isApproved && (
                                      <button
                                        onClick={() => handleApproveWhiteLabel(req.id, fd.referenceUrl, fd.desiredAgentName)}
                                        disabled={approvingReqId === req.id}
                                        className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl transition shadow-lg flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                                      >
                                        <CheckCircle size={14} />
                                        {approvingReqId === req.id ? 'Approving...' : '✨ Approve & Unlock'}
                                      </button>
                                    )}
                                    {!isApproved && !isRejected && (
                                      <button
                                        onClick={() => handleRejectWhiteLabel(req.id)}
                                        className="px-3 py-2 bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/30 rounded-xl text-xs font-bold transition cursor-pointer"
                                      >
                                        Dismiss
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Token Billing & Invoices Tab */}
                  {activeTab === 'token-billing' && (
                    <div className="flex flex-col relative z-10 gap-6">
                      {/* Top Header & Month/Rate Filters */}
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-gradient-to-r from-emerald-950/40 via-slate-900 to-slate-950 p-5 rounded-2xl border border-emerald-500/30 shadow-xl">
                        <div>
                          <div className="flex items-center gap-2">
                            <Receipt className="w-5 h-5 text-emerald-400" />
                            <h3 className="text-base font-black text-white uppercase tracking-wider font-mono">
                              Tenant Token Consumption & Disaggregated Ledger
                            </h3>
                            <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-mono text-[10px] font-bold border border-emerald-500/40">
                              MONTHLY BILLING
                            </span>
                          </div>
                          <p className="text-xs text-slate-400 mt-1 font-medium">
                            Real-time disaggregated token accounting for <strong className="text-cyan-300">{tenant.name}</strong> based on their assigned AI model pricing.
                          </p>
                        </div>

                        {/* Controls */}
                        <div className="flex items-center gap-3 flex-wrap">
                          {/* Month Selector */}
                          <div className="flex items-center gap-1.5 bg-black/60 px-3 py-1.5 rounded-xl border border-white/10">
                            <Calendar size={13} className="text-cyan-400" />
                            <input
                              type="month"
                              value={billingMonth}
                              onChange={(e) => {
                                setBillingMonth(e.target.value);
                                fetchTenantBilling(tenant.id.toString(), e.target.value, billingLkrRate);
                              }}
                              className="bg-transparent text-xs text-white font-mono outline-none cursor-pointer"
                            />
                          </div>

                          {/* LKR Exchange Rate */}
                          <div className="flex items-center gap-1.5 bg-black/60 px-3 py-1.5 rounded-xl border border-white/10" title="Current USD to LKR Exchange Rate">
                            <span className="text-[11px] text-amber-400 font-bold font-mono">LKR/$:</span>
                            <input
                              type="number"
                              value={billingLkrRate}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 305;
                                setBillingLkrRate(val);
                                fetchTenantBilling(tenant.id.toString(), billingMonth, val);
                              }}
                              step="0.5"
                              className="w-16 bg-transparent text-xs text-white font-mono font-bold outline-none"
                            />
                          </div>

                          {/* Refresh Button */}
                          <button
                            type="button"
                            onClick={() => fetchTenantBilling(tenant.id.toString(), billingMonth, billingLkrRate)}
                            disabled={loadingBilling}
                            className="p-2 rounded-xl bg-slate-900 border border-white/10 text-slate-300 hover:text-white hover:border-white/30 transition cursor-pointer disabled:opacity-50"
                            title="Refresh billing data"
                          >
                            <RefreshCw size={14} className={loadingBilling ? 'animate-spin text-emerald-400' : ''} />
                          </button>

                          {/* Generate Invoice Button */}
                          <button
                            type="button"
                            onClick={() => {
                              if (billingReport?.invoice) {
                                setSelectedInvoiceModal(billingReport.invoice);
                              } else {
                                alert('Billing report is still loading. Please wait.');
                              }
                            }}
                            className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl transition shadow-lg shadow-emerald-500/20 flex items-center gap-1.5 cursor-pointer"
                          >
                            <FileText size={14} />
                            <span>Monthly Invoice</span>
                          </button>
                        </div>
                      </div>

                      {/* 📅 BILLING DATE & GRACE CYCLE GOVERNANCE (MASTER ADMIN ADJUSTMENT) */}
                      <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-indigo-950/40 border border-indigo-500/30 rounded-2xl p-5 shadow-xl flex flex-col xl:flex-row xl:items-center justify-between gap-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Calendar className="w-4 h-4 text-indigo-400" />
                            <h4 className="text-xs font-black text-white font-mono uppercase tracking-wider">
                              Billing Cycle & Next Due Date Governance
                            </h4>
                            <span className="px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 font-mono text-[9px] font-bold border border-indigo-500/40">
                              3-DAY SETUP GRACE + MONTHLY RENEWAL
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-300 font-mono flex-wrap">
                            <span>Account Created: <strong className="text-white">{tenant.createdAt ? new Date(tenant.createdAt).toLocaleDateString() : 'N/A'}</strong></span>
                            <span>•</span>
                            <span>Cycle Start (Created + 3d): <strong className="text-cyan-300">{tenant.billingCycleStartDate || '3 days post creation'}</strong></span>
                            <span>•</span>
                            <span>Next Monthly Due Date: <strong className="text-amber-400">{tenant.subscriptionNextDueDate || tenant.monthlyDueDate || 'Pending'}</strong></span>
                            <span>•</span>
                            <span>Monthly Maintenance Fee: <strong className="text-emerald-400 font-bold">Rs. 2,500 LKR ($8.30 USD)</strong></span>
                          </div>
                        </div>

                        {/* Master Admin Adjust Due Date Form */}
                        <div className="flex items-center gap-2 bg-black/60 p-2 rounded-xl border border-indigo-500/30 shrink-0">
                          <label className="text-[10px] text-indigo-300 font-mono font-bold uppercase tracking-wider">
                            Adjust Due Date:
                          </label>
                          <input
                            type="date"
                            defaultValue={tenant.subscriptionNextDueDate || tenant.monthlyDueDate || ''}
                            id={`due-date-input-${tenant.id}`}
                            className="bg-slate-900 border border-indigo-500/40 rounded-lg px-2.5 py-1 text-xs text-indigo-200 font-mono font-bold outline-none cursor-pointer focus:ring-1 focus:ring-indigo-400"
                          />
                          <button
                            type="button"
                            onClick={async () => {
                              const input = document.getElementById(`due-date-input-${tenant.id}`) as HTMLInputElement;
                              if (!input || !input.value) {
                                alert('Please select a valid date');
                                return;
                              }
                              try {
                                const res = await fetch(`/api/admin/tenants/${tenant.id}/adjust-billing-date`, {
                                  method: 'POST',
                                  headers: authHeader(),
                                  body: JSON.stringify({ nextDueDate: input.value })
                                });
                                const d = await res.json();
                                if (d.success) {
                                  alert(`✅ Billing due date successfully adjusted to ${input.value} for ${tenant.name}`);
                                  setTenants((prev: any[]) => prev.map((t: any) => t.id.toString() === tenant.id.toString() ? { ...t, ...d.tenant } : t));
                                } else {
                                  alert('Failed: ' + (d.error || 'Server error'));
                                }
                              } catch (err: any) {
                                alert('Error: ' + err.message);
                              }
                            }}
                            className="px-3 py-1 bg-indigo-600 hover:bg-indigo-500 text-white font-mono font-bold text-xs rounded-lg transition shadow cursor-pointer active:scale-95"
                          >
                            Save Due Date
                          </button>
                        </div>
                      </div>

                      {/* Loading State */}
                      {loadingBilling && !billingReport ? (
                        <div className="py-16 flex flex-col items-center justify-center space-y-3">
                          <Loader2 className="animate-spin text-emerald-400" size={32} />
                          <p className="text-xs text-slate-400 font-mono">Calculating disaggregated token ledger & invoice...</p>
                        </div>
                      ) : (!billingReport || !billingReport.tokenUsage || !billingReport.amounts) ? (
                        <div className="bg-slate-900/50 border border-dashed border-white/10 rounded-2xl p-8 text-center">
                          <Receipt className="mx-auto text-slate-600 mb-2" size={32} />
                          <p className="text-xs text-slate-400 font-mono">
                            {loadingBilling ? 'Calculating disaggregated token ledger & invoice...' : 'No token usage recorded for this period.'}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-6">
                          {/* Active Pricing Model Banner & Inline Dedicated Model Selector */}
                          <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-cyan-950/40 border-2 border-cyan-500/40 rounded-2xl p-5 shadow-xl flex flex-col xl:flex-row xl:items-center justify-between gap-4">
                            <div className="flex items-start sm:items-center gap-3.5">
                              <div className="p-3 rounded-2xl bg-cyan-500/15 border border-cyan-500/40 text-cyan-400 shrink-0">
                                <Bot size={22} />
                              </div>
                              <div className="space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-black text-white font-mono">
                                    {billingReport.assignedModel?.displayName || 'Gemini 3.1 Live (Global Standard)'}
                                  </span>
                                  <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                                    {billingReport.assignedModel?.provider || 'Google Gemini'}
                                  </span>
                                  {tenant.assignedModel ? (
                                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-purple-500/20 text-purple-300 border border-purple-500/50">
                                      DEDICATED ASSIGNMENT
                                    </span>
                                  ) : (
                                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-slate-800 text-slate-400 border border-white/10">
                                      GLOBAL DEFAULT (GEMINI 3.1 LIVE)
                                    </span>
                                  )}
                                </div>
                                <p className="text-[11px] text-slate-400 font-mono">
                                  Fresh In: <strong className="text-emerald-400">${billingReport.assignedModel?.inputPricePerM ?? 0.75}/1M</strong> · 
                                  Cached In: <strong className="text-emerald-400">${billingReport.assignedModel?.cachedInputPricePerM ?? 0.075}/1M (-90%)</strong> · 
                                  Output: <strong className="text-amber-400">${billingReport.assignedModel?.outputPricePerM ?? 3.00}/1M</strong>
                                  {billingReport.assignedModel?.audioInputPricePerM ? ` · Live Audio: $${billingReport.assignedModel.audioInputPricePerM} in / $${billingReport.assignedModel.audioOutputPricePerM} out` : ''}
                                </p>
                              </div>
                            </div>

                            {/* Direct Model Selector Dropdown & Pricing Matrix Trigger */}
                            <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap bg-black/60 p-2.5 rounded-2xl border border-cyan-500/30 shrink-0">
                              <div className="space-y-1">
                                <label className="text-[10px] text-cyan-300 font-mono font-bold uppercase tracking-wider block">
                                  Assign Dedicated Model:
                                </label>
                                <select
                                  value={tenant.assignedModel || ''}
                                  onChange={async (e) => {
                                    const val = e.target.value ? e.target.value.trim() : null;
                                    await handleSaveTenantSettings(tenant.id.toString(), { assignedModel: val });
                                    fetchTenantBilling(tenant.id.toString(), billingMonth, billingLkrRate);
                                  }}
                                  disabled={savingSettingsId === tenant.id.toString()}
                                  className="bg-slate-900 border border-cyan-500/50 rounded-xl px-3 py-1.5 text-xs text-cyan-300 font-mono font-bold outline-none cursor-pointer focus:ring-1 focus:ring-cyan-400 transition"
                                >
                                  <option value="">
                                    🌐 Global Standard: Gemini 3.1 Live (Flagship)
                                  </option>
                                  <optgroup label="── Google Gemini Models ──">
                                    {getAllModelsCatalog()
                                      .filter(m => m.provider === 'Google Gemini')
                                      .map(m => (
                                        <option key={m.id} value={m.id}>
                                          {m.displayName} (${m.inputPricePerM} in / ${m.outputPricePerM} out)
                                        </option>
                                      ))}
                                  </optgroup>
                                  <optgroup label="── Xiaomi MiMo Models ──">
                                    {getAllModelsCatalog()
                                      .filter(m => m.provider === 'Xiaomi MiMo')
                                      .map(m => (
                                        <option key={m.id} value={m.id}>
                                          {m.displayName} (${m.inputPricePerM} in / ${m.outputPricePerM} out)
                                        </option>
                                      ))}
                                  </optgroup>
                                </select>
                              </div>

                              <button
                                type="button"
                                onClick={() => {
                                  setMatrixTargetTenantId(tenant.id.toString());
                                  setShowPricingMatrixModal(true);
                                }}
                                className="px-3.5 py-2.5 rounded-xl bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 text-xs font-mono font-bold transition flex items-center gap-1.5 cursor-pointer shrink-0 mt-auto"
                                title="Open full pricing matrix"
                              >
                                <DollarSign size={14} />
                                <span>Pricing Matrix</span>
                              </button>
                            </div>
                          </div>

                          {/* 🛡️ AUTONOMOUS MASTER AI BUDGET CAP & OVERAGE PROTECTION (Phase 1) */}
                          <div className="bg-slate-950/80 border border-rose-500/30 rounded-2xl p-4 shadow-lg space-y-3">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/5 pb-2.5">
                              <div className="flex items-center gap-2">
                                <div className="p-1.5 rounded-lg bg-rose-500/15 border border-rose-500/30 text-rose-400">
                                  <ShieldAlert size={16} />
                                </div>
                                <div>
                                  <h5 className="text-xs font-black text-white font-mono uppercase tracking-wider flex items-center gap-2">
                                    <span>Autonomous Master AI Budget Cap & Overage Protection</span>
                                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-rose-500/20 text-rose-300 border border-rose-500/40">
                                      WALLET GUARD
                                    </span>
                                  </h5>
                                  <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                                    Limits monthly platform fallback spend when tenant BYOK quota is depleted. Proactive 80% VIP WhatsApp alert to owner.
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                {billingReport?.masterAiBudget?.isExceeded && (
                                  <span className="px-2.5 py-1 rounded-lg bg-rose-500/20 border border-rose-500/50 text-rose-300 text-[10px] font-mono font-black animate-pulse flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-rose-400"></span>
                                    BUDGET CEILING REACHED (100%)
                                  </span>
                                )}
                                {billingReport?.masterAiBudget?.isWarning80 && !billingReport?.masterAiBudget?.isExceeded && (
                                  <span className="px-2.5 py-1 rounded-lg bg-amber-500/20 border border-amber-500/50 text-amber-300 text-[10px] font-mono font-bold flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                                    80% BUDGET THRESHOLD REACHED
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Spend Progress Bar & Controls */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
                              {/* Spend Cap Input */}
                              <div className="space-y-1">
                                <label className="text-[10px] text-slate-300 font-mono font-bold uppercase tracking-wider block">
                                  Monthly Spend Ceiling (LKR):
                                </label>
                                <div className="relative">
                                  <input
                                    type="number"
                                    defaultValue={tenant.customFields?.masterAiMonthlySpendCapLkr || 0}
                                    onBlur={async (e) => {
                                      const val = Math.max(0, Number(e.target.value) || 0);
                                      await handleSaveTenantSettings(tenant.id.toString(), {
                                        masterAiMonthlySpendCapLkr: val
                                      } as any);
                                      fetchTenantBilling(tenant.id.toString(), billingMonth, billingLkrRate);
                                    }}
                                    placeholder="0 for Unlimited"
                                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-1.5 text-xs text-white font-mono font-bold outline-none focus:border-rose-400 transition"
                                  />
                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-500 font-mono pointer-events-none">
                                    LKR / MO
                                  </span>
                                </div>
                                <p className="text-[9px] text-slate-500 font-mono">0 = Unlimited fallback allowed</p>
                              </div>

                              {/* Policy Selector */}
                              <div className="space-y-1">
                                <label className="text-[10px] text-slate-300 font-mono font-bold uppercase tracking-wider block">
                                  Ceiling Enforcement Policy:
                                </label>
                                <select
                                  value={tenant.customFields?.masterAiBudgetPolicy || 'pause'}
                                  onChange={async (e) => {
                                    const val = e.target.value;
                                    await handleSaveTenantSettings(tenant.id.toString(), {
                                      masterAiBudgetPolicy: val
                                    } as any);
                                    fetchTenantBilling(tenant.id.toString(), billingMonth, billingLkrRate);
                                  }}
                                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-1.5 text-xs text-rose-300 font-mono font-bold outline-none focus:border-rose-400 transition cursor-pointer"
                                >
                                  <option value="pause">🛑 Pause Fallback (Wallet Guard - Protect Admin)</option>
                                  <option value="throttle">📉 Throttle to Flash-Lite (Low-Cost Mode)</option>
                                  <option value="alert_only">⚠️ Alert Only (Never Disconnect)</option>
                                </select>
                                <p className="text-[9px] text-slate-500 font-mono">Auto-applies when 100% spend is reached</p>
                              </div>

                              {/* Live Spend Meter */}
                              <div className="space-y-1 bg-black/40 p-2.5 rounded-xl border border-white/5">
                                <div className="flex items-center justify-between text-[10px] font-mono">
                                  <span className="text-slate-400">Current Month Fallback:</span>
                                  <span className="font-bold text-white">
                                    Rs. {(billingReport?.masterAiBudget?.spentLkr ?? 0).toLocaleString()} / {billingReport?.masterAiBudget?.spendCapLkr ? `Rs. ${billingReport.masterAiBudget.spendCapLkr.toLocaleString()}` : '∞'}
                                  </span>
                                </div>
                                <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all duration-500 ${
                                      (billingReport?.masterAiBudget?.percentUsed ?? 0) >= 100
                                        ? 'bg-rose-500'
                                        : (billingReport?.masterAiBudget?.percentUsed ?? 0) >= 80
                                        ? 'bg-amber-400'
                                        : 'bg-emerald-400'
                                    }`}
                                    style={{ width: `${Math.min(100, billingReport?.masterAiBudget?.percentUsed ?? 0)}%` }}
                                  />
                                </div>
                                <div className="flex items-center justify-between text-[9px] font-mono text-slate-400">
                                  <span>{billingReport?.masterAiBudget?.percentUsed ?? 0}% consumed</span>
                                  <span>Remaining: Rs. {(billingReport?.masterAiBudget?.remainingLkr ?? 0).toLocaleString()}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* 💳 PREPAID AI WALLET & TOKEN PACK RECHARGE (Phase 6) */}
                          <div className="bg-slate-950/80 border border-emerald-500/30 rounded-2xl p-4 shadow-lg space-y-3">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/5 pb-2.5">
                              <div className="flex items-center gap-2">
                                <div className="p-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400">
                                  <Wallet size={16} />
                                </div>
                                <div>
                                  <h5 className="text-xs font-black text-white font-mono uppercase tracking-wider flex items-center gap-2">
                                    <span>Prepaid AI Token Wallet</span>
                                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                                      PREPAID BALANCE
                                    </span>
                                  </h5>
                                  <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                                    Prepaid reserves for Master AI Fallback. Automatically deducted on billable turns instead of invoice debt.
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const amountStr = prompt('Enter Top-Up Amount in LKR (e.g. 1500, 4500, 12000):', '1500');
                                    if (!amountStr || isNaN(Number(amountStr)) || Number(amountStr) <= 0) return;
                                    const res = await fetch(`/api/admin/tenants/${tenant.id}/prepaid-wallet/recharge`, {
                                      method: 'POST',
                                      headers: authHeader({ 'Content-Type': 'application/json' }),
                                      body: JSON.stringify({ amountLkr: Number(amountStr), paymentMethod: 'admin_grant', notes: 'Master Admin Manual Top-up' })
                                    });
                                    const data = await res.json();
                                    if (data.success) {
                                      alert(`✅ Successfully credited Rs. ${Number(amountStr).toLocaleString()} to Tenant ${tenant.name} wallet! New balance: Rs. ${data.newBalanceLkr?.toLocaleString()}`);
                                      fetchTenantBilling(tenant.id.toString(), billingMonth, billingLkrRate);
                                    } else {
                                      alert(`❌ Top-up failed: ${data.error}`);
                                    }
                                  }}
                                  className="px-3 py-1 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-300 text-[10px] font-mono font-bold transition flex items-center gap-1.5 cursor-pointer"
                                >
                                  <Plus size={11} className="text-emerald-400" />
                                  <span>+ Top Up Balance</span>
                                </button>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="bg-black/40 p-3 rounded-xl border border-white/5 flex items-center justify-between">
                                <span className="text-[11px] font-mono text-slate-400">Available Prepaid Balance:</span>
                                <span className="text-base font-black font-mono text-emerald-400">
                                  Rs. {((billingReport?.prepaidWallet?.balanceLkr ?? tenant.customFields?.prepaidBalanceLkr) || 0).toLocaleString()}
                                </span>
                              </div>
                              <div className="bg-black/40 p-3 rounded-xl border border-white/5 flex items-center justify-between">
                                <span className="text-[11px] font-mono text-slate-400">Total Lifetime Top-Ups:</span>
                                <span className="text-sm font-bold font-mono text-slate-200">
                                  Rs. {((billingReport?.prepaidWallet?.totalRechargedLkr ?? tenant.customFields?.totalRechargedLkr) || 0).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* 🌟 HIGH-AVAILABILITY MODEL POOL & FAILOVER CASCADE */}
                          <div className="bg-slate-950/80 border border-cyan-500/30 rounded-2xl p-4 shadow-lg space-y-3">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/5 pb-2.5">
                              <div className="flex items-center gap-2">
                                <div className="p-1.5 rounded-lg bg-cyan-500/15 border border-cyan-500/30 text-cyan-400">
                                  <Layers size={16} />
                                </div>
                                <div>
                                  <h5 className="text-xs font-black text-white font-mono uppercase tracking-wider flex items-center gap-2">
                                    <span>High-Availability Model Pool & Failover Cascade</span>
                                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">
                                      AUTO-FAILOVER ON 429 / LIMITS
                                    </span>
                                  </h5>
                                  <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                                    Order of priority on active key: Model 1 ➔ Model 2 ➔ ... ➔ Key 2 Rotation ➔ Platform Master Key
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                {/* ⚡ Auto-Sort by Speed (Phase 3) */}
                                <button
                                  type="button"
                                  onClick={async () => {
                                    await fetch(`/api/admin/tenants/${tenant.id}/sort-pool-by-speed`, {
                                      method: 'POST',
                                      headers: authHeader({ 'Content-Type': 'application/json' })
                                    });
                                    fetchTenantBilling(tenant.id.toString(), billingMonth, billingLkrRate);
                                  }}
                                  disabled={savingSettingsId === tenant.id.toString()}
                                  className="px-2.5 py-1 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 text-[10px] font-mono font-bold transition flex items-center gap-1 cursor-pointer"
                                  title="Benchmark real-time TTFT response latency and sort models fastest first"
                                >
                                  <Zap size={11} className="text-amber-400" />
                                  <span>Auto-Sort by Speed</span>
                                </button>
                                {/* 🔍 Probe Key Health (Phase 4) */}
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const res = await fetch(`/api/admin/tenants/${tenant.id}/keys/probe`, {
                                      method: 'POST',
                                      headers: authHeader({ 'Content-Type': 'application/json' })
                                    });
                                    const data = await res.json();
                                    alert(`Key Pool Diagnostics:\nTotal keys tested: ${data.totalKeys || 0}\n\n${(data.results || []).map((r: any) => `• ${r.label} (${r.maskedKey}): ${r.status.toUpperCase()} ${r.latencyMs ? `(${r.latencyMs}ms)` : ''} ${r.error ? `\n  Error: ${r.error}` : ''}`).join('\n')}`);
                                    fetchTenantBilling(tenant.id.toString(), billingMonth, billingLkrRate);
                                  }}
                                  disabled={savingSettingsId === tenant.id.toString()}
                                  className="px-2.5 py-1 rounded-lg bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 text-purple-300 text-[10px] font-mono font-bold transition flex items-center gap-1 cursor-pointer"
                                  title="Test live quota, authorization, and latency of all dedicated keys in this tenant's pool"
                                >
                                  <Activity size={11} className="text-purple-400" />
                                  <span>Probe Key Health</span>
                                </button>
                                {/* Quick Add All Gemini Models */}
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const allGemini = getAllModelsCatalog()
                                      .filter(m => m.provider === 'Google Gemini' && (m.id.startsWith('gemini-3') || m.id.includes('gemini-3')))
                                      .map(m => m.id);
                                    const currentPrimary = tenant.assignedModel || allGemini[0];
                                    const newPool = Array.from(new Set([currentPrimary, ...allGemini]));
                                    setTenants(prev => prev.map(t => t.id.toString() === tenant.id.toString() ? { ...t, geminiModelPool: newPool, assignedModel: currentPrimary } : t));
                                    await handleSaveTenantSettings(tenant.id.toString(), {
                                      assignedModel: currentPrimary,
                                      geminiModelPool: newPool
                                    }, true);
                                    fetchTenantBilling(tenant.id.toString(), billingMonth, billingLkrRate);
                                  }}
                                  disabled={savingSettingsId === tenant.id.toString()}
                                  className="px-2.5 py-1 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-[10px] font-mono font-bold transition flex items-center gap-1 cursor-pointer"
                                  title="Add all Google Gemini 3+ models to the failover pool"
                                >
                                  <Plus size={12} />
                                  <span>Add All Gemini Models</span>
                                </button>
                                {/* Clear All Cooldowns */}
                                <button
                                  type="button"
                                  onClick={async () => {
                                    await fetch('/api/admin/circuit-breaker/reset', {
                                      method: 'POST',
                                      headers: authHeader({ 'Content-Type': 'application/json' }),
                                      body: JSON.stringify({})
                                    });
                                    fetchTenantBilling(tenant.id.toString(), billingMonth, billingLkrRate);
                                  }}
                                  disabled={savingSettingsId === tenant.id.toString()}
                                  className="px-2.5 py-1 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono font-bold transition flex items-center gap-1 cursor-pointer"
                                  title="Clear all in-memory circuit breaker cooldowns immediately"
                                >
                                  <RefreshCw size={11} />
                                  <span>Clear All Cooldowns</span>
                                </button>
                                {/* Reset to Primary Only */}
                                {(tenant.geminiModelPool && tenant.geminiModelPool.length > 1) && (
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      const primaryOnly = tenant.assignedModel ? [tenant.assignedModel] : ['gemini-3.1-flash-live-preview'];
                                      setTenants(prev => prev.map(t => t.id.toString() === tenant.id.toString() ? { ...t, geminiModelPool: primaryOnly } : t));
                                      await handleSaveTenantSettings(tenant.id.toString(), {
                                        geminiModelPool: primaryOnly
                                      }, true);
                                      fetchTenantBilling(tenant.id.toString(), billingMonth, billingLkrRate);
                                    }}
                                    disabled={savingSettingsId === tenant.id.toString()}
                                    className="px-2.5 py-1 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-300 text-[10px] font-mono font-bold transition cursor-pointer"
                                    title="Reset pool to primary model only"
                                  >
                                    Reset Pool
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Active Cascade Pills */}
                            <div className="flex items-center gap-2 flex-wrap">
                              {(() => {
                                const activePool = Array.isArray(tenant.geminiModelPool) && tenant.geminiModelPool.length > 0
                                  ? tenant.geminiModelPool
                                  : (tenant.assignedModel ? [tenant.assignedModel] : ['gemini-3.1-flash-live-preview']);
                                
                                return activePool.map((modelId: string, idx: number) => {
                                  const info = getModelPricing(modelId);
                                  const isPrimary = idx === 0;
                                  const cb = billingReport?.circuitBreakerStatus?.find((c: any) => c.modelId === modelId);
                                  const isCooldown = cb && !cb.isAvailable && cb.cooldownRemainingSeconds > 0;

                                  return (
                                    <div
                                      key={modelId}
                                      className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-mono font-bold transition ${
                                        isCooldown
                                          ? 'bg-amber-950/40 border-amber-500/60 text-amber-200 shadow-[0_0_12px_rgba(245,158,11,0.25)]'
                                          : isPrimary
                                          ? 'bg-cyan-500/15 border-cyan-500/60 text-cyan-200 shadow-[0_0_12px_rgba(6,182,212,0.2)]'
                                          : 'bg-slate-900 border-slate-700/80 text-slate-300 hover:border-slate-500'
                                      }`}
                                    >
                                      <div className="flex items-center gap-1.5">
                                        <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-black ${
                                          isCooldown
                                            ? 'bg-amber-400 text-slate-950'
                                            : isPrimary
                                            ? 'bg-cyan-400 text-slate-950'
                                            : 'bg-slate-800 text-slate-400 border border-slate-700'
                                        }`}>
                                          {idx + 1}
                                        </span>
                                        <span>{info.displayName}</span>
                                        <span className="text-[10px] text-slate-400 font-normal">
                                          (${info.inputPricePerM}/${info.outputPricePerM})
                                        </span>
                                        {isPrimary && (
                                          <span className="text-[8px] uppercase tracking-wider px-1.5 py-0.2 rounded bg-cyan-400/20 text-cyan-300 border border-cyan-400/30">
                                            Primary
                                          </span>
                                        )}
                                        {isCooldown ? (
                                          <div className="flex items-center gap-1 px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[8.5px]">
                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-ping"></span>
                                            <span>COOLDOWN ({cb.cooldownRemainingSeconds}s)</span>
                                            <button
                                              type="button"
                                              onClick={async (e) => {
                                                e.stopPropagation();
                                                await fetch('/api/admin/circuit-breaker/reset', {
                                                  method: 'POST',
                                                  headers: authHeader({ 'Content-Type': 'application/json' }),
                                                  body: JSON.stringify({ model: modelId })
                                                });
                                                fetchTenantBilling(tenant.id.toString(), billingMonth, billingLkrRate);
                                              }}
                                              className="ml-1 underline text-amber-200 hover:text-white"
                                              title="Clear rate-limit cooldown now"
                                            >
                                              Reset
                                            </button>
                                          </div>
                                        ) : (
                                          <div className="flex items-center gap-1.5">
                                            <span className="text-[8px] uppercase tracking-wider px-1.5 py-0.2 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
                                              <span className="w-1 h-1 rounded-full bg-emerald-400"></span>
                                              Active
                                            </span>
                                            <span className="text-[8.5px] font-mono px-1.5 py-0.2 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20 flex items-center gap-0.5" title="Benchmark response latency (TTFT)">
                                              <Zap size={8.5} className="text-amber-400" />
                                              <span>{(() => {
                                                const metric = (billingReport?.circuitBreakerStatus?.find((c: any) => c.modelId === modelId) as any)?.latencyMs ||
                                                  (modelId.includes('lite') ? 280 : modelId.includes('2.5') ? 420 : 460);
                                                return `${metric}ms`;
                                              })()}</span>
                                            </span>
                                          </div>
                                        )}
                                      </div>

                                      {!isPrimary && (
                                        <button
                                          type="button"
                                          onClick={async () => {
                                            const updatedPool = activePool.filter((id: string) => id !== modelId);
                                            setTenants(prev => prev.map(t => t.id.toString() === tenant.id.toString() ? { ...t, geminiModelPool: updatedPool } : t));
                                            await handleSaveTenantSettings(tenant.id.toString(), {
                                              geminiModelPool: updatedPool
                                            }, true);
                                            fetchTenantBilling(tenant.id.toString(), billingMonth, billingLkrRate);
                                          }}
                                          disabled={savingSettingsId === tenant.id.toString()}
                                          className="text-slate-500 hover:text-rose-400 transition ml-1 p-0.5 rounded cursor-pointer"
                                          title={`Remove ${info.displayName} from pool`}
                                        >
                                          <X size={12} />
                                        </button>
                                      )}
                                    </div>
                                  );
                                });
                              })()}

                              {/* Dropdown to add a model to the pool */}
                              <div className="flex items-center gap-1">
                                <select
                                  onChange={async (e) => {
                                    const toAdd = e.target.value;
                                    if (!toAdd) return;
                                    const activePool = Array.isArray(tenant.geminiModelPool) && tenant.geminiModelPool.length > 0
                                      ? [...tenant.geminiModelPool]
                                      : (tenant.assignedModel ? [tenant.assignedModel] : ['gemini-3.1-flash-live-preview']);
                                    if (!activePool.includes(toAdd)) {
                                      const updatedPool = [...activePool, toAdd];
                                      setTenants(prev => prev.map(t => t.id.toString() === tenant.id.toString() ? { ...t, geminiModelPool: updatedPool } : t));
                                      await handleSaveTenantSettings(tenant.id.toString(), {
                                        geminiModelPool: updatedPool
                                      }, true);
                                      fetchTenantBilling(tenant.id.toString(), billingMonth, billingLkrRate);
                                    }
                                    e.target.value = '';
                                  }}
                                  disabled={savingSettingsId === tenant.id.toString()}
                                  defaultValue=""
                                  className="bg-slate-900 border border-cyan-500/40 rounded-xl px-2.5 py-1 text-xs text-cyan-300 font-mono font-bold outline-none cursor-pointer focus:ring-1 focus:ring-cyan-400"
                                >
                                  <option value="" disabled>+ Add Failover Model...</option>
                                  {getAllModelsCatalog()
                                    .filter(m => {
                                      const activePool = Array.isArray(tenant.geminiModelPool) && tenant.geminiModelPool.length > 0
                                        ? tenant.geminiModelPool
                                        : (tenant.assignedModel ? [tenant.assignedModel] : ['gemini-3.1-flash-live-preview']);
                                      return !activePool.includes(m.id);
                                    })
                                    .map(m => (
                                      <option key={m.id} value={m.id}>
                                        {m.displayName} (${m.inputPricePerM} / ${m.outputPricePerM})
                                      </option>
                                    ))}
                                </select>
                              </div>
                            </div>

                            {/* Smart Circuit Breaker Telemetry Footer */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-2 border-t border-white/5 text-[10px] font-mono text-slate-400">
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                                <span className="text-slate-300">
                                  In-Memory Zero-Latency Circuit Breaker:
                                </span>
                                <span className="text-emerald-400 font-semibold">
                                  Auto-recovers rate-limited models after ~65s rolling window (0ms ping overhead)
                                </span>
                              </div>
                              <div className="text-slate-400">
                                Key Pool Exhaustion: <span className="text-purple-300">Cascades to Next Dedicated Key</span> ➔ <span className="text-amber-300">Platform Master Key</span>
                              </div>
                            </div>
                          </div>

                          {/* Executive Disaggregated Token Sourcing & Quota Status */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                            {/* Free Tokens (Dedicated Key Pool) */}
                            <div className="bg-gradient-to-br from-[#062016] via-slate-950 to-emerald-950/40 border-2 border-emerald-500/50 rounded-2xl p-4 shadow-xl relative overflow-hidden">
                              <div className="flex items-center justify-between text-slate-400 text-xs font-mono mb-2">
                                <span className="font-black text-emerald-300 text-[11px] uppercase tracking-wider flex items-center gap-1.5">
                                  <Sparkles size={14} className="text-emerald-400" />
                                  Your Free Tokens (Dedicated Pool)
                                </span>
                                <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                                  100% SPONSORED
                                </span>
                              </div>
                              <p className="text-2xl font-black text-white font-mono">
                                {(billingReport.tokenUsage?.freeTokens ?? billingReport.invoice?.tokenUsage?.freeTokens ?? (billingReport.keySourceBreakdown?.hasDedicatedKeys ? billingReport.tokenUsage?.totalTokens : 0) ?? 0).toLocaleString()}
                              </p>
                              <div className="pt-2 border-t border-emerald-500/20 flex items-center justify-between text-xs font-mono">
                                <span className="text-emerald-400 font-bold">Cost: LKR 0.00 ($0.00)</span>
                                <span className="text-slate-400 text-[10px]">
                                  Saved: Rs. {Number(billingReport.amounts?.freeTokensSavedLkr ?? billingReport.amounts?.savingsLkr ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                </span>
                              </div>
                            </div>

                            {/* Paid Tokens (Platform Master AI) */}
                            <div className="bg-gradient-to-br from-[#201505] via-slate-950 to-amber-950/40 border-2 border-amber-500/50 rounded-2xl p-4 shadow-xl relative overflow-hidden">
                              <div className="flex items-center justify-between text-slate-400 text-xs font-mono mb-2">
                                <span className="font-black text-amber-300 text-[11px] uppercase tracking-wider flex items-center gap-1.5">
                                  <Zap size={14} className="text-amber-400" />
                                  Paid Tokens from Master AI
                                </span>
                                <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                                  {billingReport.tokenUsage?.paidMasterTokens ? 'OVERAGE / FAILOVER' : 'ZERO OVERAGE'}
                                </span>
                              </div>
                              <p className="text-2xl font-black text-amber-200 font-mono">
                                {(billingReport.tokenUsage?.paidMasterTokens ?? billingReport.invoice?.tokenUsage?.paidMasterTokens ?? (billingReport.keySourceBreakdown?.hasDedicatedKeys ? 0 : billingReport.tokenUsage?.totalTokens) ?? 0).toLocaleString()}
                              </p>
                              <div className="pt-2 border-t border-amber-500/20 flex items-center justify-between text-xs font-mono">
                                <span className="text-amber-300 font-bold">
                                  Rs. {Number(billingReport.amounts?.paidMasterCostLkr ?? billingReport.amounts?.billableTotalLkr ?? (billingReport.keySourceBreakdown?.hasDedicatedKeys ? 0 : billingReport.amounts?.totalLkr) ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                                <span className="text-slate-400 text-[10px]">
                                  ${Number(billingReport.amounts?.paidMasterCostUsd ?? billingReport.amounts?.billableTotalUsd ?? (billingReport.keySourceBreakdown?.hasDedicatedKeys ? 0 : billingReport.amounts?.totalUsd) ?? 0).toFixed(4)} USD
                                </span>
                              </div>
                            </div>

                            {/* Client Dedicated Pool Status */}
                            <div className="bg-slate-950/90 border border-white/10 rounded-2xl p-4 shadow-xl relative overflow-hidden flex flex-col justify-between">
                              <div className="flex items-center justify-between text-slate-400 text-xs font-mono mb-2">
                                <span className="font-black text-cyan-300 text-[11px] uppercase tracking-wider flex items-center gap-1.5">
                                  <KeyRound size={14} className="text-cyan-400" />
                                  Dedicated Key Pool Architecture
                                </span>
                                <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">
                                  ROUND-ROBIN
                                </span>
                              </div>
                              <div className="space-y-1">
                                <p className="text-sm font-bold text-white font-mono">
                                  {billingReport.keySourceBreakdown?.poolSize || billingReport.invoice?.poolSize || (tenant.geminiApiKey ? 1 : 0)} Dedicated Key(s) In Pool
                                </p>
                                <p className="text-[11px] text-slate-400 font-sans">
                                  {billingReport.keySourceBreakdown?.hasDedicatedKeys || tenant.geminiApiKey
                                    ? 'Autonomous rotation across dedicated quota with instant Master Key failover fallback.'
                                    : 'Operating directly on Platform Master Key tier (Fully Billable).'}
                                </p>
                              </div>
                              <div className="pt-2 border-t border-white/10 flex items-center justify-between text-[10px] font-mono text-slate-400">
                                <span>Auto-Cooldown: 5 min</span>
                                <span className="text-emerald-400 font-bold">100% Zero-Loss</span>
                              </div>
                            </div>
                          </div>

                          {/* 5-Metric Disaggregated Token Breakdown Grid */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3.5">
                            {/* Fresh Input / Prompt Tokens */}
                            <div className="bg-slate-950/90 border border-blue-500/30 rounded-2xl p-4 space-y-2 relative overflow-hidden shadow-lg">
                              <div className="flex items-center justify-between text-[11px] font-mono text-slate-400 uppercase tracking-wider">
                                <span>Fresh Prompt Tokens</span>
                                <span className="text-blue-400 font-bold">${billingReport.assignedModel?.inputPricePerM ?? 0.75}/1M</span>
                              </div>
                              <p className="text-xl font-black text-white font-mono">
                                {(billingReport.tokenUsage?.freshInputTokens ?? 0).toLocaleString()}
                              </p>
                              <div className="pt-2 border-t border-white/5 space-y-0.5 text-xs font-mono">
                                <p className="text-blue-300 font-bold flex items-center justify-between">
                                  <span>USD Cost:</span>
                                  <span>${(billingReport.amounts?.freshInputUsd ?? 0).toFixed(4)}</span>
                                </p>
                                <p className="text-emerald-400 font-bold flex items-center justify-between">
                                  <span>LKR (Rs.):</span>
                                  <span>Rs. {(billingReport.amounts?.freshInputLkr ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </p>
                              </div>
                            </div>

                            {/* Cached Input Tokens */}
                            <div className="bg-slate-950/90 border border-emerald-500/30 rounded-2xl p-4 space-y-2 relative overflow-hidden shadow-lg">
                              <div className="flex items-center justify-between text-[11px] font-mono text-slate-400 uppercase tracking-wider">
                                <span>Context Cached (90% OFF)</span>
                                <span className="text-emerald-400 font-bold">${billingReport.assignedModel?.cachedInputPricePerM ?? 0.075}/1M</span>
                              </div>
                              <p className="text-xl font-black text-white font-mono">
                                {(billingReport.tokenUsage?.cachedInputTokens ?? 0).toLocaleString()}
                              </p>
                              <div className="pt-2 border-t border-white/5 space-y-0.5 text-xs font-mono">
                                <p className="text-emerald-300 font-bold flex items-center justify-between">
                                  <span>USD Cost:</span>
                                  <span>${(billingReport.amounts?.cachedInputUsd ?? 0).toFixed(4)}</span>
                                </p>
                                <p className="text-emerald-400 font-bold flex items-center justify-between">
                                  <span>LKR (Rs.):</span>
                                  <span>Rs. {(billingReport.amounts?.cachedInputLkr ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </p>
                              </div>
                            </div>

                            {/* Completion / Output Tokens */}
                            <div className="bg-slate-950/90 border border-amber-500/30 rounded-2xl p-4 space-y-2 relative overflow-hidden shadow-lg">
                              <div className="flex items-center justify-between text-[11px] font-mono text-slate-400 uppercase tracking-wider">
                                <span>Completion Output</span>
                                <span className="text-amber-400 font-bold">${billingReport.assignedModel?.outputPricePerM ?? 3.00}/1M</span>
                              </div>
                              <p className="text-xl font-black text-white font-mono">
                                {(billingReport.tokenUsage?.outputTokens ?? 0).toLocaleString()}
                              </p>
                              <div className="pt-2 border-t border-white/5 space-y-0.5 text-xs font-mono">
                                <p className="text-amber-300 font-bold flex items-center justify-between">
                                  <span>USD Cost:</span>
                                  <span>${(billingReport.amounts?.outputUsd ?? 0).toFixed(4)}</span>
                                </p>
                                <p className="text-emerald-400 font-bold flex items-center justify-between">
                                  <span>LKR (Rs.):</span>
                                  <span>Rs. {(billingReport.amounts?.outputLkr ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </p>
                              </div>
                            </div>

                            {/* Live Voice Audio Tokens */}
                            <div className="bg-slate-950/90 border border-purple-500/30 rounded-2xl p-4 space-y-2 relative overflow-hidden shadow-lg">
                              <div className="flex items-center justify-between text-[11px] font-mono text-slate-400 uppercase tracking-wider">
                                <span>Voice & SIP Audio</span>
                                <span className="text-purple-400 font-bold">In/Out Tier</span>
                              </div>
                              <p className="text-xl font-black text-white font-mono">
                                {((billingReport.tokenUsage?.audioInputTokens || 0) + (billingReport.tokenUsage?.audioOutputTokens || 0)).toLocaleString()}
                              </p>
                              <div className="pt-2 border-t border-white/5 space-y-0.5 text-xs font-mono">
                                <p className="text-purple-300 font-bold flex items-center justify-between">
                                  <span>USD Cost:</span>
                                  <span>${(billingReport.amounts?.audioUsd ?? 0).toFixed(4)}</span>
                                </p>
                                <p className="text-emerald-400 font-bold flex items-center justify-between">
                                  <span>LKR (Rs.):</span>
                                  <span>Rs. {(billingReport.amounts?.audioLkr ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </p>
                              </div>
                            </div>

                            {/* Training / RAG Ingestion */}
                            <div className="bg-slate-950/90 border border-slate-700/60 rounded-2xl p-4 space-y-2 relative overflow-hidden shadow-lg">
                              <div className="flex items-center justify-between text-[11px] font-mono text-slate-400 uppercase tracking-wider">
                                <span>RAG Document / Scraper</span>
                                <span className="text-slate-400 font-bold">Aux Ingestion</span>
                              </div>
                              <p className="text-xl font-black text-white font-mono">
                                {(billingReport.tokenUsage?.trainingTokens || 0).toLocaleString()}
                              </p>
                              <div className="pt-2 border-t border-white/5 space-y-0.5 text-xs font-mono">
                                <p className="text-slate-300 font-bold flex items-center justify-between">
                                  <span>USD Cost:</span>
                                  <span>${(billingReport.amounts?.trainingUsd ?? 0).toFixed(4)}</span>
                                </p>
                                <p className="text-emerald-400 font-bold flex items-center justify-between">
                                  <span>LKR (Rs.):</span>
                                  <span>Rs. {(billingReport.amounts?.trainingLkr ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* 📊 HISTORICAL MULTI-MODEL CONSUMPTION LEDGER (ACCURATE POINT-IN-TIME BILLING) */}
                          {billingReport.modelBreakdown && billingReport.modelBreakdown.length > 0 && (
                            <div className="bg-slate-950/90 border border-cyan-500/30 rounded-3xl p-5 shadow-xl space-y-4">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/5 pb-3">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <Bot size={18} className="text-cyan-400" />
                                    <h4 className="text-sm font-black text-white font-mono uppercase tracking-wider">
                                      Point-in-Time Model Consumption Ledger
                                    </h4>
                                    <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                                      {billingReport.modelBreakdown.length} Model{billingReport.modelBreakdown.length > 1 ? 's' : ''} Recorded
                                    </span>
                                  </div>
                                  <p className="text-[11px] text-slate-400 font-mono mt-0.5">
                                    Each conversation turn is billed precisely at its assigned model's rates at the time of execution. Switching models counts new traffic from that point forward without altering historical totals.
                                  </p>
                                </div>
                                <div className="text-right">
                                  <span className="text-[10px] text-slate-400 font-mono block">Cumulative Models Total</span>
                                  <span className="text-xs font-black text-cyan-300 font-mono">
                                    Rs. {billingReport.modelBreakdown.reduce((sum: number, m: any) => sum + (m.costLkr || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </div>
                              </div>

                              {/* Models Table */}
                              <div className="overflow-x-auto">
                                <table className="w-full text-left font-mono text-xs">
                                  <thead>
                                    <tr className="border-b border-white/10 text-slate-400 text-[10px] uppercase tracking-wider">
                                      <th className="pb-2.5 font-bold">Model & Provider</th>
                                      <th className="pb-2.5 font-bold">Rate Tier ($/1M)</th>
                                      <th className="pb-2.5 font-bold text-center">Turns</th>
                                      <th className="pb-2.5 font-bold text-right">Fresh In</th>
                                      <th className="pb-2.5 font-bold text-right">Cached In</th>
                                      <th className="pb-2.5 font-bold text-right">Output</th>
                                      <th className="pb-2.5 font-bold text-right">Total Tokens</th>
                                      <th className="pb-2.5 font-bold text-right">USD Cost</th>
                                      <th className="pb-2.5 font-bold text-right text-emerald-400">LKR Cost</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-white/5">
                                    {billingReport.modelBreakdown.map((item: any) => (
                                      <tr key={item.modelId} className="hover:bg-white/[0.02] transition">
                                        <td className="py-2.5">
                                          <div className="flex items-center gap-1.5">
                                            <span className="font-bold text-white">{item.modelName}</span>
                                            {item.isPrimaryAssigned && (
                                              <span className="px-1.5 py-0.2 rounded text-[8px] font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">
                                                CURRENT
                                              </span>
                                            )}
                                          </div>
                                          <span className="text-[10px] text-slate-500 block font-normal">{item.provider} · {item.modelId}</span>
                                        </td>
                                        <td className="py-2.5 text-[11px] text-slate-300">
                                          <span>In: ${item.rates?.freshPromptPrice ?? 0.75}</span>
                                          <span className="text-slate-500"> / </span>
                                          <span>Out: ${item.rates?.completionPrice ?? 3.00}</span>
                                        </td>
                                        <td className="py-2.5 text-center text-slate-300">
                                          {item.turnsCount?.toLocaleString() ?? 0}
                                        </td>
                                        <td className="py-2.5 text-right text-blue-300">
                                          {item.promptTokens?.toLocaleString() ?? 0}
                                        </td>
                                        <td className="py-2.5 text-right text-emerald-300">
                                          {item.cachedTokens?.toLocaleString() ?? 0}
                                        </td>
                                        <td className="py-2.5 text-right text-amber-300">
                                          {item.completionTokens?.toLocaleString() ?? 0}
                                        </td>
                                        <td className="py-2.5 text-right font-black text-white">
                                          {item.totalTokens?.toLocaleString() ?? 0}
                                        </td>
                                        <td className="py-2.5 text-right text-cyan-300 font-bold">
                                          ${(item.costUsd ?? 0).toFixed(4)}
                                        </td>
                                        <td className="py-2.5 text-right font-black text-emerald-400">
                                          Rs. {(item.costLkr ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* Grand Total Financial Banner */}
                          <div className="bg-gradient-to-r from-emerald-950 via-slate-950 to-teal-950 border-2 border-emerald-500/60 rounded-3xl p-6 shadow-[0_0_50px_rgba(16,185,129,0.25)] flex flex-col md:flex-row items-center justify-between gap-6">
                            <div className="space-y-1.5 text-center md:text-left">
                              <div className="flex items-center gap-2 flex-wrap justify-center md:justify-start">
                                <span className="text-xs font-black text-emerald-400 uppercase tracking-widest font-mono">
                                  Period: {billingReport.billingPeriod?.month || billingMonth} · Total {billingReport.billingPeriod?.daysInMonth || 30} Days
                                </span>
                                {(Number(billingReport.amounts?.billableTotalLkr ?? billingReport.amounts?.paidMasterCostLkr ?? (billingReport.keySourceBreakdown?.hasDedicatedKeys ? 0 : billingReport.amounts?.totalLkr) ?? 0) === 0) && (
                                  <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-emerald-500/20 text-emerald-300 border border-emerald-500/50">
                                    100% COVERED BY TENANT POOL (Rs. 0.00 DUE)
                                  </span>
                                )}
                              </div>
                              <h4 className="text-2xl font-black text-white font-mono tracking-tight">
                                Total Consumption: {(billingReport.tokenUsage?.totalTokens ?? 0).toLocaleString()} Tokens
                              </h4>
                              <p className="text-xs text-slate-400 font-mono">
                                Converted at reference rate: <strong className="text-amber-300">1 USD = {billingReport.exchangeRate?.usdToLkr ?? billingLkrRate} LKR</strong>
                                {Number(billingReport.amounts?.freeTokensSavedLkr ?? 0) > 0 && (
                                  <span className="text-emerald-400 ml-2">
                                    · Dedicated Pool Saved: <strong>Rs. {Number(billingReport.amounts?.freeTokensSavedLkr).toLocaleString(undefined, { maximumFractionDigits: 0 })}</strong>
                                  </span>
                                )}
                              </p>
                            </div>

                            <div className="flex items-center gap-6">
                              {/* Total USD */}
                              <div className="text-center md:text-right">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono">Net Billable (USD)</span>
                                <p className="text-2xl sm:text-3xl font-black text-cyan-400 font-mono tracking-tight">
                                  ${Number(billingReport.amounts?.billableTotalUsd ?? billingReport.amounts?.paidMasterCostUsd ?? (billingReport.keySourceBreakdown?.hasDedicatedKeys ? 0 : billingReport.amounts?.totalUsd) ?? 0).toFixed(2)}
                                </p>
                                <span className="text-[9px] text-slate-500 font-mono block">Gross: ${(billingReport.amounts?.totalUsd ?? 0).toFixed(2)}</span>
                              </div>

                              {/* Divider */}
                              <div className="w-px h-12 bg-white/10 hidden sm:block"></div>

                              {/* Total LKR */}
                              <div className="text-center md:text-right">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono">Net Due to Platform</span>
                                <p className="text-2xl sm:text-3xl font-black text-emerald-400 font-mono tracking-tight">
                                  Rs. {Number(billingReport.amounts?.billableTotalLkr ?? billingReport.amounts?.paidMasterCostLkr ?? (billingReport.keySourceBreakdown?.hasDedicatedKeys ? 0 : billingReport.amounts?.totalLkr) ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </p>
                                <span className="text-[9px] text-slate-500 font-mono block">
                                  Gross: Rs. {(billingReport.amounts?.totalLkr ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </div>

                              <button
                                type="button"
                                onClick={() => setSelectedInvoiceModal(billingReport.invoice)}
                                className="px-5 py-3 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs uppercase tracking-wider transition-all shadow-xl shadow-emerald-500/30 flex items-center gap-2 cursor-pointer active:scale-95 shrink-0"
                              >
                                <Receipt size={16} />
                                <span>Invoice Preview</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
            </div>
          </div>
        )}

        {activeSection === 'internal' && (
          <div className="space-y-6">
            {/* ── Pending Approvals Banner ── */}
            {posStores.some(s => s.approvalStatus === 'PENDING') && (
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-2xl shadow-lg">
                <p className="text-amber-300 font-black text-xs uppercase tracking-wider flex items-center gap-2 mb-3">
                  <Clock size={16} /> Pending Approvals — {posStores.filter(s => s.approvalStatus === 'PENDING').length} sub-store request(s) awaiting review
                </p>
                <div className="space-y-2.5">
                  {posStores.filter(s => s.approvalStatus === 'PENDING').map(store => (
                    <div key={store.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-black/40 rounded-xl p-3.5 border border-amber-500/20 gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-extrabold text-white text-sm">{store.name}</p>
                        <p className="text-[11px] text-slate-400 font-mono mt-0.5 flex flex-wrap items-center gap-2">
                          {store.shopType && <span className="uppercase text-slate-400 font-bold">{store.shopType}</span>}
                          {store.location && <><span className="text-slate-600">·</span><span>{store.location}</span></>}
                          {store.storeCode && <span className="px-2 py-0.5 rounded bg-violet-500/20 border border-violet-500/30 text-violet-300 font-black tracking-widest text-[10px]">{store.storeCode}</span>}
                          {store.parentStoreId && <span className="text-slate-500">from parent #{store.parentStoreId}</span>}
                        </p>
                      </div>
                      <div className="admin-pending-actions flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleApproveStore(store.id.toString())}
                          disabled={approvingId === store.id.toString()}
                          className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-emerald-500/30 disabled:opacity-40 transition-all shadow-sm"
                        >
                          {approvingId === store.id.toString() ? '…' : <><CheckCircle size={14} /> Approve</>}
                        </button>
                        <button
                          onClick={() => handleRejectStore(store.id.toString())}
                          disabled={rejectingId === store.id.toString()}
                          className="flex items-center gap-1.5 px-3.5 py-2 bg-rose-500/20 border border-rose-500/40 text-rose-300 rounded-xl text-xs font-black uppercase tracking-wider hover:bg-rose-500/30 disabled:opacity-40 transition-all shadow-sm"
                        >
                          {rejectingId === store.id.toString() ? '…' : <><XCircle size={14} /> Reject</>}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Enterprise Data Table ── */}
            <div id="store-command-center" className="bg-slate-950/90 border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
              <div className="p-4 border-b border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 bg-black/40">
                <h3 className="text-sm font-black text-white flex items-center gap-2 uppercase tracking-wider font-mono">
                  <Store className="text-rose-400" size={18} /> Store Command Center
                </h3>
                {/* Filter by Status */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Filter size={14} className="text-slate-500" />
                  {(['ALL', 'ACTIVE', 'GRACE_PERIOD', 'LOCKED'] as const).map(opt => (
                    <button
                      key={opt}
                      onClick={() => setStatusFilter(opt)}
                      className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all shadow-sm ${
                        statusFilter === opt
                          ? opt === 'ALL' ? 'bg-white/20 text-white border border-white/30' :
                            opt === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' :
                            opt === 'GRACE_PERIOD' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' :
                            'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                          : 'text-slate-400 hover:text-slate-200 bg-white/5 hover:bg-white/10 border border-white/5'
                      }`}
                    >
                      {opt === 'ALL' ? 'All Stores' : opt.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="admin-table w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/10 bg-slate-900/80">
                      <th className="text-left py-3 px-3 text-slate-400 font-black uppercase tracking-widest text-[10px]">Store</th>
                      <th className="text-left py-3 px-3 text-slate-400 font-black uppercase tracking-widest text-[10px]">Code</th>
                      <th className="text-left py-3 px-3 text-slate-400 font-black uppercase tracking-widest text-[10px]">Location</th>
                      <th className="text-left py-3 px-3 text-slate-400 font-black uppercase tracking-widest text-[10px]">Status</th>
                      <th className="text-right py-3 px-3 text-slate-400 font-black uppercase tracking-widest text-[10px]">Revenue</th>
                      <th className="text-right py-3 px-3 text-slate-400 font-black uppercase tracking-widest text-[10px]">Profit</th>
                      <th className="text-right py-3 px-3 text-slate-400 font-black uppercase tracking-widest text-[10px]">Tokens</th>
                      <th className="text-center py-3 px-3 text-slate-400 font-black uppercase tracking-widest text-[10px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {rootStores.map((store) => {
                      const children = branchMap[String(store.id)] || [];
                      const isExpanded = expandedStoreGroups[String(store.id)];
                      return (
                        <React.Fragment key={store.id}>
                          <tr className={`transition-colors ${
                            store.billingStatus === 'LOCKED'
                              ? 'bg-rose-950/30 border-l-4 border-l-rose-500 hover:bg-rose-950/40'
                              : 'hover:bg-white/[0.03]'
                          }`}>
                            <td className="py-3 px-3">
                              <div className="flex items-center gap-2.5">
                                {children.length > 0 && (
                                  <button onClick={() => toggleStoreGroup(String(store.id))}
                                    className="w-5 h-5 rounded-md bg-white/5 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                                    title={isExpanded ? 'Collapse sub-shops' : 'Expand sub-shops'}>
                                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                  </button>
                                )}
                                <div>
                                  <p className="font-extrabold text-white text-sm">{store.name}</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    {store.storeCode && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-violet-500/20 border border-violet-500/30 text-violet-300 font-black tracking-widest text-[9px]">
                                        <KeyRound size={9} /> {store.storeCode}
                                      </span>
                                    )}
                                    {store.shopType && <span className="text-[10px] text-slate-400 font-bold uppercase">{store.shopType}</span>}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-3">
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-violet-500/20 border border-violet-500/30 text-violet-300 font-black tracking-widest text-[10px] font-mono select-all cursor-pointer hover:bg-violet-500/30 transition-colors shadow-sm" title="Click to select, then copy">
                                {store.storeCode || '—'}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-slate-300 font-medium text-xs">{store.location || '—'}</td>
                            <td className="py-3 px-3">
                              <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase border shadow-sm ${
                                store.billingStatus === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' :
                                store.billingStatus === 'GRACE_PERIOD' ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' :
                                'bg-rose-500/20 text-rose-300 border-rose-500/40'
                              }`}>
                                {store.billingStatus ? store.billingStatus.replace('_', ' ') : 'ACTIVE'}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-right text-white font-mono font-bold text-xs">
                              {parseFloat(store.monthlyRevenue || '0') > 0
                                ? `Rs. ${parseFloat(store.monthlyRevenue).toLocaleString('en-LK', { minimumFractionDigits: 2 })}`
                                : '$0.00'}
                            </td>
                            <td className="py-3 px-3 text-right text-amber-300 font-mono font-bold text-xs">
                              {parseFloat(store.commission || '0') > 0
                                ? `Rs. ${parseFloat(store.commission).toLocaleString('en-LK', { minimumFractionDigits: 2 })}`
                                : '$0.00'}
                            </td>
                            <td className="py-3 px-3 text-right">
                              <div className="text-purple-300 font-mono font-bold text-xs">
                                {store.lunaTokenUsage > 0
                                  ? `$${store.lunaTokenUsage.toFixed(4)}`
                                  : '0 tokens'}
                              </div>
                            </td>
                             <td className="py-3 px-3 text-center">
                               <StoreActionDropdown
                                 store={store}
                                 onManage={() => setManageStore(store)}
                                 onResetCreds={() => { setResetCredsStore(store); setResetCredsData({ storeCode: store.storeCode || '', adminUsername: store.adminUsername || '', password: '' }); setResetCredsError(''); }}
                                 onActivity={async () => {
                                   setPosActivityStore(store); setShowPosActivity(true);
                                   setPosActivityLoading(true); setPosActivityData(null);
                                   try {
                                     const res = await fetch(`/api/admin/pos-activity?storeId=${store.id}`, { headers: authHeader() });
                                     const d = await res.json();
                                     setPosActivityData(d.events ? d : { events: [], stats: [] });
                                   } catch { setPosActivityData({ events: [], stats: [] }); }
                                   finally { setPosActivityLoading(false); }
                                 }}
                                 onAudit={() => {
                                   setAuditStore(store); setShowStoreAudit(true); setAuditLoading(true); setAuditData(null);
                                   fetch('/api/admin/stores/' + store.id + '/audit', { headers: authHeader() })
                                     .then(r => r.json()).then(d => { setAuditData(d); setAuditLoading(false); })
                                     .catch(() => { setAuditLoading(false); });
                                 }}
                                 onGrace={() => handleTestLockAndBill(store, 'GRACE_PERIOD')}
                                 onHardLock={() => handleTestLockAndBill(store, 'LOCKED')}
                                 onUnlock={() => handleForceUnlock(store)}
                                 onFreeMode={async () => {
                                   try {
                                     const res = await fetch('/api/admin/billing/toggle-bypass', {
                                       method: 'POST',
                                       headers: authHeader(),
                                       body: JSON.stringify({ storeId: store.id })
                                     });
                                     if (!res.ok) throw new Error('Toggle bypass failed');
                                     const d = await res.json();
                                     setPosStores(prev => prev.map(s => s.id.toString() === store.id.toString() ? { ...s, commissionBypass: d.commissionBypass } : s));
                                   } catch (e: any) { alert(e.message); }
                                 }}
                                 onDelete={() => handleDeletePosStore(store.id.toString())}
                               />
                             </td>
                          </tr>
                          {isExpanded && children.length > 0 && children.map(child => (
                            <tr key={child.id} className={`border-b border-white/5 bg-slate-900/40 ${
                              child.billingStatus === 'LOCKED'
                                ? 'bg-rose-950/20 border-l-4 border-l-rose-500'
                                : ''
                            }`}>
                              <td className="py-2.5 px-3 pl-8">
                                <div>
                                  <p className="font-bold text-white/90 flex items-center gap-2 text-xs">
                                    <GitBranch size={12} className="text-purple-400" />
                                    {child.name}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1">
                                    {child.storeCode && (
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-500/20 border border-violet-500/30 text-violet-300 font-black tracking-widest text-[9px]">
                                        <KeyRound size={8} /> {child.storeCode}
                                      </span>
                                    )}
                                    {child.shopType && <span className="text-[9px] text-slate-400 uppercase font-bold">{child.shopType}</span>}
                                  </div>
                                </div>
                              </td>
                              <td className="py-2.5 px-3">
                                <span className="font-mono text-[10px] text-violet-300 font-bold">{child.storeCode || '—'}</span>
                              </td>
                              <td className="py-2.5 px-3 text-slate-400 text-xs font-medium">{child.location || '—'}</td>
                              <td className="py-2.5 px-3">
                                <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase border shadow-sm ${
                                  child.billingStatus === 'ACTIVE' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' :
                                  child.billingStatus === 'GRACE_PERIOD' ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' :
                                  'bg-rose-500/20 text-rose-300 border-rose-500/40'
                                }`}>
                                  {child.billingStatus ? child.billingStatus.replace('_', ' ') : 'ACTIVE'}
                                </span>
                              </td>
                              <td className="py-2.5 px-3 text-right text-white/80 font-mono text-xs font-bold">
                                {(child.monthlyRevenue || 0) > 0
                                  ? `Rs. ${(child.monthlyRevenue as number).toLocaleString('en-LK', { minimumFractionDigits: 2 })}`
                                  : '—'}
                              </td>
                              <td className="py-2.5 px-3 text-right font-mono text-xs text-amber-300 font-bold">
                                {(child.commission || 0) > 0
                                  ? `Rs. ${(child.commission as number).toLocaleString('en-LK', { minimumFractionDigits: 2 })}`
                                  : '—'}
                              </td>
                              <td className="py-2.5 px-3 text-right font-mono text-xs text-purple-300 font-bold">
                                {child.lunaTokenUsage > 0
                                  ? `$${child.lunaTokenUsage.toFixed(4)}`
                                  : '—'}
                              </td>
                              <td className="py-2.5 px-3 text-center">
                                 <StoreActionDropdown
                                   store={child}
                                   onManage={() => setManageStore(child)}
                                   onResetCreds={() => { setResetCredsStore(child); setResetCredsData({ storeCode: child.storeCode || '', adminUsername: child.adminUsername || '', password: '' }); setResetCredsError(''); }}
                                   onActivity={async () => {
                                     setPosActivityStore(child); setShowPosActivity(true);
                                     setPosActivityLoading(true); setPosActivityData(null);
                                     try {
                                       const res = await fetch(`/api/admin/pos-activity?storeId=${child.id}`, { headers: authHeader() });
                                       const d = await res.json();
                                       setPosActivityData(d.events ? d : { events: [], stats: [] });
                                     } catch { setPosActivityData({ events: [], stats: [] }); }
                                     finally { setPosActivityLoading(false); }
                                   }}
                                   onAudit={() => {
                                     setAuditStore(child); setShowStoreAudit(true); setAuditLoading(true); setAuditData(null);
                                     fetch('/api/admin/stores/' + child.id + '/audit', { headers: authHeader() })
                                       .then(r => r.json()).then(d => { setAuditData(d); setAuditLoading(false); })
                                       .catch(() => { setAuditLoading(false); });
                                   }}
                                   onGrace={() => handleTestLockAndBill(child, 'GRACE_PERIOD')}
                                   onHardLock={() => handleTestLockAndBill(child, 'LOCKED')}
                                   onUnlock={() => handleForceUnlock(child)}
                                   onFreeMode={async () => {
                                     try {
                                       const res = await fetch('/api/admin/billing/toggle-bypass', {
                                         method: 'POST',
                                         headers: authHeader(),
                                         body: JSON.stringify({ storeId: child.id })
                                       });
                                       if (!res.ok) throw new Error('Toggle bypass failed');
                                       const d = await res.json();
                                       setPosStores(prev => prev.map(s => s.id.toString() === child.id.toString() ? { ...s, commissionBypass: d.commissionBypass } : s));
                                     } catch (e: any) { alert(e.message); }
                                   }}
                                   onDelete={() => handleDeletePosStore(child.id.toString())}
                                 />
                               </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'corporate-suite' && (
          <CorporateMasterPanel authHeader={authHeader} />
        )}

        {activeSection === 'apps' && (
          <div className="space-y-6">
            <div className="bg-slate-950/90 border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
              <div className="p-4 border-b border-white/10 flex items-center justify-between bg-black/40">
                <h3 className="text-sm font-black text-white flex items-center gap-2 uppercase tracking-wider font-mono">
                  <Store className="text-blue-400" size={18} /> Kangavena App Command Center
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="admin-table w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/10 bg-slate-900/80 font-black text-slate-400 text-[10px]">
                      <th className="text-left py-3 px-4 uppercase tracking-widest">App Name</th>
                      <th className="text-left py-3 px-4 uppercase tracking-widest">Package Name</th>
                      <th className="text-left py-3 px-4 uppercase tracking-widest">Luna App Key</th>
                      <th className="text-left py-3 px-4 uppercase tracking-widest">Status</th>
                      <th className="text-right py-3 px-4 uppercase tracking-widest">Tokens</th>
                      <th className="text-center py-3 px-4 uppercase tracking-widest">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {apps.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-10 text-slate-500 font-medium font-mono text-xs">No registered Kangavena Apps yet. Add your first app above!</td>
                      </tr>
                    ) : (
                      apps.map((app) => (
                        <tr key={app.id} className="hover:bg-white/[0.03] transition-colors">
                          <td className="py-3.5 px-4 font-extrabold text-white">{app.name}</td>
                          <td className="py-3.5 px-4 font-mono text-xs text-blue-300 font-bold">{app.packageName}</td>
                          <td className="py-3.5 px-4 font-mono text-xs text-slate-400">
                            <span className="cursor-pointer text-purple-300 font-bold bg-purple-500/10 px-2 py-1 rounded border border-purple-500/20" onClick={() => toggleKeyReveal(app.id.toString())}>
                              {maskKey(app.apiKey, app.id.toString())}
                            </span>
                          </td>
                          <td className="py-3.5 px-4">
                            <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-sm">
                              {app.status || 'Active'}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 text-right font-mono font-bold text-amber-300">{(app.lunaTokenUsage || 0).toLocaleString()}</td>
                          <td className="py-3.5 px-4 text-center">
                            <button
                              onClick={() => handleDeleteApp(app.id.toString())}
                              className="p-2 bg-rose-500/15 hover:bg-rose-500/30 border border-rose-500/30 text-rose-300 rounded-xl transition-colors shadow-sm"
                              title="Delete App"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Luna App Detailed Usage Logs */}
            <div className="bg-slate-950/90 border border-white/10 rounded-2xl overflow-hidden shadow-2xl mt-6">
              <div className="p-4 border-b border-white/10 flex items-center justify-between bg-black/40">
                <h3 className="text-sm font-black text-white flex items-center gap-2 uppercase tracking-wider font-mono">
                  <Activity className="text-emerald-400 animate-pulse" size={18} /> Luna AI — App Usage & Analytics Logs
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="admin-table w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/10 bg-slate-900/80 font-black text-slate-400 text-[10px]">
                      <th className="text-left py-3 px-4 uppercase tracking-widest">Date & Time</th>
                      <th className="text-left py-3 px-4 uppercase tracking-widest">App Name</th>
                      <th className="text-left py-3 px-4 uppercase tracking-widest">User / Couple ID</th>
                      <th className="text-left py-3 px-4 uppercase tracking-widest">User Prompt</th>
                      <th className="text-left py-3 px-4 uppercase tracking-widest">Luna AI Response</th>
                      <th className="text-right py-3 px-4 uppercase tracking-widest">Tokens</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {usageLogsLoading ? (
                      <tr>
                        <td colSpan={6} className="text-center py-10 text-slate-400 font-medium font-mono text-xs">
                          <Loader2 className="animate-spin inline mr-2 text-cyan-400" size={16} /> Loading Luna app analytics logs...
                        </td>
                      </tr>
                    ) : usageLogs.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-10 text-slate-500 font-medium font-mono text-xs">No Luna usage logs recorded yet. Send some chat messages in the app!</td>
                      </tr>
                    ) : (
                      usageLogs.map((log: any) => (
                        <tr key={log.id} className="hover:bg-white/[0.03] transition-colors">
                          <td className="py-3.5 px-4 text-xs font-mono text-slate-400">
                            {new Date(log.createdAt).toLocaleString('en-US', { hour12: true })}
                          </td>
                          <td className="py-3.5 px-4 font-bold text-slate-200">
                            {log.KangavenaApp?.name || 'Luna Couple Tracking'}
                          </td>
                          <td className="py-3.5 px-4 text-xs text-cyan-300 font-bold font-mono">
                            {log.userIdentifier}
                          </td>
                          <td className="py-3.5 px-4 text-xs text-white max-w-[250px] truncate" title={log.prompt}>
                            {log.prompt}
                          </td>
                          <td className="py-3.5 px-4 text-xs text-slate-300 max-w-[300px] truncate" title={log.response}>
                            {log.response}
                          </td>
                          <td className="py-3.5 px-4 text-right font-mono font-bold text-amber-300">
                            {log.tokensUsed}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        </div>
      </main>

      {/* Add Tenant Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4">
          <div className="bg-slate-900 border border-purple-500/40 rounded-3xl p-8 w-full max-w-md shadow-[0_0_80px_rgba(168,85,247,0.25)]">
            <h2 className="text-xl font-black mb-6 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-indigo-300 to-red-400 font-mono tracking-tight">CREATE TENET CLIENT ACCOUNT</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">Business / Client Name *</label>
                <input 
                  type="text" 
                  value={newTenant.name} 
                  onChange={e => setNewTenant({...newTenant, name: e.target.value})} 
                  className="w-full bg-slate-950 border border-white/10 rounded-2xl p-3.5 text-white focus:border-purple-500 outline-none transition-colors text-sm" 
                  placeholder="e.g., ICFS Campus or Apex Logistics" 
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">Website URL (Optional)</label>
                <input 
                  type="text" 
                  value={newTenant.url} 
                  onChange={e => setNewTenant({...newTenant, url: e.target.value})} 
                  className="w-full bg-slate-950 border border-white/10 rounded-2xl p-3.5 text-white focus:border-purple-500 outline-none transition-colors text-sm" 
                  placeholder="e.g., https://icfscampus.com (Optional for WhatsApp AI)" 
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">Business Category / Purpose</label>
                <input 
                  type="text" 
                  value={newTenant.purpose} 
                  onChange={e => setNewTenant({...newTenant, purpose: e.target.value})} 
                  className="w-full bg-slate-950 border border-white/10 rounded-2xl p-3.5 text-white focus:border-purple-500 outline-none transition-colors text-sm" 
                  placeholder="e.g., Education, Automotive, Retail" 
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">Client Login Email *</label>
                <input 
                  type="email" 
                  value={newTenant.clientEmail} 
                  onChange={e => setNewTenant({...newTenant, clientEmail: e.target.value})} 
                  className="w-full bg-slate-950 border border-cyan-500/30 rounded-2xl p-3.5 text-white focus:border-cyan-400 outline-none transition-colors text-sm font-semibold" 
                  placeholder="client@company.com" 
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">Client Login Password *</label>
                <input 
                  type="password" 
                  value={newTenant.password} 
                  onChange={e => setNewTenant({...newTenant, password: e.target.value})} 
                  className="w-full bg-slate-950 border border-cyan-500/30 rounded-2xl p-3.5 text-white focus:border-cyan-400 outline-none transition-colors text-sm font-semibold" 
                  placeholder="••••••••" 
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">Custom Instructions (Secret Rules for Luna)</label>
                <textarea 
                  value={newTenant.customInstructions} 
                  onChange={e => setNewTenant({...newTenant, customInstructions: e.target.value})} 
                  className="w-full bg-slate-950 border border-white/10 rounded-2xl p-3.5 text-white focus:border-purple-500 outline-none transition-colors min-h-[90px] text-xs font-mono resize-none" 
                  placeholder="e.g., We offer 10% student discounts. Be extremely polite." 
                />
              </div>

              {/* Initial Licensing & Setup Fee Governance */}
              <div className="bg-slate-950/80 border border-white/10 rounded-2xl p-3.5 space-y-2">
                <span className="text-[10px] font-black uppercase text-amber-400 font-mono flex items-center gap-1.5">
                  <Crown size={13} /> Initial Licensing & Setup Fee
                </span>
                <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                  <button
                    type="button"
                    onClick={() => setNewTenant({ ...newTenant, isFullBundle: true, isSetupFeePaid: true, isWhiteLabel: true })}
                    className={`p-2.5 rounded-xl border text-left transition cursor-pointer ${
                      newTenant.isFullBundle 
                        ? 'bg-amber-500/20 border-amber-500/60 text-amber-200 shadow-[0_0_15px_rgba(245,158,11,0.2)]'
                        : 'bg-slate-900 border-white/5 text-slate-400'
                    }`}
                  >
                    <div className="font-bold flex items-center gap-1">
                      <Sparkles size={11} className="text-amber-400" />
                      <span>Full Pro Bundle</span>
                    </div>
                    <div className="text-[9px] text-slate-400 mt-0.5">Setup Paid • Unlimited • White-Label</div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setNewTenant({ ...newTenant, isFullBundle: false, isSetupFeePaid: false, isWhiteLabel: false })}
                    className={`p-2.5 rounded-xl border text-left transition cursor-pointer ${
                      !newTenant.isFullBundle 
                        ? 'bg-cyan-500/20 border-cyan-500/60 text-cyan-200'
                        : 'bg-slate-900 border-white/5 text-slate-400'
                    }`}
                  >
                    <div className="font-bold">Basic Trial</div>
                    <div className="text-[9px] text-slate-400 mt-0.5">100k Tokens • White-Label Locked</div>
                  </button>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-8">
                <button 
                  onClick={() => setIsModalOpen(false)} 
                  className="px-5 py-3 text-slate-400 hover:text-white transition-colors font-black uppercase text-xs tracking-wider"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleAddTenant} 
                  disabled={!newTenant.name}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 via-indigo-600 to-red-600 hover:from-purple-500 hover:to-red-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-2xl font-black uppercase text-xs tracking-wider shadow-[0_0_20px_rgba(168,85,247,0.4)] transition-all"
                >
                  Generate Key & Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Kangavena App Modal */}
      {isAppModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4">
          <div className="bg-slate-900 border border-blue-500/40 rounded-3xl p-8 w-full max-w-md shadow-[0_0_80px_rgba(59,130,246,0.25)]">
            <h2 className="text-xl font-black mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400 font-mono tracking-tight">ADD NEW KANGAVENA APP</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">App Name</label>
                <input 
                  type="text" 
                  value={newApp.name} 
                  onChange={e => setNewApp({...newApp, name: e.target.value})} 
                  className="w-full bg-slate-950 border border-white/10 rounded-2xl p-3.5 text-white focus:border-blue-500 outline-none transition-colors text-sm" 
                  placeholder="e.g., Luna Couple Tracking" 
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">Package Name</label>
                <input 
                  type="text" 
                  value={newApp.packageName} 
                  onChange={e => setNewApp({...newApp, packageName: e.target.value})} 
                  className="w-full bg-slate-950 border border-white/10 rounded-2xl p-3.5 text-white focus:border-blue-500 outline-none transition-colors text-sm font-mono" 
                  placeholder="e.g., com.kangavena.couple" 
                />
              </div>
              <div className="flex justify-end gap-3 mt-8">
                <button 
                  onClick={() => setIsAppModalOpen(false)} 
                  className="px-5 py-3 text-slate-400 hover:text-white transition-colors font-black uppercase text-xs tracking-wider"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleAddApp} 
                  disabled={!newApp.name || !newApp.packageName || appsLoading}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-2xl font-black uppercase text-xs tracking-wider shadow-[0_0_20px_rgba(59,130,246,0.4)] transition-all"
                >
                  {appsLoading ? 'Saving...' : 'Register App'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Credentials Modal */}
      {resetCredsStore && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 backdrop-blur-md p-4" onClick={e => { if (e.target === e.currentTarget) setResetCredsStore(null); }}>
          <div className="bg-slate-900 border border-amber-500/40 rounded-3xl p-8 w-full max-w-md shadow-[0_0_80px_rgba(245,158,11,0.25)]">
            <h2 className="text-xl font-black mb-1.5 text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500 font-mono tracking-tight">RESET CREDENTIALS</h2>
            <p className="text-xs text-slate-400 font-mono mb-6">Store: <span className="text-amber-300 font-bold">{resetCredsStore.name}</span></p>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">Store Code</label>
                <input
                  type="text"
                  value={resetCredsData.storeCode}
                  onChange={e => setResetCredsData({ ...resetCredsData, storeCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16) })}
                  className="w-full bg-slate-950 border border-white/10 rounded-2xl p-3.5 text-violet-300 focus:border-amber-500 outline-none transition-colors font-mono tracking-[0.2em] text-sm"
                  placeholder="e.g. TECH or RAT01"
                  maxLength={16}
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">New Admin Username</label>
                <input
                  type="text"
                  value={resetCredsData.adminUsername}
                  onChange={e => setResetCredsData({ ...resetCredsData, adminUsername: e.target.value })}
                  className="w-full bg-slate-950 border border-white/10 rounded-2xl p-3.5 text-white focus:border-amber-500 outline-none transition-colors font-mono text-sm"
                  placeholder="Leave blank to keep current"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">New Admin Password</label>
                <input
                  type="password"
                  value={resetCredsData.password}
                  onChange={e => setResetCredsData({ ...resetCredsData, password: e.target.value })}
                  className="w-full bg-slate-950 border border-white/10 rounded-2xl p-3.5 text-white focus:border-amber-500 outline-none transition-colors text-sm"
                  placeholder="Leave blank to keep current"
                />
              </div>
              {resetCredsError && (
                <div className="px-3.5 py-2.5 rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-300 text-xs font-mono">{resetCredsError}</div>
              )}
              <div className="flex justify-end gap-3 mt-8">
                <button
                  onClick={() => setResetCredsStore(null)}
                  className="px-5 py-3 text-slate-400 hover:text-white transition-colors font-black uppercase text-xs tracking-wider"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetCredentials}
                  disabled={resetCredsSaving || (!resetCredsData.adminUsername.trim() && !resetCredsData.password && !resetCredsData.storeCode.trim())}
                  className="px-6 py-3 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-2xl font-black uppercase text-xs tracking-wider shadow-[0_0_20px_rgba(245,158,11,0.4)] transition-all"
                >
                  {resetCredsSaving ? 'Saving…' : 'Save Credentials'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tenant Management Modal */}
      {manageStore && (
        <TenantManageModal
          store={manageStore}
          onClose={() => setManageStore(null)}
          adminToken={adminToken}
        />
      )}

      {/* Add POS Store Modal */}
      {isPosModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4">
          <div className="bg-slate-900 border border-red-500/40 rounded-3xl p-8 w-full max-w-lg shadow-[0_0_80px_rgba(239,68,68,0.25)] max-h-[90vh] overflow-y-auto custom-scrollbar">
            <h2 className="text-xl font-black mb-6 text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-500 font-mono tracking-tight">ADD NEW POS STORE</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">Store Name</label>
                <input 
                  type="text" 
                  value={newPosStore.name} 
                  onChange={e => setNewPosStore({...newPosStore, name: e.target.value})} 
                  className="w-full bg-slate-950 border border-white/10 rounded-2xl p-3.5 text-white focus:border-red-500 outline-none transition-colors text-sm" 
                  placeholder="e.g., Tech Hub" 
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">Location <span className="normal-case text-slate-500">(short area name)</span></label>
                <input 
                  type="text" 
                  value={newPosStore.location} 
                  onChange={e => setNewPosStore({...newPosStore, location: e.target.value})} 
                  className="w-full bg-slate-950 border border-white/10 rounded-2xl p-3.5 text-white focus:border-red-500 outline-none transition-colors text-sm" 
                  placeholder="e.g., Ratnapura" 
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">Phone <span className="normal-case text-slate-500">(optional)</span></label>
                <input type="tel" value={newPosStore.phone} onChange={e => setNewPosStore({...newPosStore, phone: e.target.value})}
                  className="w-full bg-slate-950 border border-white/10 rounded-2xl p-3.5 text-white focus:border-red-500 outline-none transition-colors text-sm"
                  placeholder="e.g., +94 77 123 4567" />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">Full Address <span className="normal-case text-slate-500">(optional)</span></label>
                <textarea value={newPosStore.address} rows={2} onChange={e => setNewPosStore({...newPosStore, address: e.target.value})}
                  className="w-full bg-slate-950 border border-white/10 rounded-2xl p-3.5 text-white focus:border-red-500 outline-none transition-colors text-sm resize-none"
                  placeholder="e.g., No. 42, Main Street, Ratnapura 70000" />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">Store Location Map <span className="normal-case text-slate-500">(click to pin)</span></label>
                <React.Suspense fallback={<div className="h-52 w-full rounded-2xl bg-slate-950 border border-white/10 mt-2" />}>
                  <LazyMapPicker
                    lat={newPosStore.latitude}
                    lng={newPosStore.longitude}
                    onChange={(lat, lng) => setNewPosStore({...newPosStore, latitude: lat, longitude: lng})}
                  />
                </React.Suspense>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">Email <span className="normal-case text-slate-500">(optional)</span></label>
                <input type="email" value={newPosStore.email} onChange={e => setNewPosStore({...newPosStore, email: e.target.value})}
                  className="w-full bg-slate-950 border border-white/10 rounded-2xl p-3.5 text-white focus:border-red-500 outline-none transition-colors text-sm"
                  placeholder="e.g. store@example.com" />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">Website <span className="normal-case text-slate-500">(optional)</span></label>
                <input type="url" value={newPosStore.website} onChange={e => setNewPosStore({...newPosStore, website: e.target.value})}
                  className="w-full bg-slate-950 border border-white/10 rounded-2xl p-3.5 text-white focus:border-red-500 outline-none transition-colors text-sm"
                  placeholder="e.g. https://techhub.lk" />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">Store Code</label>
                <input 
                  type="text" 
                  value={newPosStore.storeCode} 
                  onChange={e => setNewPosStore({...newPosStore, storeCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16)})} 
                  className="w-full bg-slate-950 border border-white/10 rounded-2xl p-3.5 text-violet-300 focus:border-red-500 outline-none transition-colors font-mono tracking-[0.2em] text-sm" 
                  placeholder="e.g. TECH or RAT01 (auto if blank)" 
                  maxLength={16}
                />
                <p className="text-[10px] text-slate-500 mt-1 font-mono">Short memorable code managers type at login. Uppercase letters/numbers only.</p>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">Admin Username</label>
                <input 
                  type="text" 
                  value={newPosStore.adminUsername} 
                  onChange={e => setNewPosStore({...newPosStore, adminUsername: e.target.value})} 
                  className="w-full bg-slate-950 border border-white/10 rounded-2xl p-3.5 text-white focus:border-red-500 outline-none transition-colors font-mono text-sm" 
                  placeholder="Login username (auto-generated if blank)" 
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 mb-1.5 uppercase tracking-widest">Admin Password</label>
                <input 
                  type="password" 
                  value={newPosStore.password} 
                  onChange={e => setNewPosStore({...newPosStore, password: e.target.value})} 
                  className="w-full bg-slate-950 border border-white/10 rounded-2xl p-3.5 text-white focus:border-red-500 outline-none transition-colors text-sm" 
                  placeholder="Create a secure login password" 
                />
              </div>
              {newPosStore.name && !newPosStore.adminUsername && (
                <p className="text-xs text-slate-500 font-mono">Username will be auto-set to: <span className="text-rose-400 font-bold">{newPosStore.name.toLowerCase().replace(/\s+/g, '_')}</span></p>
              )}
              <div className="flex justify-end gap-3 mt-8">
                <button 
                  onClick={() => setIsPosModalOpen(false)} 
                  className="px-5 py-3 text-slate-400 hover:text-white transition-colors font-black uppercase text-xs tracking-wider"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleAddPosStore} 
                  disabled={!newPosStore.name || !newPosStore.password}
                  className="px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-2xl font-black uppercase text-xs tracking-wider shadow-[0_0_20px_rgba(239,68,68,0.4)] transition-all"
                >
                  Generate POS Key
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Billing Test — Invoice Breakdown Modal */}
      {billingTestStore && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4" onClick={e => { if (e.target === e.currentTarget) setBillingTestStore(null); }}>
          <div className="bg-slate-900 border border-rose-500/40 rounded-3xl p-8 w-full max-w-lg shadow-[0_0_80px_rgba(225,29,72,0.25)]">
            <h2 className="text-xl font-black mb-6 text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-red-500 font-mono tracking-tight">TEST LOCK & BILL</h2>
            <p className="text-slate-400 text-xs font-mono mb-6">Store: <span className="text-white font-bold">{billingTestStore.store.name}</span></p>
            {billingTestStore.loading && (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin w-8 h-8 border-2 border-rose-500/50 border-t-rose-500 rounded-full" />
                <span className="ml-3 text-slate-400 font-mono text-xs">Generating test invoice & updating store status…</span>
              </div>
            )}
            {billingTestStore.error && (
              <div className="px-4 py-3 rounded-2xl bg-rose-500/15 border border-rose-500/30 text-rose-300 text-xs font-mono mb-4">{billingTestStore.error}</div>
            )}
            {billingTestStore.invoice && !billingTestStore.loading && (
              <div className="space-y-4">
                <div className={`px-4 py-3.5 rounded-2xl font-black text-xs flex items-center gap-2 uppercase tracking-wider ${
                  billingTestStore.invoice.billingStatus === 'GRACE_PERIOD'
                    ? 'bg-amber-500/15 border border-amber-500/30 text-amber-300'
                    : 'bg-rose-500/15 border border-rose-500/30 text-rose-300'
                }`}>
                  <Zap size={14} /> Store set to {billingTestStore.invoice.billingStatus || 'LOCKED'} — 1 test invoice generated
                </div>
                <div className="bg-black/60 rounded-2xl p-4 border border-white/10 space-y-2.5 font-mono text-xs">
                  <div className="flex justify-between text-slate-400"><span>Invoice Ref</span><span className="text-white font-bold">{billingTestStore.invoice.invoiceRef}</span></div>
                  <div className="flex justify-between text-slate-400"><span>Billing Period</span><span className="text-white font-bold">{billingTestStore.invoice.billingPeriod}</span></div>
                  <div className="border-t border-white/10 my-2" />
                  <div className="flex justify-between text-slate-400"><span>Input Tokens</span><span className="text-cyan-300 font-bold">{billingTestStore.invoice.tokenInputCount?.toLocaleString() || 0}</span></div>
                  <div className="flex justify-between text-slate-400"><span>Output Tokens</span><span className="text-cyan-300 font-bold">{billingTestStore.invoice.tokenOutputCount?.toLocaleString() || 0}</span></div>
                  <div className="flex justify-between text-slate-400"><span>Token Cost (USD)</span><span className="text-cyan-300 font-bold">${(billingTestStore.invoice.tokenCostUsd || 0).toFixed(4)}</span></div>
                  <div className="border-t border-white/10 my-2" />
                  <div className="flex justify-between text-slate-400"><span>Net Profit (LKR)</span><span className="text-amber-300 font-bold">{billingTestStore.invoice.netProfitLkr?.toFixed(2) || '0.00'}</span></div>
                  <div className="flex justify-between text-slate-400"><span>Commission (LKR)</span><span className="text-amber-300 font-bold">{billingTestStore.invoice.commissionLkr?.toFixed(2) || '0.00'}</span></div>
                  <div className="flex justify-between text-slate-400"><span>Commission (USD)</span><span className="text-amber-300 font-bold">${(billingTestStore.invoice.commissionUsd || 0).toFixed(4)}</span></div>
                  <div className="border-t border-white/10 my-2" />
                  <div className="flex justify-between text-base font-black"><span className="text-slate-300 uppercase tracking-wider text-xs">Total (USD)</span><span className="text-rose-400">${(billingTestStore.invoice.totalAmtUsd || 0).toFixed(4)}</span></div>
                </div>
                <div className="flex justify-end">
                  <button onClick={() => setBillingTestStore(null)}
                    className="px-6 py-3 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 text-white rounded-2xl font-black uppercase text-xs tracking-wider shadow-[0_0_20px_rgba(225,29,72,0.4)] transition-all">
                    Close
                  </button>
                </div>
              </div>
            )}
            {!billingTestStore.invoice && !billingTestStore.loading && !billingTestStore.error && (
              <div className="flex justify-end">
                <button onClick={() => setBillingTestStore(null)}
                  className="px-5 py-3 text-slate-400 hover:text-white transition-colors font-black uppercase text-xs tracking-wider">
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Commission Ledger Modal */}
      {firstStoreApiKey && (
        <CommissionLedgerModal
          isOpen={showCommissionLedger}
          onClose={() => setShowCommissionLedger(false)}
          storeApiKey={firstStoreApiKey}
        />
      )}

      {/* Personal Luna Panel */}
      <PersonalLunaPanel
        isOpen={showPersonalLuna}
        onClose={() => setShowPersonalLuna(false)}
        authHeader={authHeader}
      />

      {/* Luna Master Config & Permission Hub */}
      <LunaMasterConfigHub
        isOpen={showLunaConfigHub}
        onClose={() => setShowLunaConfigHub(false)}
        authHeader={authHeader}
      />



      {/* Luna Usage Ledger Modal */}
      {showLunaUsageLedger && (
        <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
          onClick={e => { if (e.target === e.currentTarget) { setShowLunaUsageLedger(false); setLunaUsageData(null); } }}
        >
          <div className="bg-slate-900 border border-purple-500/30 rounded-3xl w-full max-w-4xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-8 py-6 border-b border-white/10 bg-slate-950 shrink-0">
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-2xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
                  <BrainCircuit className="text-purple-400" size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-black text-white">Luna Token Usage</h2>
                  <p className="text-xs text-slate-400 font-medium">Lifetime platform-wide AI token consumption</p>
                </div>
              </div>
              <button onClick={() => { setShowLunaUsageLedger(false); setLunaUsageData(null); }}
                className="w-9 h-9 rounded-xl hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-8">
              {lunaUsageLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500"></div>
                </div>
              ) : lunaUsageData ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-black/60 rounded-2xl border border-white/10 p-5">
                      <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Total Cost</p>
                      <p className="text-2xl font-black text-white font-mono">{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(lunaUsageData.totalCost)}</p>
                    </div>
                    <div className="bg-black/60 rounded-2xl border border-white/10 p-5">
                      <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Input Tokens</p>
                      <p className="text-2xl font-black text-cyan-300 font-mono">{new Intl.NumberFormat('en-US').format(lunaUsageData.totalInputTokens)}</p>
                    </div>
                    <div className="bg-black/60 rounded-2xl border border-white/10 p-5">
                      <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Output Tokens</p>
                      <p className="text-2xl font-black text-amber-300 font-mono">{new Intl.NumberFormat('en-US').format(lunaUsageData.totalOutputTokens)}</p>
                    </div>
                  </div>
                  {lunaUsageData.records.length > 0 ? (
                    <div className="overflow-x-auto rounded-2xl border border-white/10">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-white/10 bg-slate-950">
                            <th className="text-left py-3 px-4 text-slate-400 font-black uppercase tracking-widest text-[10px]">Date</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-black uppercase tracking-widest text-[10px]">Store</th>
                            <th className="text-right py-3 px-4 text-slate-400 font-black uppercase tracking-widest text-[10px]">Input</th>
                            <th className="text-right py-3 px-4 text-slate-400 font-black uppercase tracking-widest text-[10px]">Output</th>
                            <th className="text-right py-3 px-4 text-slate-400 font-black uppercase tracking-widest text-[10px]">Cost</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {lunaUsageData.records.map((rec: any, i: number) => (
                            <tr key={rec.id || i} className="hover:bg-white/[0.04] transition-colors">
                              <td className="py-3 px-4 text-slate-300 whitespace-nowrap font-mono">{rec.recordedAt ? new Date(rec.recordedAt).toLocaleDateString() : '—'}</td>
                              <td className="py-3 px-4 text-slate-200 font-bold">{rec.storeName || 'Store #' + rec.storeId}</td>
                              <td className="py-3 px-4 text-right text-slate-300 font-mono">{new Intl.NumberFormat().format(rec.inputTokens || 0)}</td>
                              <td className="py-3 px-4 text-right text-slate-300 font-mono">{new Intl.NumberFormat().format(rec.outputTokens || 0)}</td>
                              <td className="py-3 px-4 text-right font-mono text-purple-300 font-bold">${Number(rec.totalCostUsd || 0).toFixed(6)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-6 text-amber-300 text-xs font-mono text-center">
                      No token usage records found.
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Store Audit Modal */}
      {showStoreAudit && auditStore && (
        <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
          onClick={e => { if (e.target === e.currentTarget) { setShowStoreAudit(false); setAuditData(null); setAuditStore(null); } }}
        >
          <div className="bg-slate-900 border border-cyan-500/30 rounded-3xl w-full max-w-4xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-8 py-6 border-b border-white/10 bg-slate-950 shrink-0">
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                  <Receipt className="text-cyan-400" size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-black text-white">{auditStore.name}</h2>
                  <p className="text-xs text-slate-400 font-medium">Billing & Usage Audit Trail</p>
                </div>
              </div>
              <button onClick={() => { setShowStoreAudit(false); setAuditData(null); setAuditStore(null); }}
                className="w-9 h-9 rounded-xl hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-8">
              {auditLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-500"></div>
                </div>
              ) : auditData ? (
                <div className="space-y-8">
                  <div className="bg-black/60 rounded-2xl border border-white/10 p-5">
                    <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Total Billed</p>
                    <p className="text-3xl font-black text-white font-mono">{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(auditData.totalBilledUsd)}</p>
                  </div>
                  <div>
                    <h3 className="text-xs font-black text-slate-300 uppercase tracking-widest mb-3 flex items-center gap-2 font-mono">
                      <Receipt size={14} className="text-cyan-400" /> Billing Invoices
                    </h3>
                    {auditData.invoices.length > 0 ? (
                      <div className="overflow-x-auto rounded-2xl border border-white/10">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-white/10 bg-slate-950 text-[10px]">
                              <th className="text-left py-3 px-4 text-slate-400 font-black uppercase tracking-widest">Period</th>
                              <th className="text-left py-3 px-4 text-slate-400 font-black uppercase tracking-widest">Ref</th>
                              <th className="text-right py-3 px-4 text-slate-400 font-black uppercase tracking-widest">Amount</th>
                              <th className="text-left py-3 px-4 text-slate-400 font-black uppercase tracking-widest">Status</th>
                              <th className="text-left py-3 px-4 text-slate-400 font-black uppercase tracking-widest">Due</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/5">
                            {auditData.invoices.map((inv: any, i: number) => (
                              <tr key={inv.id || i} className="hover:bg-white/[0.04] transition-colors">
                                <td className="py-3 px-4 text-slate-300 font-mono">{inv.billingPeriod || '—'}</td>
                                <td className="py-3 px-4 text-slate-300 font-mono font-bold">{inv.invoiceRef || '—'}</td>
                                <td className="py-3 px-4 text-right text-white font-mono font-bold">${Number(inv.totalAmtUsd || 0).toFixed(2)}</td>
                                <td className="py-3 px-4">
                                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase ${
                                    inv.status === 'PAID' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' :
                                    inv.status === 'WAIVED' ? 'bg-slate-800 text-slate-400 border border-slate-700' :
                                    'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                                  }`}>{inv.status || 'UNPAID'}</span>
                                </td>
                                <td className="py-3 px-4 text-slate-400 font-mono">{inv.dueDate || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-slate-500 text-xs italic font-mono">No invoices yet</p>
                    )}
                  </div>
                  <div>
                    <h3 className="text-xs font-black text-slate-300 uppercase tracking-widest mb-3 flex items-center gap-2 font-mono">
                      <BrainCircuit size={14} className="text-purple-400" /> Luna Token Usage
                    </h3>
                    {auditData.lunaUsage.length > 0 ? (
                      <div className="overflow-x-auto rounded-2xl border border-white/10">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-white/10 bg-slate-950 text-[10px]">
                              <th className="text-left py-3 px-4 text-slate-400 font-black uppercase tracking-widest">Date</th>
                              <th className="text-right py-3 px-4 text-slate-400 font-black uppercase tracking-widest">Input</th>
                              <th className="text-right py-3 px-4 text-slate-400 font-black uppercase tracking-widest">Output</th>
                              <th className="text-right py-3 px-4 text-slate-400 font-black uppercase tracking-widest">Cost</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/5">
                            {auditData.lunaUsage.map((rec: any, i: number) => (
                              <tr key={rec.id || i} className="hover:bg-white/[0.04] transition-colors">
                                <td className="py-3 px-4 text-slate-300 font-mono whitespace-nowrap">{rec.recordedAt ? new Date(rec.recordedAt).toLocaleDateString() : '—'}</td>
                                <td className="py-3 px-4 text-right text-slate-300 font-mono">{new Intl.NumberFormat().format(rec.inputTokens || 0)}</td>
                                <td className="py-3 px-4 text-right text-slate-300 font-mono">{new Intl.NumberFormat().format(rec.outputTokens || 0)}</td>
                                <td className="py-3 px-4 text-right font-mono text-purple-300 font-bold">${Number(rec.totalCostUsd || 0).toFixed(6)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-slate-500 text-xs italic font-mono">No usage records yet</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-16 text-slate-400 font-mono text-xs">Failed to load audit data</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* POS Store Activity Modal */}
      {showPosActivity && posActivityStore && (
        <div className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
          onClick={e => { if (e.target === e.currentTarget) { setShowPosActivity(false); setPosActivityData(null); setPosActivityStore(null); } }}
        >
          <div className="bg-[#090D18] border border-amber-500/30 rounded-3xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-[0_0_80px_rgba(245,158,11,0.15)] flex flex-col">
            {/* Header */}
            <div className="px-6 py-5 border-b border-amber-500/20 bg-slate-950/80 shrink-0">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                    <Activity className="text-amber-400" size={20} />
                  </div>
                  <div>
                    <h2 className="text-xl font-black text-white tracking-tight font-mono">STORE ACTIVITY</h2>
                    <p className="text-xs text-amber-400/80 font-mono">{posActivityStore.name} · Session & Login Auditing</p>
                  </div>
                </div>
                <button onClick={() => { setShowPosActivity(false); setPosActivityData(null); setPosActivityStore(null); setPosActivityFrom(''); setPosActivityTo(''); }}
                  className="w-9 h-9 rounded-xl border border-amber-500/20 flex items-center justify-center text-amber-400/60 hover:text-white hover:border-amber-400 hover:bg-amber-500/10 transition-all">
                  <X size={16} />
                </button>
              </div>
              {/* Date filter */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] text-slate-400 uppercase tracking-widest font-black">Filter:</span>
                <input type="date" value={posActivityFrom} onChange={e => setPosActivityFrom(e.target.value)}
                  className="bg-black/60 border border-white/10 rounded-xl px-3 py-1.5 text-xs text-slate-200 font-mono outline-none focus:border-amber-500/50 [color-scheme:dark]" />
                <span className="text-slate-500 text-xs">→</span>
                <input type="date" value={posActivityTo} onChange={e => setPosActivityTo(e.target.value)}
                  className="bg-black/60 border border-white/10 rounded-xl px-3 py-1.5 text-xs text-slate-200 font-mono outline-none focus:border-amber-500/50 [color-scheme:dark]" />
                <button onClick={async () => {
                  setPosActivityLoading(true);
                  try {
                    const params = new URLSearchParams({ storeId: String(posActivityStore.id) });
                    if (posActivityFrom) params.set('from', posActivityFrom);
                    if (posActivityTo) params.set('to', posActivityTo);
                    const res = await fetch(`/api/admin/pos-activity?${params}`, { headers: authHeader() });
                    const d = await res.json();
                    setPosActivityData(d.events ? d : { events: [], stats: [] });
                  } catch { setPosActivityData({ events: [], stats: [] }); }
                  finally { setPosActivityLoading(false); }
                }} className="px-4 py-1.5 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs font-black uppercase tracking-wider hover:bg-amber-500/30 transition-all flex items-center gap-2 shadow-sm">
                  <RefreshCw size={12} /> {posActivityFrom || posActivityTo ? 'Search' : 'Latest'}
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 p-6 font-sans">
              {posActivityLoading ? (
                <div className="flex flex-col items-center justify-center h-48">
                  <div className="w-9 h-9 border-2 border-amber-500/30 border-t-amber-400 rounded-full animate-spin mb-3" />
                  <span className="text-amber-400 text-xs font-mono">Loading activity...</span>
                </div>
              ) : posActivityData ? (
                <>
                  {/* Summary stats */}
                  {posActivityData.stats?.[0] && (() => {
                    const s = posActivityData.stats[0];
                    return (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                        {[
                          { label: 'Total Logins', value: s.logins ?? 0, color: 'text-emerald-400' },
                          { label: 'Sessions Closed', value: s.logouts ?? 0, color: 'text-rose-400' },
                          { label: 'Unique IPs', value: s.uniqueIPCount ?? 0, color: 'text-cyan-400' },
                          { label: 'Avg Session', value: `${s.avgSessionMinutes ?? 0} min`, color: 'text-amber-300' },
                        ].map(({ label, value, color }) => (
                          <div key={label} className="bg-black/50 border border-white/10 rounded-2xl p-4">
                            <p className="text-[9px] text-slate-400 uppercase tracking-widest font-black mb-1">{label}</p>
                            <p className={`text-xl font-black font-mono ${color}`}>{value}</p>
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Unique IPs */}
                  {posActivityData.stats?.[0]?.uniqueIPs?.length > 0 && (
                    <div className="mb-6 p-4 bg-black/40 border border-white/10 rounded-2xl">
                      <p className="text-[10px] text-slate-400 uppercase tracking-widest font-black mb-2.5">IP Addresses Used</p>
                      <div className="flex flex-wrap gap-2">
                        {posActivityData.stats[0].uniqueIPs.map((ip: string) => (
                          <span key={ip} className="px-3 py-1 bg-cyan-500/15 border border-cyan-500/30 rounded-xl text-cyan-300 font-mono text-xs font-bold">{ip}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Event Timeline */}
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest font-black mb-3 font-mono">Event Timeline</p>
                  {posActivityData.events.length === 0 ? (
                    <div className="text-center py-10 text-slate-500 text-xs font-mono">No activity recorded yet</div>
                  ) : (
                    <div className="overflow-x-auto rounded-2xl border border-white/10">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-white/10 bg-slate-950 text-[10px]">
                            {['Event', 'User', 'Role', 'IP', 'Duration', 'Timestamp'].map(h => (
                              <th key={h} className="px-4 py-3 text-left font-black text-slate-400 uppercase tracking-widest">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {posActivityData.events.map((ev: any, i: number) => (
                            <tr key={i} className="hover:bg-white/[0.03] transition-colors">
                              <td className="px-4 py-3">
                                <span className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase ${ev.eventType === 'LOGIN' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : ev.eventType === 'LOGOUT' ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}>
                                  {ev.eventType}
                                </span>
                              </td>
                              <td className="px-4 py-3 font-mono font-bold text-white">{ev.username || '—'}</td>
                              <td className="px-4 py-3 text-slate-400 capitalize font-medium">{ev.role || '—'}</td>
                              <td className="px-4 py-3 font-mono text-cyan-300 font-bold">{ev.ip || '—'}</td>
                              <td className="px-4 py-3 font-mono text-amber-300 font-bold">
                                {ev.durationSeconds > 0 ? `${Math.floor(ev.durationSeconds / 60)}m ${ev.durationSeconds % 60}s` : '—'}
                              </td>
                              <td className="px-4 py-3 text-slate-400 whitespace-nowrap font-mono text-xs">
                                {ev.createdAt ? new Date(ev.createdAt).toLocaleString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-slate-400 font-mono text-xs">Failed to load activity data.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Luna AI Container - Persistent to keep WebSocket alive */}
      <div className={`fixed inset-0 z-[99999] transition-all duration-500 ease-in-out ${isLunaMaximized ? 'bg-black/95 backdrop-blur-2xl pointer-events-auto' : 'bg-transparent pointer-events-none'}`}>
        
        {/* Minimize Button */}
        {isLunaMaximized && (
          <button 
            onClick={() => setIsLunaMaximized(false)} 
            className="absolute top-8 right-8 text-white/50 hover:text-white text-4xl transition-colors z-[100000]"
            title="Minimize Luna"
          >
            &times;
          </button>
        )}

        {/* The REAL Shared Luna Component */}
        <div 
          className={`absolute transition-all duration-500 ease-in-out flex items-center justify-center ${
            isLunaMaximized 
              ? 'inset-0 scale-100' 
              : 'bottom-6 right-6 w-[192px] h-[192px] scale-[0.4] origin-bottom-right rounded-full bg-black/50 border border-purple-500/50 shadow-[0_0_60px_rgba(168,85,247,0.5)] pointer-events-auto cursor-pointer hover:scale-[0.45] luna-minimized'
          }`}
          onClick={() => !isLunaMaximized && setIsLunaMaximized(true)}
        >
          <div className={`w-full h-full flex items-center justify-center ${!isLunaMaximized ? 'pointer-events-none' : ''}`}>
            <LunaVoiceModel apiKey="GOD_MODE_ADMIN" role="admin" />
          </div>
        </div>
      </div>

      {/* ── Phase 32: Edit Credentials & Dedicated Sovereign AI Key Modal ── */}
      {credModalTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md" onClick={() => setCredModalTenant(null)}>
          <div className="w-full max-w-lg bg-[#0b0f19] border border-cyan-500/40 rounded-3xl p-6 shadow-[0_0_60px_rgba(6,182,212,0.25)] space-y-5 relative max-h-[90vh] overflow-y-auto custom-scrollbar" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-cyan-500/20 border border-cyan-400/40 text-cyan-300">
                  <KeyRound size={20} />
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-100 flex items-center gap-2">
                    Tenant Credentials & Sovereign AI Engine
                  </h3>
                  <p className="text-xs text-cyan-300/80 font-mono">{credModalTenant.name} (ID: {credModalTenant.id})</p>
                </div>
              </div>
              <button type="button" onClick={() => setCredModalTenant(null)} className="p-1.5 rounded-xl bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all cursor-pointer">✕</button>
            </div>

            <div className="space-y-5">
              {/* Section 1: Tenant Admin Login */}
              <div className="space-y-3 p-4 rounded-2xl bg-slate-950/60 border border-white/5">
                <h4 className="text-xs font-black uppercase tracking-wider text-slate-300 font-mono flex items-center gap-1.5">
                  <Shield size={13} className="text-cyan-400" />
                  Tenant Admin Login Credentials
                </h4>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">Admin Email</label>
                  <input
                    type="email"
                    value={credModalEmail}
                    onChange={(e) => setCredModalEmail(e.target.value)}
                    placeholder="admin@example.com"
                    className="w-full bg-slate-900 border border-cyan-500/30 focus:border-cyan-400 focus:outline-none rounded-xl px-3.5 py-2 text-xs text-slate-200 placeholder:text-slate-500 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">New Password <span className="text-slate-500 font-normal">(leave blank to keep current)</span></label>
                  <input
                    type="password"
                    value={credModalPassword}
                    onChange={(e) => setCredModalPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-slate-900 border border-cyan-500/30 focus:border-cyan-400 focus:outline-none rounded-xl px-3.5 py-2 text-xs text-slate-200 placeholder:text-slate-500 font-mono"
                  />
                </div>
              </div>

              {/* Section 2: Dedicated Sovereign AI Key Pool (Multi-Key Load Balancer & Auto-Failover) */}
              <div className="space-y-3.5 p-4 rounded-2xl bg-gradient-to-b from-purple-950/30 to-slate-950/60 border border-purple-500/30">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-wider text-purple-300 font-mono flex items-center gap-1.5">
                      <Cpu size={14} className="text-purple-400" />
                      Dedicated Sovereign AI Key Pool (BYOK)
                    </h4>
                    <p className="text-[10px] text-slate-400 font-mono">
                      Multi-Key Smart Load Balancing & 429 Auto-Failover Cascade
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-purple-500/20 text-purple-300 border border-purple-500/40">
                      {credModalKeyPool.filter(k => k.status === 'active').length} ACTIVE
                    </span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">
                      ~{Math.max(15, credModalKeyPool.filter(k => k.status === 'active').length * 15)} RPM POOL
                    </span>
                  </div>
                </div>

                {/* Key Pool List */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-wider text-slate-400 font-mono">
                    <span>Configured Keys in Pool ({credModalKeyPool.length})</span>
                    {credModalLoadingPool && (
                      <span className="flex items-center gap-1 text-cyan-400">
                        <RefreshCw size={10} className="animate-spin" /> Syncing pool...
                      </span>
                    )}
                  </div>

                  {credModalKeyPool.length === 0 && !credModalLoadingPool ? (
                    <div className="p-3 rounded-xl bg-slate-900/80 border border-white/10 flex items-center gap-2 text-slate-400">
                      <span className="w-2 h-2 rounded-full bg-slate-500" />
                      <p className="text-[11px] text-slate-300 font-mono">
                        No dedicated keys in pool. All requests utilize <strong className="text-cyan-400">Platform Sovereign Master Key</strong>.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                      {credModalKeyPool.map((keyItem: any) => {
                        const isTesting = credModalTestingKeyId === keyItem.id;
                        const testResult = credModalKeyResults[keyItem.id];
                        const isRateLimited = keyItem.status === 'rate_limited';

                        return (
                          <div
                            key={keyItem.id}
                            className={`p-3 rounded-xl border transition-all ${
                              isRateLimited
                                ? 'bg-amber-950/20 border-amber-500/40'
                                : keyItem.status === 'invalid'
                                ? 'bg-rose-950/20 border-rose-500/40'
                                : 'bg-slate-900/90 border-purple-500/30'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <span
                                  className={`w-2 h-2 rounded-full shrink-0 ${
                                    isRateLimited
                                      ? 'bg-amber-400 animate-pulse'
                                      : keyItem.status === 'invalid'
                                      ? 'bg-rose-400'
                                      : 'bg-emerald-400'
                                  }`}
                                />
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <p className="text-xs font-bold text-slate-100 truncate">{keyItem.label || 'Dedicated Key'}</p>
                                    <span
                                      className={`px-1.5 py-0.2 rounded text-[9px] font-mono font-bold uppercase ${
                                        isRateLimited
                                          ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                                          : keyItem.status === 'invalid'
                                          ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                                          : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                      }`}
                                    >
                                      {isRateLimited ? '⏳ 429 Cooldown' : keyItem.status || 'Active'}
                                    </span>
                                  </div>
                                  <p className="text-[11px] font-mono text-purple-400/90 truncate">{keyItem.maskedKey}</p>
                                </div>
                              </div>

                              <div className="flex items-center gap-1.5 shrink-0">
                                {isRateLimited && (
                                  <button
                                    type="button"
                                    onClick={() => handleResetPoolKeyCooldown(keyItem.id)}
                                    className="px-2 py-1 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 text-[10px] font-bold border border-amber-500/30 transition-all cursor-pointer"
                                    title="Manually re-activate key now"
                                  >
                                    Reset
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleTestPoolKey(keyItem.id)}
                                  disabled={isTesting}
                                  className="px-2.5 py-1 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 text-[10px] font-bold border border-purple-500/30 transition-all flex items-center gap-1 cursor-pointer disabled:opacity-40"
                                  title="Test this API key live against Gemini API"
                                >
                                  {isTesting ? (
                                    <><RefreshCw size={10} className="animate-spin text-purple-400" /> Testing</>
                                  ) : (
                                    <><Zap size={10} className="text-purple-400" /> Test</>
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveKeyFromPool(keyItem.id)}
                                  className="p-1 rounded-lg bg-rose-500/15 hover:bg-rose-500/30 text-rose-300 border border-rose-500/30 transition-all cursor-pointer"
                                  title="Delete key from pool"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>

                            <div className="mt-1.5 flex items-center justify-between text-[10px] font-mono text-slate-500">
                              <span>Used: {keyItem.usageCount || 0} calls</span>
                              <span>{keyItem.lastUsedAt ? `Last active ${Math.round((Date.now() - keyItem.lastUsedAt)/60000)}m ago` : 'Never used yet'}</span>
                            </div>

                            {/* Specific Key Test Feedback */}
                            {testResult && (
                              <div className={`mt-2 p-1.5 rounded-lg text-[10px] font-mono flex items-center gap-1.5 border ${
                                testResult.success
                                  ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                                  : 'bg-rose-500/15 border-rose-500/40 text-rose-300'
                              }`}>
                                {testResult.success ? <CheckCircle size={11} className="shrink-0" /> : <XCircle size={11} className="shrink-0" />}
                                <span className="truncate">{testResult.message}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Add Key to Pool Subform */}
                <div className="pt-2 border-t border-purple-500/20 space-y-2">
                  <label className="block text-[10px] font-black uppercase tracking-wider text-purple-300 font-mono">
                    + Add New Gemini API Key to Pool
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div className="sm:col-span-2">
                      <input
                        type="password"
                        value={credModalNewKeyInput}
                        onChange={(e) => setCredModalNewKeyInput(e.target.value)}
                        placeholder="Paste Gemini Key: AIzaSy... or AQ..."
                        className="w-full bg-slate-900 border border-purple-500/40 focus:border-purple-400 focus:outline-none rounded-xl px-3 py-2 text-xs text-slate-200 placeholder:text-slate-500 font-mono"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        value={credModalNewKeyLabel}
                        onChange={(e) => setCredModalNewKeyLabel(e.target.value)}
                        placeholder="Label: e.g. Ad Key 2"
                        className="w-full bg-slate-900 border border-purple-500/40 focus:border-purple-400 focus:outline-none rounded-xl px-3 py-2 text-xs text-slate-200 placeholder:text-slate-500 font-mono"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-slate-500 font-mono">
                      🔒 AES-256-GCM encrypted. Hidden from tenant client.
                    </p>
                    <button
                      type="button"
                      onClick={handleAddKeyToPool}
                      disabled={credModalAddingKey || !credModalNewKeyInput.trim()}
                      className="px-3.5 py-1.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold font-mono transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-40"
                    >
                      {credModalAddingKey ? (
                        <><RefreshCw size={12} className="animate-spin" /> Adding...</>
                      ) : (
                        <>+ Add Key to Pool</>
                      )}
                    </button>
                  </div>
                </div>

                {/* Failover Cascade & Cooldown Protection Policy */}
                <div className="p-2.5 rounded-xl bg-slate-950/80 border border-white/5 text-[10px] text-slate-400 font-mono space-y-1">
                  <p className="flex items-center gap-1.5 text-cyan-300 font-bold">
                    <Shield size={11} className="text-cyan-400" />
                    Autonomous Multi-Tier Failover Protection:
                  </p>
                  <p>
                    1. Inbound inquiries round-robin across active dedicated keys.<br />
                    2. If a key hits 429 quota, it enters a cooldown and rotates in-flight to next key.<br />
                    3. If all pool keys deplete, system gracefully falls back to Platform Master Key unless restricted.<br />
                    4. Autonomous background probe tests keys every 5 minutes and auto-restores traffic.
                  </p>
                </div>

                {/* Strict BYOK Mode: Restrict Platform Master Key Fallback */}
                <div className="p-3 rounded-2xl bg-amber-950/20 border border-amber-500/30 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5 pr-2">
                      <label className="text-xs font-black text-amber-300 font-mono flex items-center gap-1.5 cursor-pointer">
                        <ShieldAlert size={14} className="text-amber-400" />
                        Restrict Platform Master Key Fallback (Strict BYOK Mode)
                      </label>
                      <p className="text-[10px] text-slate-400 font-mono">
                        When enabled, Luna will NEVER use the Master Admin's paid API keys if this tenant's dedicated keys are rate-limited, exhausted, or missing.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        checked={credModalDisallowMasterKeyFallback}
                        onChange={(e) => setCredModalDisallowMasterKeyFallback(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500"></div>
                    </label>
                  </div>
                  {credModalDisallowMasterKeyFallback && (
                    <p className="text-[10px] text-amber-400/90 font-mono font-bold bg-amber-500/10 p-2 rounded-lg border border-amber-500/20">
                      ⚠️ Zero Platform Quota Consumption Enforced: All WhatsApp calls, web voice, and AI tasks will be strictly blocked if dedicated keys hit quota or are absent.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <button
                type="button"
                onClick={() => setCredModalTenant(null)}
                disabled={credModalSaving}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition-all cursor-pointer disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCredentialSubmit}
                disabled={credModalSaving}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 via-teal-500 to-purple-600 hover:opacity-95 text-slate-950 font-black text-xs uppercase tracking-wider shadow-[0_0_25px_rgba(6,182,212,0.4)] transition-all active:scale-95 disabled:opacity-40 cursor-pointer"
              >
                {credModalSaving ? (
                  <><RefreshCw size={14} className="animate-spin" /> Saving...</>
                ) : (
                  <><KeyRound size={14} /> Update Credentials & Key</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════════
          AI MODEL PRICING & SPECIFICATION MATRIX MODAL
          ══════════════════════════════════════════════════════════════════════════ */}
      {showPricingMatrixModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4 overflow-y-auto">
          <div className="bg-slate-950 border border-cyan-500/40 rounded-3xl w-full max-w-6xl max-h-[92vh] shadow-[0_0_90px_rgba(6,182,212,0.35)] flex flex-col overflow-hidden my-auto">
            {/* Header */}
            <div className="p-6 border-b border-cyan-500/20 bg-gradient-to-r from-cyan-950/40 via-slate-950 to-slate-950 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-cyan-500/20 border border-cyan-500/40 text-cyan-400">
                  <Bot size={24} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg sm:text-xl font-black text-white font-mono tracking-tight uppercase">
                      💎 AI Model Pricing & Specification Matrix
                    </h2>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">
                      USD ($) & LKR (Rs.)
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 font-mono mt-0.5">
                    Authoritative rates for Google Gemini & Xiaomi MiMo models. All metrics calibrated per 1,000,000 tokens.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* LKR Exchange Rate Quick Modifier */}
                <div className="flex items-center gap-2 bg-black/60 px-3 py-1.5 rounded-xl border border-white/10 font-mono text-xs">
                  <span className="text-amber-400 font-bold">1 USD =</span>
                  <input
                    type="number"
                    value={billingLkrRate}
                    onChange={(e) => setBillingLkrRate(parseFloat(e.target.value) || 305)}
                    className="w-16 bg-slate-900 border border-white/20 rounded px-1.5 py-0.5 text-center text-white font-bold outline-none focus:border-cyan-400"
                    step="0.5"
                  />
                  <span className="text-slate-400">LKR</span>
                </div>

                <button
                  type="button"
                  onClick={() => setShowPricingMatrixModal(false)}
                  className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-xl transition cursor-pointer"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Sub-header Filter Tabs, Target Tenant Selector & Simulator */}
            <div className="px-6 py-4 bg-slate-900/60 border-b border-white/5 flex flex-col xl:flex-row items-start xl:items-center justify-between gap-4 shrink-0">
              {/* Category Filter & Target Tenant Selector */}
              <div className="flex items-center gap-3 overflow-x-auto w-full xl:w-auto pb-1 xl:pb-0 flex-wrap">
                <div className="flex items-center gap-1.5">
                  {(['all', 'google', 'mimo', 'live'] as const).map((filter) => (
                    <button
                      key={filter}
                      type="button"
                      onClick={() => setPricingFilter(filter)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-mono font-bold uppercase tracking-wider transition cursor-pointer shrink-0 ${
                        pricingFilter === filter
                          ? 'bg-cyan-500 text-slate-950 shadow-md shadow-cyan-500/20'
                          : 'bg-black/40 text-slate-400 hover:text-white hover:bg-black/60 border border-white/5'
                      }`}
                    >
                      {filter === 'all' ? 'All Models (15+)' : filter === 'google' ? 'Google Gemini' : filter === 'mimo' ? 'Xiaomi MiMo' : 'Live Voice'}
                    </button>
                  ))}
                </div>

                {/* Target Tenant Selector inside Matrix */}
                <div className="flex items-center gap-2 bg-black/70 px-3 py-1 rounded-xl border border-cyan-500/40 font-mono text-xs">
                  <span className="text-cyan-400 font-bold flex items-center gap-1">
                    <Bot size={13} />
                    <span>Target Tenant:</span>
                  </span>
                  <select
                    value={matrixTargetTenantId || expandedTenantId || (tenants[0]?.id?.toString() || '')}
                    onChange={(e) => setMatrixTargetTenantId(e.target.value)}
                    className="bg-slate-900 border border-white/20 rounded px-2 py-0.5 text-white font-bold outline-none cursor-pointer focus:border-cyan-400 max-w-[200px] truncate"
                  >
                    {tenants.map(t => (
                      <option key={t.id} value={t.id.toString()}>
                        {t.name} ({t.assignedModel ? (getModelPricing(t.assignedModel)?.displayName || t.assignedModel) : 'Global Gemini 3.1 Live'})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Calculator Quick Toggle/Display */}
              <div className="flex items-center gap-2 text-xs font-mono text-slate-400">
                <Calculator size={14} className="text-cyan-400" />
                <span>Simulate Token Cost:</span>
                <input
                  type="number"
                  value={modelSimInputTokens}
                  onChange={(e) => setModelSimInputTokens(Math.max(0, parseInt(e.target.value) || 0))}
                  placeholder="Input"
                  title="Fresh Prompt Tokens"
                  className="w-16 bg-black/60 border border-white/10 rounded px-1.5 py-0.5 text-cyan-300 font-bold text-center"
                />
                <span>in +</span>
                <input
                  type="number"
                  value={modelSimOutputTokens}
                  onChange={(e) => setModelSimOutputTokens(Math.max(0, parseInt(e.target.value) || 0))}
                  placeholder="Output"
                  title="Output Tokens"
                  className="w-16 bg-black/60 border border-white/10 rounded px-1.5 py-0.5 text-amber-300 font-bold text-center"
                />
                <span>out</span>
              </div>
            </div>

            {/* Scrollable Model Table & Interactive Simulator */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Simulation Result Preview Banner */}
              {(() => {
                const simModel = getModelPricing(modelSimSelectedModel);
                const simBreakdown = calculateTokenCostBreakdown({
                  modelId: modelSimSelectedModel,
                  inputTokens: modelSimInputTokens,
                  cachedTokens: Math.round(modelSimInputTokens * 0.4),
                  outputTokens: modelSimOutputTokens,
                  usdToLkrRate: billingLkrRate,
                });
                return (
                  <div className="bg-gradient-to-r from-cyan-950/30 via-slate-900 to-indigo-950/30 border border-cyan-500/30 rounded-2xl p-4 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-cyan-500/20 text-cyan-300">
                        <Sparkles size={18} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-400 uppercase font-mono">Live Simulation Model:</span>
                          <select
                            value={modelSimSelectedModel}
                            onChange={(e) => setModelSimSelectedModel(e.target.value)}
                            className="bg-black border border-cyan-500/40 rounded-lg px-2.5 py-1 text-xs text-cyan-300 font-mono font-bold outline-none"
                          >
                            {getAllModelsCatalog().map(m => (
                              <option key={m.id} value={m.id}>{m.displayName}</option>
                            ))}
                          </select>
                        </div>
                        <p className="text-[11px] text-slate-400 font-mono mt-1">
                          Simulating {modelSimInputTokens.toLocaleString()} input tokens + {modelSimOutputTokens.toLocaleString()} output tokens.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 font-mono">
                      <div className="text-right">
                        <span className="text-[10px] text-slate-400 uppercase">Simulated USD</span>
                        <p className="text-base font-black text-cyan-400">${simBreakdown.costs.totalCostUsd.toFixed(4)}</p>
                      </div>
                      <div className="w-px h-8 bg-white/10"></div>
                      <div className="text-right">
                        <span className="text-[10px] text-slate-400 uppercase">Simulated LKR</span>
                        <p className="text-base font-black text-emerald-400">Rs. {simBreakdown.costs.totalCostLkr.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Models Matrix Table */}
              <div className="overflow-x-auto rounded-2xl border border-white/10 shadow-xl">
                <table className="w-full text-left font-mono text-xs">
                  <thead className="bg-slate-900/90 text-slate-400 uppercase text-[10px] tracking-wider border-b border-white/10">
                    <tr>
                      <th className="py-3 px-4">AI Model & Capability</th>
                      <th className="py-3 px-4">Provider / Context</th>
                      <th className="py-3 px-4 text-right">Fresh In (1M)</th>
                      <th className="py-3 px-4 text-right">Cached In (1M)</th>
                      <th className="py-3 px-4 text-right">Output (1M)</th>
                      <th className="py-3 px-4 text-right">Live Audio (1M)</th>
                      <th className="py-3 px-4 text-right">LKR Approx (1M in+out)</th>
                      <th className="py-3 px-4 text-center">⚡ Dedicated Assignment</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {getAllModelsCatalog()
                      .filter((m) => {
                        if (pricingFilter === 'google') return m.provider === 'Google Gemini';
                        if (pricingFilter === 'mimo') return m.provider === 'Xiaomi MiMo';
                        if (pricingFilter === 'live') return m.isLiveCapable;
                        return true;
                      })
                      .map((model) => {
                        const inLkr = (model.inputPricePerM * billingLkrRate).toFixed(0);
                        const outLkr = (model.outputPricePerM * billingLkrRate).toFixed(0);
                        const combinedLkr = ((model.inputPricePerM + model.outputPricePerM) * billingLkrRate).toFixed(0);

                        return (
                          <tr key={model.id} className="hover:bg-cyan-500/[0.04] transition-colors">
                            <td className="py-3 px-4">
                              <div className="flex flex-col">
                                <span className="font-bold text-white flex items-center gap-1.5">
                                  {model.displayName}
                                  {model.isLiveCapable && (
                                    <span className="px-1.5 py-0.2 rounded text-[9px] bg-sky-500/20 text-sky-300 border border-sky-500/40">
                                      VOICE LIVE
                                    </span>
                                  )}
                                </span>
                                <span className="text-[10px] text-slate-400 mt-0.5">{model.id}</span>
                              </div>
                            </td>

                            <td className="py-3 px-4">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${model.badgeColor}`}>
                                {model.provider}
                              </span>
                              <div className="text-[10px] text-slate-400 mt-0.5">{model.contextWindow}</div>
                            </td>

                            <td className="py-3 px-4 text-right">
                              <span className="font-bold text-blue-400">${model.inputPricePerM}</span>
                              <div className="text-[10px] text-slate-400">Rs. {inLkr}</div>
                            </td>

                            <td className="py-3 px-4 text-right">
                              <span className="font-bold text-emerald-400">${model.cachedInputPricePerM}</span>
                              <span className="block text-[9px] text-emerald-500/80 font-bold">-90% OFF</span>
                            </td>

                            <td className="py-3 px-4 text-right">
                              <span className="font-bold text-amber-300">${model.outputPricePerM}</span>
                              <div className="text-[10px] text-slate-400">Rs. {outLkr}</div>
                            </td>

                            <td className="py-3 px-4 text-right">
                              {model.audioInputPricePerM ? (
                                <div>
                                  <span className="text-purple-300 font-bold">${model.audioInputPricePerM} in / ${model.audioOutputPricePerM} out</span>
                                  <span className="block text-[10px] text-slate-400">Native VAD Audio</span>
                                </div>
                              ) : (
                                <span className="text-slate-500">—</span>
                              )}
                            </td>

                            <td className="py-3 px-4 text-right">
                              <span className="font-bold text-emerald-400">Rs. {combinedLkr}</span>
                              <div className="text-[10px] text-slate-400">@ {billingLkrRate} LKR/$</div>
                            </td>

                            <td className="py-3 px-4 text-center">
                              {(() => {
                                const activeTenantId = matrixTargetTenantId || expandedTenantId || (tenants[0]?.id?.toString() || '');
                                const activeTenant = tenants.find(t => t.id.toString() === activeTenantId);
                                const isCurrent = activeTenant?.assignedModel === model.id || (!activeTenant?.assignedModel && model.id === 'gemini-3.1-flash-live-preview');

                                return (
                                  <div className="flex items-center justify-center">
                                    {isCurrent ? (
                                      <span className="px-2.5 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-500/50 text-[10px] font-bold flex items-center gap-1">
                                        <Check size={12} />
                                        <span>Active Model</span>
                                      </span>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          if (!activeTenant) return;
                                          await handleSaveTenantSettings(activeTenant.id.toString(), { assignedModel: model.id });
                                          fetchTenantBilling(activeTenant.id.toString(), billingMonth, billingLkrRate);
                                        }}
                                        disabled={savingSettingsId === activeTenant?.id.toString()}
                                        className="px-2.5 py-1 rounded-lg bg-cyan-500/20 hover:bg-cyan-500 text-cyan-300 hover:text-slate-950 border border-cyan-500/40 text-[10px] font-bold transition cursor-pointer active:scale-95 disabled:opacity-50 flex items-center gap-1 shadow whitespace-nowrap"
                                      >
                                        <Zap size={11} />
                                        <span>Assign to {activeTenant?.name ? activeTenant.name.slice(0, 10) : 'Tenant'}</span>
                                      </button>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 bg-slate-950 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs font-mono text-slate-400 shrink-0">
              <div className="flex items-center gap-3 flex-wrap">
                <p>
                  🌐 Default sovereign core: <strong className="text-cyan-300">gemini-3.1-flash-live-preview</strong>. All pricing syncs with tenant billing ledgers.
                </p>
                {(() => {
                  const activeTenantId = matrixTargetTenantId || expandedTenantId || (tenants[0]?.id?.toString() || '');
                  const activeTenant = tenants.find(t => t.id.toString() === activeTenantId);
                  if (!activeTenant || !activeTenant.assignedModel) return null;
                  return (
                    <button
                      type="button"
                      onClick={async () => {
                        await handleSaveTenantSettings(activeTenant.id.toString(), { assignedModel: null });
                        fetchTenantBilling(activeTenant.id.toString(), billingMonth, billingLkrRate);
                      }}
                      className="px-2.5 py-1 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 border border-purple-500/40 text-[11px] font-bold transition cursor-pointer"
                    >
                      Revert {activeTenant.name} to Global Default (Gemini 3.1 Live)
                    </button>
                  );
                })()}
              </div>
              <button
                type="button"
                onClick={() => setShowPricingMatrixModal(false)}
                className="px-5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold transition cursor-pointer shrink-0"
              >
                Close Matrix
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════════
          TENANT MONTHLY TOKEN INVOICE MODAL
          ══════════════════════════════════════════════════════════════════════════ */}
      {selectedInvoiceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4 overflow-y-auto">
          <div className="bg-slate-950 border border-emerald-500/50 rounded-3xl w-full max-w-3xl max-h-[92vh] shadow-[0_0_90px_rgba(16,185,129,0.35)] flex flex-col overflow-hidden my-auto">
            {/* Header */}
            <div className="p-6 border-b border-emerald-500/20 bg-gradient-to-r from-emerald-950/40 via-slate-950 to-slate-950 flex items-center justify-between gap-4 shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-400">
                  <Receipt size={24} />
                </div>
                <div>
                  <h2 className="text-lg font-black text-white font-mono uppercase tracking-tight">
                    Official Token Usage Invoice
                  </h2>
                  <p className="text-xs text-slate-400 font-mono mt-0.5">
                    Invoice #{selectedInvoiceModal.invoiceNumber} · Period: {selectedInvoiceModal.billingPeriod?.month}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setSelectedInvoiceModal(null)}
                className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-xl transition cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>

            {/* Scrollable Printable Invoice Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 font-mono text-xs text-slate-300">
              {/* Tenant & Platform Header */}
              <div className="p-5 bg-black/60 rounded-2xl border border-white/10 flex flex-col sm:flex-row justify-between gap-4">
                <div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Billed To Tenant</span>
                  <h3 className="text-base font-black text-white">{selectedInvoiceModal.tenantName || selectedInvoiceModal.tenant?.name || `Tenant #${selectedInvoiceModal.tenantId || selectedInvoiceModal.tenant?.id}`}</h3>
                  <p className="text-slate-400 mt-0.5">Tenant ID: #{selectedInvoiceModal.tenantId || selectedInvoiceModal.tenant?.id}</p>
                  <p className="text-cyan-300 font-bold mt-1">
                    Assigned Model: {selectedInvoiceModal.assignedModel?.displayName || selectedInvoiceModal.assignedModelName}
                  </p>
                  <p className="text-slate-400 text-[11px]">Model ID: {selectedInvoiceModal.assignedModel?.id || selectedInvoiceModal.assignedModel}</p>
                  {selectedInvoiceModal.hasDedicatedKeys ? (
                    <span className="inline-flex items-center gap-1 mt-2 px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                      <Sparkles size={11} /> Dedicated Key Pool ({selectedInvoiceModal.poolSize || 1} keys) Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 mt-2 px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-slate-800 text-slate-400 border border-white/10">
                      Platform Master Key Tier
                    </span>
                  )}
                </div>

                <div className="text-left sm:text-right">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Billing Service</span>
                  <h4 className="font-bold text-white">Kangavena Cloud AI SaaS</h4>
                  <p className="text-slate-400">Colombo, Sri Lanka</p>
                  <p className="text-amber-400 font-bold mt-1">
                    Reference Rate: 1 USD = {selectedInvoiceModal.exchangeRate?.usdToLkr || selectedInvoiceModal.amounts?.usdToLkrRate || 305} LKR
                  </p>
                  <p className="text-slate-500 text-[10px] mt-0.5">Issued: {new Date(selectedInvoiceModal.generatedAt).toLocaleString()}</p>
                </div>
              </div>

              {/* Executive Token Sourcing Breakdown */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {/* Free Dedicated Tokens */}
                <div className="p-4 rounded-2xl bg-gradient-to-br from-[#062016] via-slate-950 to-emerald-950/40 border border-emerald-500/40 flex items-center justify-between">
                  <div>
                    <span className="text-[10px] font-mono font-bold text-emerald-400 uppercase tracking-wider block">
                      Your Free Tokens (Dedicated Pool)
                    </span>
                    <p className="text-xl font-black text-white font-mono mt-0.5">
                      {(selectedInvoiceModal.tokenUsage?.freeTokens ?? (selectedInvoiceModal.hasDedicatedKeys ? selectedInvoiceModal.tokenUsage?.totalTokens : 0) ?? 0).toLocaleString()}
                    </p>
                    <span className="text-[10px] text-emerald-300 font-mono">
                      100% Free · Saved Rs. {Number(selectedInvoiceModal.amounts?.freeTokensSavedLkr ?? selectedInvoiceModal.amounts?.totalSavedLkr ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                  <div className="p-3 rounded-2xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                    <Sparkles size={20} />
                  </div>
                </div>

                {/* Paid Master AI Tokens */}
                <div className="p-4 rounded-2xl bg-gradient-to-br from-[#201505] via-slate-950 to-amber-950/40 border border-amber-500/40 flex items-center justify-between">
                  <div>
                    <span className="text-[10px] font-mono font-bold text-amber-400 uppercase tracking-wider block">
                      Paid Tokens from Master AI
                    </span>
                    <p className="text-xl font-black text-amber-200 font-mono mt-0.5">
                      {(selectedInvoiceModal.tokenUsage?.paidMasterTokens ?? (selectedInvoiceModal.hasDedicatedKeys ? 0 : selectedInvoiceModal.tokenUsage?.totalTokens) ?? 0).toLocaleString()}
                    </p>
                    <span className="text-[10px] text-amber-300 font-mono">
                      Billable: Rs. {Number(selectedInvoiceModal.amounts?.billableTotalLkr ?? selectedInvoiceModal.amounts?.paidMasterCostLkr ?? (selectedInvoiceModal.hasDedicatedKeys ? 0 : selectedInvoiceModal.amounts?.totalLkr) ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="p-3 rounded-2xl bg-amber-500/10 text-amber-400 border border-amber-500/30">
                    <Zap size={20} />
                  </div>
                </div>
              </div>

              {/* Itemized Table */}
              <div className="rounded-2xl border border-white/10 overflow-hidden shadow-lg">
                <table className="w-full text-left">
                  <thead className="bg-slate-900/90 text-slate-400 uppercase text-[10px] tracking-wider border-b border-white/10">
                    <tr>
                      <th className="py-3 px-4">Line Item / Token Category</th>
                      <th className="py-3 px-4 text-right">Units (Tokens)</th>
                      <th className="py-3 px-4 text-right">Rate / 1M</th>
                      <th className="py-3 px-4 text-right">USD Amount</th>
                      <th className="py-3 px-4 text-right">LKR (Rs.)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5 bg-slate-950/80">
                    {/* Fresh Input */}
                    <tr>
                      <td className="py-3 px-4">
                        <span className="font-bold text-white">Fresh Prompt / Input Tokens</span>
                        <span className="block text-[10px] text-slate-400">Context input and user queries</span>
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-white">
                        {selectedInvoiceModal.tokenUsage?.freshInputTokens?.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-400">
                        ${selectedInvoiceModal.assignedModel?.inputPricePerM || selectedInvoiceModal.rates?.inputPerMillionUsd || 0.75}
                      </td>
                      <td className="py-3 px-4 text-right text-cyan-300 font-bold">
                        ${(selectedInvoiceModal.amounts?.freshInputUsd ?? 0).toFixed(4)}
                      </td>
                      <td className="py-3 px-4 text-right text-emerald-400 font-bold">
                        Rs. {(selectedInvoiceModal.amounts?.freshInputLkr ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>

                    {/* Cached Input */}
                    <tr>
                      <td className="py-3 px-4">
                        <span className="font-bold text-white">Context Cached Tokens</span>
                        <span className="block text-[10px] text-emerald-500 font-bold">90% Discounted Rate</span>
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-white">
                        {(selectedInvoiceModal.tokenUsage?.cachedInputTokens ?? 0).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-400">
                        ${selectedInvoiceModal.assignedModel?.cachedInputPricePerM || selectedInvoiceModal.rates?.cachedInputPerMillionUsd || 0.075}
                      </td>
                      <td className="py-3 px-4 text-right text-cyan-300 font-bold">
                        ${(selectedInvoiceModal.amounts?.cachedInputUsd ?? 0).toFixed(4)}
                      </td>
                      <td className="py-3 px-4 text-right text-emerald-400 font-bold">
                        Rs. {(selectedInvoiceModal.amounts?.cachedInputLkr ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>

                    {/* Output */}
                    <tr>
                      <td className="py-3 px-4">
                        <span className="font-bold text-white">Completion / Output Tokens</span>
                        <span className="block text-[10px] text-slate-400">Generated responses & agent actions</span>
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-white">
                        {(selectedInvoiceModal.tokenUsage?.outputTokens ?? 0).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-400">
                        ${selectedInvoiceModal.assignedModel?.outputPricePerM || selectedInvoiceModal.rates?.outputPerMillionUsd || 3.00}
                      </td>
                      <td className="py-3 px-4 text-right text-amber-300 font-bold">
                        ${(selectedInvoiceModal.amounts?.outputUsd ?? 0).toFixed(4)}
                      </td>
                      <td className="py-3 px-4 text-right text-emerald-400 font-bold">
                        Rs. {(selectedInvoiceModal.amounts?.outputLkr ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>

                    {/* Audio */}
                    <tr>
                      <td className="py-3 px-4">
                        <span className="font-bold text-white">Voice & Telephony Audio</span>
                        <span className="block text-[10px] text-slate-400">
                          {selectedInvoiceModal.tokenUsage?.audioInputTokens || 0} in / {selectedInvoiceModal.tokenUsage?.audioOutputTokens || 0} out
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-white">
                        {((selectedInvoiceModal.tokenUsage?.audioInputTokens || 0) + (selectedInvoiceModal.tokenUsage?.audioOutputTokens || 0)).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-400">
                        Audio Tier
                      </td>
                      <td className="py-3 px-4 text-right text-purple-300 font-bold">
                        ${(selectedInvoiceModal.amounts?.audioUsd ?? 0).toFixed(4)}
                      </td>
                      <td className="py-3 px-4 text-right text-emerald-400 font-bold">
                        Rs. {(selectedInvoiceModal.amounts?.audioLkr ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>

                    {/* RAG & Training */}
                    <tr>
                      <td className="py-3 px-4">
                        <span className="font-bold text-white">RAG Knowledge & Training</span>
                        <span className="block text-[10px] text-slate-400">Document chunking & vector synthesis</span>
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-white">
                        {(selectedInvoiceModal.tokenUsage?.trainingTokens || 0).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-400">
                        Aux Tier
                      </td>
                      <td className="py-3 px-4 text-right text-slate-300 font-bold">
                        ${(selectedInvoiceModal.amounts?.trainingUsd ?? 0).toFixed(4)}
                      </td>
                      <td className="py-3 px-4 text-right text-emerald-400 font-bold">
                        Rs. {(selectedInvoiceModal.amounts?.trainingLkr ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Itemized Multi-Model Breakdown inside Invoice */}
              {selectedInvoiceModal.modelBreakdown && selectedInvoiceModal.modelBreakdown.length > 0 && (
                <div className="bg-black/40 border border-cyan-500/20 rounded-2xl p-4 space-y-3 font-mono">
                  <div className="flex items-center justify-between border-b border-white/5 pb-2">
                    <span className="text-xs font-bold text-cyan-300 uppercase tracking-wider flex items-center gap-1.5">
                      <Bot size={14} />
                      Itemized Model Usage Breakdown
                    </span>
                    <span className="text-[10px] text-slate-400">
                      {selectedInvoiceModal.modelBreakdown.length} Model{selectedInvoiceModal.modelBreakdown.length > 1 ? 's' : ''} Utilized
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead>
                        <tr className="text-[10px] text-slate-400 border-b border-white/5 pb-1 uppercase">
                          <th className="pb-1 font-bold">Model</th>
                          <th className="pb-1 font-bold text-center">Turns</th>
                          <th className="pb-1 font-bold text-right">Tokens</th>
                          <th className="pb-1 font-bold text-right">Rates ($/1M)</th>
                          <th className="pb-1 font-bold text-right">Cost (LKR)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5 text-[11px]">
                        {selectedInvoiceModal.modelBreakdown.map((item: any) => (
                          <tr key={item.modelId}>
                            <td className="py-1.5 font-bold text-white flex items-center gap-1">
                              <span>{item.modelName}</span>
                              {item.isPrimaryAssigned && (
                                <span className="px-1 py-0.1 rounded text-[7.5px] bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">
                                  PRIMARY
                                </span>
                              )}
                            </td>
                            <td className="py-1.5 text-center text-slate-300">{item.turnsCount?.toLocaleString() || 0}</td>
                            <td className="py-1.5 text-right font-bold text-slate-200">{item.totalTokens?.toLocaleString() || 0}</td>
                            <td className="py-1.5 text-right text-slate-400">
                              ${item.rates?.freshPromptPrice ?? 0.75} in / ${item.rates?.completionPrice ?? 3.00} out
                            </td>
                            <td className="py-1.5 text-right font-bold text-emerald-400">
                              Rs. {(item.costLkr || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Total Due Card */}
              <div className="p-5 bg-gradient-to-r from-emerald-950/60 to-slate-900 rounded-2xl border border-emerald-500/50 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <span className="text-xs text-slate-400 uppercase font-mono">Gross Consumption</span>
                  <p className="text-xl font-black text-white font-mono">
                    {(selectedInvoiceModal.tokenUsage?.totalTokens ?? 0).toLocaleString()} Tokens
                  </p>
                  {Number(selectedInvoiceModal.amounts?.freeTokensSavedLkr ?? selectedInvoiceModal.amounts?.totalSavedLkr ?? 0) > 0 && (
                    <p className="text-xs text-emerald-400 font-mono mt-0.5">
                      Dedicated Pool Savings: -Rs. {Number(selectedInvoiceModal.amounts?.freeTokensSavedLkr ?? selectedInvoiceModal.amounts?.totalSavedLkr ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </p>
                  )}
                </div>

                <div className="text-right">
                  <div className="flex items-center gap-2 justify-end">
                    <span className="text-xs text-slate-400 uppercase font-mono">Net Amount Due</span>
                    {Number(selectedInvoiceModal.amounts?.billableTotalLkr ?? selectedInvoiceModal.amounts?.paidMasterCostLkr ?? (selectedInvoiceModal.hasDedicatedKeys ? 0 : selectedInvoiceModal.amounts?.totalLkr) ?? 0) === 0 && (
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                        100% COVERED
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-2 justify-end">
                    <span className="text-lg font-black text-cyan-400 font-mono">
                      (${Number(selectedInvoiceModal.amounts?.billableTotalUsd ?? selectedInvoiceModal.amounts?.paidMasterCostUsd ?? (selectedInvoiceModal.hasDedicatedKeys ? 0 : selectedInvoiceModal.amounts?.totalUsd) ?? 0).toFixed(2)} USD)
                    </span>
                    <span className="text-2xl font-black text-emerald-400 font-mono">
                      Rs. {Number(selectedInvoiceModal.amounts?.billableTotalLkr ?? selectedInvoiceModal.amounts?.paidMasterCostLkr ?? (selectedInvoiceModal.hasDedicatedKeys ? 0 : selectedInvoiceModal.amounts?.totalLkr) ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="p-4 bg-slate-950 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 shrink-0">
              <div className="flex items-center gap-2">
                {/* Copy Text Summary for WhatsApp */}
                <button
                  type="button"
                  onClick={() => {
                    const freeToks = Number(selectedInvoiceModal.tokenUsage?.freeTokens ?? (selectedInvoiceModal.hasDedicatedKeys ? selectedInvoiceModal.tokenUsage?.totalTokens : 0) ?? 0);
                    const paidToks = Number(selectedInvoiceModal.tokenUsage?.paidMasterTokens ?? (selectedInvoiceModal.hasDedicatedKeys ? 0 : selectedInvoiceModal.tokenUsage?.totalTokens) ?? 0);
                    const billableUsd = Number(selectedInvoiceModal.amounts?.billableTotalUsd ?? selectedInvoiceModal.amounts?.paidMasterCostUsd ?? (selectedInvoiceModal.hasDedicatedKeys ? 0 : selectedInvoiceModal.amounts?.totalUsd) ?? 0);
                    const billableLkr = Number(selectedInvoiceModal.amounts?.billableTotalLkr ?? selectedInvoiceModal.amounts?.paidMasterCostLkr ?? (selectedInvoiceModal.hasDedicatedKeys ? 0 : selectedInvoiceModal.amounts?.totalLkr) ?? 0);
                    const savedLkr = Number(selectedInvoiceModal.amounts?.freeTokensSavedLkr ?? selectedInvoiceModal.amounts?.totalSavedLkr ?? 0);

                    const text = `🧾 *KANGAVENA AI TOKEN INVOICE #${selectedInvoiceModal.invoiceNumber}*\n` +
                      `🏢 Tenant: ${selectedInvoiceModal.tenantName || selectedInvoiceModal.tenant?.name || `Tenant #${selectedInvoiceModal.tenantId || selectedInvoiceModal.tenant?.id}`}\n` +
                      `📅 Period: ${selectedInvoiceModal.billingPeriod?.month || selectedInvoiceModal.billingPeriod}\n` +
                      `🤖 Assigned Model: ${selectedInvoiceModal.assignedModel?.displayName || selectedInvoiceModal.assignedModelName || 'Gemini 3.1 Live'}\n` +
                      `──────────────────────\n` +
                      `📊 Total Tokens: ${selectedInvoiceModal.tokenUsage?.totalTokens?.toLocaleString()} tokens\n` +
                      `🟢 Your Free Tokens: ${freeToks.toLocaleString()} (100% Free / Pool)\n` +
                      `⚡ Paid Master AI Tokens: ${paidToks.toLocaleString()}\n` +
                      `🔹 Prompt Tokens: ${selectedInvoiceModal.tokenUsage?.freshInputTokens?.toLocaleString()}\n` +
                      `🔹 Cached Tokens: ${selectedInvoiceModal.tokenUsage?.cachedInputTokens?.toLocaleString()} (90% OFF)\n` +
                      `🔹 Output Tokens: ${selectedInvoiceModal.tokenUsage?.outputTokens?.toLocaleString()}\n` +
                      `🔹 Audio Tokens: ${((selectedInvoiceModal.tokenUsage?.audioInputTokens || 0) + (selectedInvoiceModal.tokenUsage?.audioOutputTokens || 0)).toLocaleString()}\n` +
                      `──────────────────────\n` +
                      (savedLkr > 0 ? `🎁 Dedicated Pool Savings: -Rs. ${savedLkr.toLocaleString('en-US', { maximumFractionDigits: 0 })}\n` : '') +
                      `💵 Net Due (USD): $${billableUsd.toFixed(2)}\n` +
                      `🇱🇰 Net Due (LKR): Rs. ${billableLkr.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n` +
                      `💱 Rate: 1 USD = ${selectedInvoiceModal.exchangeRate?.usdToLkr || selectedInvoiceModal.amounts?.usdToLkrRate || 305} LKR\n` +
                      `Status: ${billableLkr === 0 ? 'PAID / COVERED BY POOL' : 'UNPAID / PENDING'}\n` +
                      `Generated by Kangavena Cloud Platform`;
                    navigator.clipboard.writeText(text);
                    setInvoiceCopyFeedback(true);
                    setTimeout(() => setInvoiceCopyFeedback(false), 2500);
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl transition shadow flex items-center gap-1.5 cursor-pointer"
                >
                  {invoiceCopyFeedback ? <Check size={14} /> : <Copy size={14} />}
                  <span>{invoiceCopyFeedback ? 'Copied to Clipboard!' : 'Copy for WhatsApp'}</span>
                </button>

                {/* Print Invoice */}
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white border border-white/10 rounded-xl text-xs font-mono font-bold transition flex items-center gap-1.5 cursor-pointer"
                >
                  <Printer size={14} />
                  <span>Print Invoice</span>
                </button>
              </div>

              <button
                type="button"
                onClick={() => setSelectedInvoiceModal(null)}
                className="px-5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-mono font-bold text-xs transition cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
